// Agent Data - Mock data for Rhino-1 Enterprise AI Workforce
// This file contains all agent definitions organized by department

export const initialAgents = [
  // ============ EXISTING AGENTS (General) ============
  {
    id: 'agent-001',
    name: 'Rhino Sales Assistant',
    role: 'Sales Agent',
    department: 'General',
    description: 'Handles lead qualification, follow-up scheduling, and sales pipeline management.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-01T10:00:00Z',
    updatedAt: '2025-12-10T14:30:00Z',
    personaPrompt: 'You are a professional sales assistant for RhinoShield Florida. Your goal is to help qualify leads, schedule follow-ups, and maintain accurate pipeline data. Always be helpful, concise, and focused on moving deals forward.',
    capabilities: ['Reads and updates CRM records', 'Sends internal notifications', 'Drafts email templates', 'Handles scheduling logic'],
    modulesAssigned: ['Sales Hub', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 12,
    tasksCompleted7Days: 87,
    avgResponseTime: '1.2s'
  },
  {
    id: 'agent-002',
    name: 'Ops Workflow Agent',
    role: 'Operations Agent',
    department: 'General',
    description: 'Manages job scheduling, crew assignments, and operational workflow automation.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-05T09:00:00Z',
    updatedAt: '2025-12-11T08:15:00Z',
    personaPrompt: 'You are an operations assistant for RhinoShield Florida. Focus on efficient job scheduling, crew coordination, and ensuring smooth operational workflows. Prioritize accuracy and timely updates.',
    capabilities: ['Reads and updates CRM records', 'Handles scheduling logic', 'Summarizes daily activity'],
    modulesAssigned: ['Operations Hub', 'Automations'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: false
    },
    limits: { maxTasksPerHour: 100, maxConcurrentTasks: 10 },
    tasksInQueue: 7,
    tasksToday: 24,
    tasksCompleted7Days: 156,
    avgResponseTime: '0.8s'
  },
  {
    id: 'agent-003',
    name: 'Daily Reporter',
    role: 'Reporting Agent',
    department: 'General',
    description: 'Generates daily summaries, performance reports, and analytics insights.',
    status: 'paused',
    createdBy: 'Ryan March',
    createdAt: '2025-12-08T11:00:00Z',
    updatedAt: '2025-12-10T16:45:00Z',
    personaPrompt: 'You are a reporting assistant that generates clear, actionable summaries and analytics. Focus on key metrics, trends, and insights that help leadership make informed decisions.',
    capabilities: ['Summarizes daily activity', 'Sends internal notifications'],
    modulesAssigned: ['Reporting / Analytics'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 2 },
    tasksInQueue: 0,
    tasksToday: 0,
    tasksCompleted7Days: 14,
    avgResponseTime: '2.5s'
  },
  {
    id: 'agent-004',
    name: 'Scheduling Bot',
    role: 'Scheduling Agent',
    department: 'General',
    description: 'Automates appointment scheduling, calendar management, and reminder notifications.',
    status: 'training',
    createdBy: 'Gabriel Gecan',
    createdAt: '2025-12-10T14:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'You are a scheduling assistant focused on optimal appointment booking, avoiding conflicts, and sending timely reminders. Be efficient and always confirm details.',
    capabilities: ['Handles scheduling logic', 'Sends internal notifications'],
    modulesAssigned: ['Sales Hub', 'Operations Hub'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 3 },
    tasksInQueue: 0,
    tasksToday: 0,
    tasksCompleted7Days: 0,
    avgResponseTime: 'N/A'
  },

  // ============ MARKETING DEPARTMENT ============
  // --- Leadership Roles ---
  {
    id: 'mkt-001',
    name: 'CMO Agent',
    role: 'Chief Marketing Officer',
    department: 'Marketing',
    category: 'Leadership',
    description: 'Executive-level marketing strategist responsible for overseeing all marketing operations, setting department goals, and ensuring alignment with company objectives.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Develops comprehensive marketing strategies',
      'Oversees all marketing department agents',
      'Analyzes market trends and competitive landscape',
      'Sets KPIs and performance benchmarks',
      'Coordinates cross-functional initiatives',
      'Approves major campaign decisions'
    ],
    primaryResponsibilities: [
      'Define marketing vision and strategy',
      'Manage marketing budget allocation',
      'Review and approve campaign proposals',
      'Monitor department-wide performance metrics',
      'Coordinate with Sales and Operations leadership'
    ],
    recommendedTasks: [
      'Strategic planning sessions',
      'Budget reviews',
      'Campaign approvals',
      'Performance analysis',
      'Leadership reports'
    ],
    modulesAssigned: ['Marketing Hub', 'Sales Hub', 'CRM / Contacts', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canAccessAnalytics: true,
      canApproveContent: true,
      canManageBudget: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 124,
    avgResponseTime: '1.5s',
    teamMembers: ['mkt-002', 'mkt-003', 'mkt-004', 'mkt-005', 'mkt-006', 'mkt-007', 'mkt-008', 'mkt-009', 'mkt-010', 'mkt-011', 'mkt-012', 'mkt-013', 'mkt-014', 'mkt-015', 'mkt-016', 'mkt-017', 'mkt-018', 'mkt-019', 'mkt-020']
  },
  {
    id: 'mkt-002',
    name: 'Marketing Ops Director',
    role: 'Marketing Operations Director',
    department: 'Marketing',
    category: 'Leadership',
    description: 'Manages the operational backbone of the marketing department, ensuring efficient workflows, tool integrations, and process optimization.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages marketing technology stack',
      'Optimizes marketing workflows',
      'Coordinates team resources and capacity',
      'Implements process automation',
      'Tracks operational metrics',
      'Ensures data quality and governance'
    ],
    primaryResponsibilities: [
      'Oversee marketing operations and processes',
      'Manage marketing technology integrations',
      'Optimize resource allocation',
      'Implement workflow automations',
      'Maintain data hygiene and compliance'
    ],
    recommendedTasks: [
      'Process optimization',
      'Tool integration setup',
      'Workflow automation',
      'Resource planning',
      'Data quality audits'
    ],
    modulesAssigned: ['Marketing Hub', 'Automations', 'Reporting / Analytics', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canAccessAnalytics: true,
      canManageAutomations: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 22,
    tasksCompleted7Days: 145,
    avgResponseTime: '1.1s'
  },

  // --- Strategy Roles ---
  {
    id: 'mkt-003',
    name: 'Brand Strategy Director',
    role: 'Brand Strategy Director',
    department: 'Marketing',
    category: 'Strategy',
    description: 'Develops and maintains brand identity, positioning, and messaging guidelines to ensure consistent brand representation across all channels.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Develops brand positioning strategies',
      'Creates brand guidelines and standards',
      'Monitors brand consistency',
      'Conducts brand health assessments',
      'Defines brand voice and messaging',
      'Manages brand asset library'
    ],
    primaryResponsibilities: [
      'Define and evolve brand identity',
      'Create brand style guidelines',
      'Ensure brand consistency across channels',
      'Conduct competitive brand analysis',
      'Oversee brand refresh initiatives'
    ],
    recommendedTasks: [
      'Brand guideline updates',
      'Messaging framework development',
      'Brand audit reviews',
      'Competitive analysis',
      'Brand approval requests'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: true,
      canApproveContent: true,
      canAccessAnalytics: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 9,
    tasksCompleted7Days: 67,
    avgResponseTime: '2.0s'
  },
  {
    id: 'mkt-004',
    name: 'Creative Director',
    role: 'Creative Director',
    department: 'Marketing',
    category: 'Strategy',
    description: 'Leads creative vision and direction for all marketing materials, campaigns, and visual content production.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Develops creative concepts and campaigns',
      'Directs visual design standards',
      'Reviews and approves creative assets',
      'Manages creative team output',
      'Ensures creative alignment with brand',
      'Innovates new creative approaches'
    ],
    primaryResponsibilities: [
      'Set creative direction for campaigns',
      'Review and approve all creative assets',
      'Mentor creative team members',
      'Develop innovative campaign concepts',
      'Maintain creative quality standards'
    ],
    recommendedTasks: [
      'Creative concept development',
      'Asset review and approval',
      'Campaign creative direction',
      'Design system updates',
      'Creative brief creation'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: true,
      canApproveContent: true,
      canCreateAssets: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 6,
    tasksToday: 14,
    tasksCompleted7Days: 89,
    avgResponseTime: '1.8s'
  },

  // --- Content & Creative ---
  {
    id: 'mkt-005',
    name: 'Social Media Manager',
    role: 'Social Media Manager',
    department: 'Marketing',
    category: 'Content & Creative',
    description: 'Manages all social media channels, content calendars, community engagement, and social advertising campaigns.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates social media content',
      'Manages posting schedules',
      'Engages with community',
      'Monitors social mentions',
      'Analyzes social performance',
      'Coordinates influencer partnerships'
    ],
    primaryResponsibilities: [
      'Develop social content calendar',
      'Create and publish social posts',
      'Manage community engagement',
      'Track social media metrics',
      'Respond to customer inquiries'
    ],
    recommendedTasks: [
      'Content creation',
      'Post scheduling',
      'Community responses',
      'Performance reporting',
      'Trend monitoring'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: false,
      canPublishContent: true,
      canAccessAnalytics: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 35,
    tasksCompleted7Days: 234,
    avgResponseTime: '0.8s'
  },
  {
    id: 'mkt-006',
    name: 'Content Writer',
    role: 'Content Writer / Copywriter',
    department: 'Marketing',
    category: 'Content & Creative',
    description: 'Creates compelling written content including blog posts, website copy, email content, and marketing collateral.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Writes blog posts and articles',
      'Creates website copy',
      'Develops email content',
      'Crafts ad copy',
      'Produces case studies',
      'Optimizes content for SEO'
    ],
    primaryResponsibilities: [
      'Write engaging marketing content',
      'Develop content for campaigns',
      'Create SEO-optimized articles',
      'Draft email copy',
      'Produce customer success stories'
    ],
    recommendedTasks: [
      'Blog post writing',
      'Email copy creation',
      'Landing page copy',
      'Ad copywriting',
      'Content editing'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: false,
      canSendNotifications: false,
      canDraftEmails: true,
      canCreateContent: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 4 },
    tasksInQueue: 4,
    tasksToday: 8,
    tasksCompleted7Days: 52,
    avgResponseTime: '3.5s'
  },
  {
    id: 'mkt-007',
    name: 'Video Production Specialist',
    role: 'Video Production Specialist',
    department: 'Marketing',
    category: 'Content & Creative',
    description: 'Produces video content including promotional videos, tutorials, testimonials, and social media clips.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates video content concepts',
      'Manages video production workflow',
      'Edits and produces videos',
      'Optimizes videos for platforms',
      'Creates video thumbnails',
      'Manages video asset library'
    ],
    primaryResponsibilities: [
      'Plan and produce marketing videos',
      'Edit video content',
      'Optimize for different platforms',
      'Create video scripts',
      'Manage video assets'
    ],
    recommendedTasks: [
      'Video concept development',
      'Video editing',
      'Platform optimization',
      'Thumbnail creation',
      'Script writing'
    ],
    modulesAssigned: ['Marketing Hub'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: false,
      canSendNotifications: false,
      canDraftEmails: false,
      canCreateAssets: true
    },
    limits: { maxTasksPerHour: 10, maxConcurrentTasks: 3 },
    tasksInQueue: 2,
    tasksToday: 5,
    tasksCompleted7Days: 28,
    avgResponseTime: '5.0s'
  },
  {
    id: 'mkt-008',
    name: 'Graphic Designer',
    role: 'Graphic Designer / Creative Assets',
    department: 'Marketing',
    category: 'Content & Creative',
    description: 'Designs visual assets including graphics, infographics, presentations, and marketing collateral.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates marketing graphics',
      'Designs infographics',
      'Develops presentation templates',
      'Creates social media visuals',
      'Designs print materials',
      'Maintains brand visual consistency'
    ],
    primaryResponsibilities: [
      'Design marketing visuals',
      'Create campaign graphics',
      'Develop brand assets',
      'Design social media graphics',
      'Produce print-ready materials'
    ],
    recommendedTasks: [
      'Graphic design requests',
      'Social media graphics',
      'Infographic creation',
      'Presentation design',
      'Brand asset updates'
    ],
    modulesAssigned: ['Marketing Hub'],
    permissions: {
      canReadCRM: false,
      canWriteCRM: false,
      canSendNotifications: false,
      canDraftEmails: false,
      canCreateAssets: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 7,
    tasksToday: 16,
    tasksCompleted7Days: 98,
    avgResponseTime: '2.2s'
  },

  // --- Paid Media ---
  {
    id: 'mkt-009',
    name: 'Advertising Manager',
    role: 'Advertising Manager (PPC)',
    department: 'Marketing',
    category: 'Paid Media',
    description: 'Manages paid advertising campaigns across Google Ads, Facebook Ads, and other platforms to drive qualified leads.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates and manages PPC campaigns',
      'Optimizes ad spend and bidding',
      'Conducts A/B testing on ads',
      'Analyzes campaign performance',
      'Manages retargeting campaigns',
      'Creates audience segments'
    ],
    primaryResponsibilities: [
      'Manage paid advertising campaigns',
      'Optimize ad performance and ROI',
      'Create targeted audience segments',
      'Monitor and adjust ad spend',
      'Report on campaign metrics'
    ],
    recommendedTasks: [
      'Campaign creation',
      'Bid optimization',
      'Ad copy testing',
      'Performance analysis',
      'Budget management'
    ],
    modulesAssigned: ['Marketing Hub', 'Reporting / Analytics', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: false,
      canAccessAnalytics: true,
      canManageBudget: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 9,
    tasksToday: 28,
    tasksCompleted7Days: 178,
    avgResponseTime: '1.2s'
  },
  {
    id: 'mkt-010',
    name: 'Email Marketing Manager',
    role: 'Email Marketing Manager',
    department: 'Marketing',
    category: 'Paid Media',
    description: 'Develops and executes email marketing campaigns, manages subscriber lists, and optimizes email performance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates email campaigns',
      'Manages email lists and segments',
      'Designs email templates',
      'Conducts A/B testing',
      'Analyzes email metrics',
      'Ensures deliverability'
    ],
    primaryResponsibilities: [
      'Develop email marketing strategy',
      'Create and send email campaigns',
      'Manage subscriber segmentation',
      'Optimize email performance',
      'Maintain list hygiene'
    ],
    recommendedTasks: [
      'Campaign creation',
      'List segmentation',
      'Template design',
      'A/B testing',
      'Performance reporting'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canSendEmails: true,
      canAccessAnalytics: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 19,
    tasksCompleted7Days: 134,
    avgResponseTime: '1.4s'
  },

  // --- Analytics & Optimization ---
  {
    id: 'mkt-011',
    name: 'Marketing Automation Engineer',
    role: 'Marketing Automation Engineer',
    department: 'Marketing',
    category: 'Analytics & Optimization',
    description: 'Builds and maintains marketing automation workflows, integrations, and triggered campaigns.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Builds automation workflows',
      'Creates trigger-based campaigns',
      'Integrates marketing tools',
      'Develops lead scoring models',
      'Manages drip campaigns',
      'Optimizes automation performance'
    ],
    primaryResponsibilities: [
      'Build marketing automation workflows',
      'Create triggered email sequences',
      'Develop lead scoring logic',
      'Integrate marketing platforms',
      'Monitor automation performance'
    ],
    recommendedTasks: [
      'Workflow creation',
      'Integration setup',
      'Lead scoring updates',
      'Drip campaign builds',
      'Automation optimization'
    ],
    modulesAssigned: ['Marketing Hub', 'Automations', 'CRM / Contacts', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canManageAutomations: true,
      canAccessAnalytics: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 11,
    tasksCompleted7Days: 78,
    avgResponseTime: '2.0s'
  },
  {
    id: 'mkt-012',
    name: 'SEO Specialist',
    role: 'SEO Specialist',
    department: 'Marketing',
    category: 'Analytics & Optimization',
    description: 'Optimizes website and content for search engines to improve organic visibility and traffic.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Conducts keyword research',
      'Optimizes on-page SEO',
      'Analyzes search rankings',
      'Builds backlink strategies',
      'Performs technical SEO audits',
      'Creates SEO content briefs'
    ],
    primaryResponsibilities: [
      'Improve organic search rankings',
      'Conduct keyword research',
      'Optimize website content',
      'Monitor search performance',
      'Develop link building strategy'
    ],
    recommendedTasks: [
      'Keyword research',
      'On-page optimization',
      'Technical SEO audits',
      'Ranking analysis',
      'Content optimization'
    ],
    modulesAssigned: ['Marketing Hub', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: false,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: false,
      canAccessAnalytics: true,
      canEditWebsite: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 14,
    tasksCompleted7Days: 92,
    avgResponseTime: '1.8s'
  },
  {
    id: 'mkt-013',
    name: 'Web Optimization Agent',
    role: 'Web & Landing Page Optimization',
    department: 'Marketing',
    category: 'Analytics & Optimization',
    description: 'Optimizes website performance, landing pages, and conversion funnels for maximum lead generation.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Optimizes landing pages',
      'Improves page load speed',
      'Conducts UX analysis',
      'Creates conversion funnels',
      'Implements tracking pixels',
      'A/B tests page elements'
    ],
    primaryResponsibilities: [
      'Optimize landing page performance',
      'Improve website conversion rates',
      'Implement tracking and analytics',
      'Conduct user experience audits',
      'Test page variations'
    ],
    recommendedTasks: [
      'Landing page optimization',
      'Speed optimization',
      'Conversion analysis',
      'A/B test setup',
      'Tracking implementation'
    ],
    modulesAssigned: ['Marketing Hub', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: false,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: false,
      canAccessAnalytics: true,
      canEditWebsite: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 10,
    tasksCompleted7Days: 64,
    avgResponseTime: '2.2s'
  },
  {
    id: 'mkt-014',
    name: 'Analytics & Reporting Agent',
    role: 'Analytics & Reporting Agent',
    department: 'Marketing',
    category: 'Analytics & Optimization',
    description: 'Collects, analyzes, and reports on marketing performance data to drive data-informed decisions.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates marketing reports',
      'Analyzes campaign performance',
      'Builds dashboards',
      'Tracks KPIs and metrics',
      'Identifies trends and insights',
      'Forecasts marketing outcomes'
    ],
    primaryResponsibilities: [
      'Generate marketing reports',
      'Analyze performance data',
      'Build and maintain dashboards',
      'Track marketing KPIs',
      'Provide data-driven insights'
    ],
    recommendedTasks: [
      'Report generation',
      'Dashboard creation',
      'Performance analysis',
      'Trend identification',
      'KPI tracking'
    ],
    modulesAssigned: ['Marketing Hub', 'Reporting / Analytics', 'Sales Hub', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: false,
      canAccessAnalytics: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 7 },
    tasksInQueue: 4,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.6s'
  },
  {
    id: 'mkt-015',
    name: 'Customer Journey Architect',
    role: 'Customer Journey & Funnel Architect',
    department: 'Marketing',
    category: 'Analytics & Optimization',
    description: 'Designs and optimizes customer journeys and marketing funnels from awareness to conversion.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Maps customer journeys',
      'Designs marketing funnels',
      'Identifies friction points',
      'Creates touchpoint strategies',
      'Optimizes conversion paths',
      'Develops nurture sequences'
    ],
    primaryResponsibilities: [
      'Design customer journey maps',
      'Optimize marketing funnels',
      'Identify and fix friction points',
      'Create touchpoint strategies',
      'Improve customer experience'
    ],
    recommendedTasks: [
      'Journey mapping',
      'Funnel analysis',
      'Friction point identification',
      'Touchpoint optimization',
      'Conversion path design'
    ],
    modulesAssigned: ['Marketing Hub', 'Sales Hub', 'CRM / Contacts', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canAccessAnalytics: true,
      canManageAutomations: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 2,
    tasksToday: 7,
    tasksCompleted7Days: 45,
    avgResponseTime: '2.5s'
  },
  {
    id: 'mkt-020',
    name: 'A/B Testing Agent',
    role: 'A/B Testing & Experimentation',
    department: 'Marketing',
    category: 'Analytics & Optimization',
    description: 'Designs and manages A/B tests and experiments to continuously optimize marketing performance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Designs A/B test experiments',
      'Analyzes test results',
      'Calculates statistical significance',
      'Documents test learnings',
      'Prioritizes test backlog',
      'Implements winning variations'
    ],
    primaryResponsibilities: [
      'Design A/B test experiments',
      'Analyze test performance',
      'Determine statistical winners',
      'Document experiment insights',
      'Manage testing roadmap'
    ],
    recommendedTasks: [
      'Test hypothesis creation',
      'Experiment setup',
      'Results analysis',
      'Winner implementation',
      'Insights documentation'
    ],
    modulesAssigned: ['Marketing Hub', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: false,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: false,
      canAccessAnalytics: true,
      canRunExperiments: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s'
  },

  // --- Customer Journey / PR / Community ---
  {
    id: 'mkt-016',
    name: 'PR & Communications Agent',
    role: 'PR & Communications Agent',
    department: 'Marketing',
    category: 'Customer Journey / PR / Community',
    description: 'Manages public relations, press releases, media outreach, and company communications.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Writes press releases',
      'Manages media relationships',
      'Handles crisis communications',
      'Coordinates PR campaigns',
      'Monitors media mentions',
      'Develops spokesperson talking points'
    ],
    primaryResponsibilities: [
      'Manage media relations',
      'Write press releases',
      'Handle company communications',
      'Monitor brand mentions',
      'Coordinate PR activities'
    ],
    recommendedTasks: [
      'Press release writing',
      'Media outreach',
      'Crisis response',
      'Mention monitoring',
      'Talking points creation'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: false,
      canSendNotifications: true,
      canDraftEmails: true,
      canPublishContent: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 2,
    tasksToday: 6,
    tasksCompleted7Days: 38,
    avgResponseTime: '2.8s'
  },
  {
    id: 'mkt-017',
    name: 'Community Manager',
    role: 'Community Manager',
    department: 'Marketing',
    category: 'Customer Journey / PR / Community',
    description: 'Builds and nurtures customer communities, manages forums, and fosters brand advocacy.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages online communities',
      'Engages with customers',
      'Creates community content',
      'Moderates discussions',
      'Identifies brand advocates',
      'Organizes community events'
    ],
    primaryResponsibilities: [
      'Build and manage communities',
      'Engage with community members',
      'Foster brand advocacy',
      'Moderate discussions',
      'Create community programs'
    ],
    recommendedTasks: [
      'Community engagement',
      'Discussion moderation',
      'Advocate identification',
      'Event coordination',
      'Content creation'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canPublishContent: true
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 24,
    tasksCompleted7Days: 156,
    avgResponseTime: '1.0s'
  },
  {
    id: 'mkt-018',
    name: 'Reputation & Reviews Agent',
    role: 'Reputation & Reviews Agent',
    department: 'Marketing',
    category: 'Customer Journey / PR / Community',
    description: 'Manages online reviews, reputation monitoring, and customer feedback responses.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors online reviews',
      'Responds to customer feedback',
      'Manages reputation platforms',
      'Requests customer reviews',
      'Analyzes sentiment trends',
      'Escalates critical issues'
    ],
    primaryResponsibilities: [
      'Monitor online reputation',
      'Respond to reviews',
      'Request customer reviews',
      'Track sentiment trends',
      'Manage review platforms'
    ],
    recommendedTasks: [
      'Review monitoring',
      'Response drafting',
      'Review requests',
      'Sentiment analysis',
      'Platform management'
    ],
    modulesAssigned: ['Marketing Hub', 'CRM / Contacts', 'Reporting / Analytics'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canAccessAnalytics: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 124,
    avgResponseTime: '1.2s'
  },
  {
    id: 'mkt-019',
    name: 'Lead Nurture Agent',
    role: 'Lead Nurture Follow-Up Agent',
    department: 'Marketing',
    category: 'Customer Journey / PR / Community',
    description: 'Nurtures leads through personalized follow-up sequences to move them toward conversion.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates nurture sequences',
      'Personalizes follow-up messages',
      'Scores lead engagement',
      'Triggers timely outreach',
      'Qualifies marketing leads',
      'Hands off to sales'
    ],
    primaryResponsibilities: [
      'Nurture marketing leads',
      'Create follow-up sequences',
      'Score lead engagement',
      'Qualify leads for sales',
      'Manage lead handoff'
    ],
    recommendedTasks: [
      'Sequence creation',
      'Follow-up messaging',
      'Lead scoring',
      'Qualification checks',
      'Sales handoff'
    ],
    modulesAssigned: ['Marketing Hub', 'Sales Hub', 'CRM / Contacts', 'Automations'],
    permissions: {
      canReadCRM: true,
      canWriteCRM: true,
      canSendNotifications: true,
      canDraftEmails: true,
      canManageAutomations: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 10 },
    tasksInQueue: 15,
    tasksToday: 42,
    tasksCompleted7Days: 287,
    avgResponseTime: '0.9s'
  },

  // ============ OPERATIONS DEPARTMENT ============
  // --- Leadership ---
  {
    id: 'ops-001',
    name: 'COO Agent',
    role: 'Chief Operating Officer',
    department: 'Operations',
    category: 'Leadership',
    description: 'Executive-level operations strategist responsible for overseeing all production, scheduling, crew management, quality control, and fulfillment operations at Rhino Shield.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Oversees all operations department agents',
      'Sets operational KPIs and benchmarks',
      'Coordinates cross-functional initiatives',
      'Analyzes operational efficiency metrics',
      'Approves major scheduling decisions',
      'Manages department resource allocation'
    ],
    primaryResponsibilities: [
      'Define operations vision and strategy',
      'Monitor department-wide performance',
      'Coordinate with Sales and Marketing leadership',
      'Review and approve scheduling changes',
      'Ensure quality and safety compliance'
    ],
    recommendedTasks: [
      'Strategic planning sessions',
      'Performance reviews',
      'Resource allocation',
      'Cross-department coordination',
      'Operational audits'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Scheduling / Calendar', 'Materials & Inventory', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canModifySchedules: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canAccessInventory: true,
      canViewOpsDashboards: true,
      canApproveChanges: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 4,
    tasksToday: 16,
    tasksCompleted7Days: 112,
    avgResponseTime: '1.5s',
    teamMembers: ['ops-002', 'ops-003', 'ops-004', 'ops-005', 'ops-006', 'ops-007', 'ops-008', 'ops-009', 'ops-010', 'ops-011', 'ops-012', 'ops-013', 'ops-014', 'ops-015', 'ops-016', 'ops-017', 'ops-018', 'ops-019', 'ops-020', 'ops-021', 'ops-022', 'ops-023']
  },
  {
    id: 'ops-002',
    name: 'Operations Manager',
    role: 'Operations Manager',
    department: 'Operations',
    category: 'Leadership',
    description: 'Day-to-day operations lead responsible for managing workflow execution, team coordination, and ensuring smooth production processes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages daily operations workflow',
      'Coordinates between teams',
      'Handles escalations and issues',
      'Monitors production progress',
      'Ensures resource availability',
      'Reports to COO on performance'
    ],
    primaryResponsibilities: [
      'Manage daily operational activities',
      'Coordinate cross-team workflows',
      'Handle operational escalations',
      'Monitor and report on KPIs',
      'Ensure team productivity'
    ],
    recommendedTasks: [
      'Daily operations review',
      'Team coordination',
      'Issue resolution',
      'Progress monitoring',
      'Status reporting'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Scheduling / Calendar', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canModifySchedules: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 12 },
    tasksInQueue: 8,
    tasksToday: 32,
    tasksCompleted7Days: 198,
    avgResponseTime: '1.0s'
  },

  // --- Scheduling & Workflow ---
  {
    id: 'ops-003',
    name: 'Production Scheduler',
    role: 'Production Scheduler',
    department: 'Operations',
    category: 'Scheduling & Workflow',
    description: 'Manages the master production schedule, coordinates job timelines, and ensures optimal resource utilization across all projects.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates and manages production schedules',
      'Optimizes job sequencing',
      'Balances workload across crews',
      'Identifies scheduling conflicts',
      'Adjusts timelines for delays',
      'Forecasts capacity needs'
    ],
    primaryResponsibilities: [
      'Maintain master production schedule',
      'Optimize job sequencing and timing',
      'Balance crew workloads',
      'Manage schedule conflicts',
      'Forecast resource requirements'
    ],
    recommendedTasks: [
      'Schedule creation',
      'Conflict resolution',
      'Timeline adjustments',
      'Capacity planning',
      'Workload balancing'
    ],
    modulesAssigned: ['Operations Hub', 'Scheduling / Calendar', 'CRM / Jobs'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canModifySchedules: true,
      canReadCustomerNotes: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 38,
    tasksCompleted7Days: 245,
    avgResponseTime: '0.9s'
  },
  {
    id: 'ops-004',
    name: 'Job Prep Coordinator',
    role: 'Job Prep Coordinator',
    department: 'Operations',
    category: 'Scheduling & Workflow',
    description: 'Ensures all job prerequisites are completed before crew dispatch, including materials, permits, and customer confirmations.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Verifies job readiness checklist',
      'Confirms material availability',
      'Validates permits and approvals',
      'Coordinates customer confirmations',
      'Prepares job documentation',
      'Flags incomplete preparations'
    ],
    primaryResponsibilities: [
      'Verify all job prerequisites',
      'Coordinate material staging',
      'Confirm customer availability',
      'Prepare crew job packets',
      'Flag and resolve prep issues'
    ],
    recommendedTasks: [
      'Readiness verification',
      'Material confirmation',
      'Customer contact',
      'Documentation prep',
      'Issue flagging'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Materials & Inventory'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canAccessInventory: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 24,
    tasksCompleted7Days: 168,
    avgResponseTime: '1.2s'
  },
  {
    id: 'ops-016',
    name: 'Weather Monitor Agent',
    role: 'Weather Monitoring & Rescheduling',
    department: 'Operations',
    category: 'Scheduling & Workflow',
    description: 'Monitors weather conditions and proactively reschedules jobs affected by inclement weather to minimize disruptions.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors weather forecasts',
      'Identifies at-risk jobs',
      'Triggers rescheduling workflows',
      'Notifies crews and customers',
      'Tracks weather delays',
      'Optimizes reschedule timing'
    ],
    primaryResponsibilities: [
      'Monitor weather conditions daily',
      'Identify weather-impacted jobs',
      'Initiate rescheduling process',
      'Communicate changes to stakeholders',
      'Track weather-related delays'
    ],
    recommendedTasks: [
      'Weather monitoring',
      'Risk assessment',
      'Reschedule coordination',
      'Stakeholder notification',
      'Delay tracking'
    ],
    modulesAssigned: ['Operations Hub', 'Scheduling / Calendar', 'Automations'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canModifySchedules: true,
      canSendNotifications: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 6 },
    tasksInQueue: 3,
    tasksToday: 12,
    tasksCompleted7Days: 78,
    avgResponseTime: '1.4s'
  },
  {
    id: 'ops-021',
    name: 'Workflow Automation Agent',
    role: 'Workflow Automation Agent (Ops)',
    department: 'Operations',
    category: 'Scheduling & Workflow',
    description: 'Designs, implements, and maintains operational automation workflows to improve efficiency and reduce manual tasks.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates automation workflows',
      'Optimizes existing processes',
      'Monitors automation performance',
      'Troubleshoots workflow issues',
      'Integrates systems and tools',
      'Documents automation logic'
    ],
    primaryResponsibilities: [
      'Build operational automations',
      'Optimize workflow efficiency',
      'Monitor automation health',
      'Resolve automation failures',
      'Document automation processes'
    ],
    recommendedTasks: [
      'Workflow creation',
      'Process optimization',
      'Issue troubleshooting',
      'Integration setup',
      'Performance monitoring'
    ],
    modulesAssigned: ['Operations Hub', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canModifySchedules: true,
      canManageAutomations: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 14,
    tasksCompleted7Days: 92,
    avgResponseTime: '2.0s'
  },

  // --- Field Operations ---
  {
    id: 'ops-005',
    name: 'Field Ops Supervisor',
    role: 'Field Operations Supervisor',
    department: 'Operations',
    category: 'Field Operations',
    description: 'Oversees field crews during job execution, ensures work quality, and handles on-site issues and escalations.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Supervises field crew activities',
      'Monitors job progress in real-time',
      'Handles on-site escalations',
      'Ensures quality standards',
      'Coordinates with dispatch',
      'Reports field status updates'
    ],
    primaryResponsibilities: [
      'Supervise field operations',
      'Monitor crew performance',
      'Handle on-site issues',
      'Ensure quality compliance',
      'Provide status updates'
    ],
    recommendedTasks: [
      'Field supervision',
      'Progress monitoring',
      'Issue escalation',
      'Quality checks',
      'Status reporting'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Scheduling / Calendar'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 28,
    tasksCompleted7Days: 186,
    avgResponseTime: '1.1s'
  },
  {
    id: 'ops-006',
    name: 'Dispatch Agent',
    role: 'Crew Assignment & Dispatch',
    department: 'Operations',
    category: 'Field Operations',
    description: 'Assigns crews to jobs based on skills, availability, and location, and manages dispatch communications.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Assigns crews to jobs',
      'Optimizes crew routing',
      'Manages dispatch communications',
      'Tracks crew locations',
      'Handles reassignments',
      'Coordinates emergency dispatch'
    ],
    primaryResponsibilities: [
      'Assign crews to scheduled jobs',
      'Optimize crew routing',
      'Manage dispatch communications',
      'Handle crew reassignments',
      'Track field team locations'
    ],
    recommendedTasks: [
      'Crew assignment',
      'Route optimization',
      'Dispatch coordination',
      'Reassignment handling',
      'Location tracking'
    ],
    modulesAssigned: ['Operations Hub', 'Scheduling / Calendar', 'CRM / Jobs'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canModifySchedules: true,
      canSendNotifications: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 15 },
    tasksInQueue: 9,
    tasksToday: 45,
    tasksCompleted7Days: 312,
    avgResponseTime: '0.7s'
  },
  {
    id: 'ops-007',
    name: 'Crew Management Agent',
    role: 'Crew Management Agent',
    department: 'Operations',
    category: 'Field Operations',
    description: 'Tracks crew availability, certifications, performance, and manages subcontractor relationships.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks crew availability',
      'Manages certifications and training',
      'Monitors crew performance',
      'Manages subcontractor relationships',
      'Handles time-off requests',
      'Maintains crew profiles'
    ],
    primaryResponsibilities: [
      'Manage crew availability',
      'Track certifications and training',
      'Monitor performance metrics',
      'Coordinate with subcontractors',
      'Maintain crew records'
    ],
    recommendedTasks: [
      'Availability tracking',
      'Certification management',
      'Performance reviews',
      'Subcontractor coordination',
      'Record updates'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canViewOpsDashboards: true,
      canManageCrews: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 18,
    tasksCompleted7Days: 124,
    avgResponseTime: '1.3s'
  },
  {
    id: 'ops-009',
    name: 'Safety Compliance Agent',
    role: 'Safety Compliance Agent',
    department: 'Operations',
    category: 'Field Operations',
    description: 'Monitors safety compliance, tracks incidents, ensures proper protocols are followed, and manages safety training.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors safety compliance',
      'Tracks safety incidents',
      'Manages safety training records',
      'Conducts safety audits',
      'Ensures protocol adherence',
      'Reports safety metrics'
    ],
    primaryResponsibilities: [
      'Monitor safety compliance',
      'Track and report incidents',
      'Manage safety training',
      'Conduct regular audits',
      'Enforce safety protocols'
    ],
    recommendedTasks: [
      'Compliance monitoring',
      'Incident tracking',
      'Training management',
      'Safety audits',
      'Protocol enforcement'
    ],
    modulesAssigned: ['Operations Hub', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canViewOpsDashboards: true,
      canManageSafety: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 2,
    tasksToday: 10,
    tasksCompleted7Days: 68,
    avgResponseTime: '1.8s'
  },
  {
    id: 'ops-008',
    name: 'QC Inspector Agent',
    role: 'Quality Control Inspector',
    department: 'Operations',
    category: 'Field Operations',
    description: 'Performs quality inspections on completed work, documents findings, and ensures all jobs meet Rhino Shield standards.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Conducts quality inspections',
      'Documents inspection findings',
      'Identifies deficiencies',
      'Creates punchlist items',
      'Verifies rework completion',
      'Reports quality metrics'
    ],
    primaryResponsibilities: [
      'Perform post-job inspections',
      'Document quality findings',
      'Create and track punchlists',
      'Verify rework completion',
      'Report quality metrics'
    ],
    recommendedTasks: [
      'Quality inspection',
      'Findings documentation',
      'Punchlist creation',
      'Rework verification',
      'Quality reporting'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 16,
    tasksCompleted7Days: 108,
    avgResponseTime: '1.5s'
  },
  {
    id: 'ops-018',
    name: 'Punchlist Coordinator',
    role: 'Punchlist Coordinator',
    department: 'Operations',
    category: 'Field Operations',
    description: 'Manages punchlist items, coordinates rework scheduling, and ensures all outstanding items are resolved before closeout.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages punchlist items',
      'Schedules rework activities',
      'Tracks item resolution',
      'Coordinates with crews',
      'Communicates with customers',
      'Verifies completion'
    ],
    primaryResponsibilities: [
      'Manage all punchlist items',
      'Schedule rework activities',
      'Track resolution progress',
      'Coordinate crew assignments',
      'Verify item completion'
    ],
    recommendedTasks: [
      'Punchlist management',
      'Rework scheduling',
      'Progress tracking',
      'Crew coordination',
      'Completion verification'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Scheduling / Calendar'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canModifySchedules: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 7 },
    tasksInQueue: 8,
    tasksToday: 22,
    tasksCompleted7Days: 148,
    avgResponseTime: '1.2s'
  },

  // --- Supply Chain & Logistics ---
  {
    id: 'ops-013',
    name: 'Materials Manager',
    role: 'Materials & Inventory Manager',
    department: 'Operations',
    category: 'Supply Chain & Logistics',
    description: 'Manages inventory levels, material ordering, and ensures adequate stock for all scheduled jobs.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages inventory levels',
      'Processes material orders',
      'Tracks stock availability',
      'Forecasts material needs',
      'Monitors reorder points',
      'Handles inventory audits'
    ],
    primaryResponsibilities: [
      'Manage inventory levels',
      'Process material orders',
      'Forecast material requirements',
      'Maintain accurate stock records',
      'Optimize inventory costs'
    ],
    recommendedTasks: [
      'Inventory management',
      'Order processing',
      'Stock forecasting',
      'Audit coordination',
      'Cost optimization'
    ],
    modulesAssigned: ['Operations Hub', 'Materials & Inventory', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canAccessInventory: true,
      canWriteInventory: true,
      canViewOpsDashboards: true,
      canManagePurchasing: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 20,
    tasksCompleted7Days: 138,
    avgResponseTime: '1.3s'
  },
  {
    id: 'ops-014',
    name: 'Supplier Coordination Agent',
    role: 'Supplier Coordination Agent',
    department: 'Operations',
    category: 'Supply Chain & Logistics',
    description: 'Manages supplier relationships, coordinates orders, and ensures timely material delivery from vendors.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages supplier relationships',
      'Coordinates purchase orders',
      'Tracks order status',
      'Resolves supplier issues',
      'Negotiates pricing',
      'Maintains vendor records'
    ],
    primaryResponsibilities: [
      'Manage supplier relationships',
      'Coordinate material orders',
      'Track delivery schedules',
      'Resolve supply issues',
      'Maintain vendor database'
    ],
    recommendedTasks: [
      'Supplier communication',
      'Order coordination',
      'Delivery tracking',
      'Issue resolution',
      'Vendor management'
    ],
    modulesAssigned: ['Operations Hub', 'Materials & Inventory'],
    permissions: {
      canAccessInventory: true,
      canWriteInventory: true,
      canManagePurchasing: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 14,
    tasksCompleted7Days: 96,
    avgResponseTime: '1.5s'
  },
  {
    id: 'ops-015',
    name: 'Logistics Coordinator',
    role: 'Delivery & Logistics Coordinator',
    department: 'Operations',
    category: 'Supply Chain & Logistics',
    description: 'Coordinates material deliveries to job sites, manages logistics scheduling, and ensures timely arrivals.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Schedules material deliveries',
      'Coordinates logistics timing',
      'Tracks delivery status',
      'Manages delivery exceptions',
      'Optimizes delivery routes',
      'Confirms site readiness'
    ],
    primaryResponsibilities: [
      'Schedule material deliveries',
      'Coordinate logistics timing',
      'Track delivery progress',
      'Handle delivery exceptions',
      'Confirm site accessibility'
    ],
    recommendedTasks: [
      'Delivery scheduling',
      'Logistics coordination',
      'Status tracking',
      'Exception handling',
      'Route optimization'
    ],
    modulesAssigned: ['Operations Hub', 'Materials & Inventory', 'Scheduling / Calendar'],
    permissions: {
      canAccessJobs: true,
      canAccessInventory: true,
      canModifySchedules: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 7 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.2s'
  },
  {
    id: 'ops-020',
    name: 'Permit Agent',
    role: 'Regulatory & Permit Agent',
    department: 'Operations',
    category: 'Supply Chain & Logistics',
    description: 'Manages permit applications, tracks regulatory compliance, and ensures all required approvals are obtained.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages permit applications',
      'Tracks permit status',
      'Ensures regulatory compliance',
      'Coordinates inspections',
      'Maintains permit records',
      'Handles permit renewals'
    ],
    primaryResponsibilities: [
      'Submit permit applications',
      'Track approval status',
      'Ensure regulatory compliance',
      'Coordinate required inspections',
      'Maintain compliance records'
    ],
    recommendedTasks: [
      'Permit application',
      'Status tracking',
      'Compliance verification',
      'Inspection coordination',
      'Record maintenance'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canViewOpsDashboards: true,
      canManagePermits: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 54,
    avgResponseTime: '2.0s'
  },

  // --- Customer-Facing Ops ---
  {
    id: 'ops-010',
    name: 'Customer Care Agent',
    role: 'Customer Care & Follow-Up Agent',
    department: 'Operations',
    category: 'Customer-Facing Ops',
    description: 'Handles customer communications during job execution, provides updates, and addresses concerns promptly.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages customer communications',
      'Provides job status updates',
      'Addresses customer concerns',
      'Schedules follow-up calls',
      'Documents customer feedback',
      'Escalates issues as needed'
    ],
    primaryResponsibilities: [
      'Communicate with customers proactively',
      'Provide job status updates',
      'Address concerns promptly',
      'Document all interactions',
      'Escalate issues appropriately'
    ],
    recommendedTasks: [
      'Customer outreach',
      'Status updates',
      'Concern resolution',
      'Follow-up scheduling',
      'Feedback documentation'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canSendNotifications: true
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 10 },
    tasksInQueue: 7,
    tasksToday: 32,
    tasksCompleted7Days: 218,
    avgResponseTime: '0.9s'
  },
  {
    id: 'ops-011',
    name: 'Warranty Claims Agent',
    role: 'Warranty Claims Agent',
    department: 'Operations',
    category: 'Customer-Facing Ops',
    description: 'Processes warranty claims, coordinates warranty repairs, and tracks warranty status and resolutions.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Processes warranty claims',
      'Validates warranty eligibility',
      'Schedules warranty repairs',
      'Tracks claim resolution',
      'Documents warranty history',
      'Reports warranty metrics'
    ],
    primaryResponsibilities: [
      'Process incoming warranty claims',
      'Validate warranty coverage',
      'Schedule repair appointments',
      'Track claim resolution',
      'Maintain warranty records'
    ],
    recommendedTasks: [
      'Claim processing',
      'Eligibility validation',
      'Repair scheduling',
      'Resolution tracking',
      'Record maintenance'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Scheduling / Calendar'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canModifySchedules: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.4s'
  },
  {
    id: 'ops-019',
    name: 'Customer Survey Agent',
    role: 'Customer Satisfaction / Survey Agent',
    department: 'Operations',
    category: 'Customer-Facing Ops',
    description: 'Sends satisfaction surveys, collects feedback, analyzes responses, and identifies improvement opportunities.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Sends satisfaction surveys',
      'Collects customer feedback',
      'Analyzes survey responses',
      'Identifies trends and issues',
      'Reports satisfaction metrics',
      'Triggers follow-up actions'
    ],
    primaryResponsibilities: [
      'Send post-job surveys',
      'Collect and analyze feedback',
      'Identify improvement areas',
      'Report satisfaction scores',
      'Trigger follow-up for low scores'
    ],
    recommendedTasks: [
      'Survey distribution',
      'Response collection',
      'Feedback analysis',
      'Trend identification',
      'Follow-up triggers'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canSendNotifications: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '1.0s'
  },

  // --- Documentation & Reporting ---
  {
    id: 'ops-012',
    name: 'Job Documentation Agent',
    role: 'Job Documentation & Upload Agent',
    department: 'Operations',
    category: 'Documentation & Reporting',
    description: 'Manages job documentation, processes photo uploads, and ensures complete records for all projects.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Processes job documentation',
      'Manages photo uploads',
      'Organizes job records',
      'Validates documentation completeness',
      'Creates documentation reports',
      'Archives completed jobs'
    ],
    primaryResponsibilities: [
      'Process all job documentation',
      'Manage photo uploads and organization',
      'Validate record completeness',
      'Maintain documentation standards',
      'Archive completed project files'
    ],
    recommendedTasks: [
      'Document processing',
      'Photo management',
      'Record validation',
      'Standards compliance',
      'File archiving'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canManageDocuments: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.1s'
  },
  {
    id: 'ops-017',
    name: 'Project Closeout Agent',
    role: 'Project Closeout Agent',
    department: 'Operations',
    category: 'Documentation & Reporting',
    description: 'Manages the project closeout process, ensures all completion requirements are met, and finalizes job records.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages closeout checklist',
      'Verifies completion requirements',
      'Finalizes job records',
      'Coordinates final documentation',
      'Triggers warranty activation',
      'Closes jobs in system'
    ],
    primaryResponsibilities: [
      'Manage closeout checklist',
      'Verify all requirements met',
      'Finalize project documentation',
      'Activate warranty coverage',
      'Close jobs officially'
    ],
    recommendedTasks: [
      'Closeout processing',
      'Requirement verification',
      'Documentation finalization',
      'Warranty activation',
      'Job closure'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canWriteJobs: true,
      canReadCustomerNotes: true,
      canWriteCustomerNotes: true,
      canViewOpsDashboards: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 10,
    tasksCompleted7Days: 72,
    avgResponseTime: '1.6s'
  },
  {
    id: 'ops-022',
    name: 'Cost Tracking Agent',
    role: 'Cost Tracking & Budget Monitoring',
    department: 'Operations',
    category: 'Documentation & Reporting',
    description: 'Monitors job costs, tracks budget variances, and provides cost analysis to optimize profitability.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks job costs',
      'Monitors budget variances',
      'Analyzes cost drivers',
      'Reports profitability metrics',
      'Identifies cost savings',
      'Forecasts project costs'
    ],
    primaryResponsibilities: [
      'Track all job-related costs',
      'Monitor budget performance',
      'Analyze cost variances',
      'Report profitability metrics',
      'Identify optimization opportunities'
    ],
    recommendedTasks: [
      'Cost tracking',
      'Budget monitoring',
      'Variance analysis',
      'Profitability reporting',
      'Cost forecasting'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canViewOpsDashboards: true,
      canAccessFinancials: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.4s'
  },
  {
    id: 'ops-023',
    name: 'Post-Job Analytics Agent',
    role: 'Post-Job Reporting & Analytics',
    department: 'Operations',
    category: 'Documentation & Reporting',
    description: 'Generates post-job reports, analyzes operational performance, and provides insights for continuous improvement.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Generates post-job reports',
      'Analyzes operational metrics',
      'Identifies performance trends',
      'Creates executive summaries',
      'Benchmarks against targets',
      'Recommends improvements'
    ],
    primaryResponsibilities: [
      'Generate comprehensive reports',
      'Analyze operational performance',
      'Identify trends and patterns',
      'Create leadership summaries',
      'Recommend improvements'
    ],
    recommendedTasks: [
      'Report generation',
      'Performance analysis',
      'Trend identification',
      'Executive summaries',
      'Improvement recommendations'
    ],
    modulesAssigned: ['Operations Hub', 'CRM / Jobs', 'Reporting / Analytics'],
    permissions: {
      canAccessJobs: true,
      canViewOpsDashboards: true,
      canAccessAnalytics: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 2,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.2s'
  },

  // ============ SALES DEPARTMENT ============
  // --- Leadership ---
  {
    id: 'sales-001',
    name: 'CSO Agent',
    role: 'Chief Sales Officer',
    department: 'Sales',
    category: 'Leadership',
    description: 'Executive-level sales strategist responsible for overseeing all sales operations, revenue targets, team performance, and go-to-market strategy at Rhino Shield.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Oversees all sales department agents',
      'Sets revenue targets and quotas',
      'Develops sales strategy',
      'Analyzes sales performance metrics',
      'Approves major deals and pricing',
      'Manages department budget and headcount'
    ],
    primaryResponsibilities: [
      'Define sales vision and strategy',
      'Set and monitor revenue targets',
      'Review team and individual performance',
      'Coordinate with Marketing and Operations',
      'Approve significant pricing decisions'
    ],
    recommendedTasks: [
      'Strategic planning',
      'Performance reviews',
      'Revenue forecasting',
      'Deal approvals',
      'Territory planning'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true,
      canViewCommissions: true,
      canApproveDeals: true,
      canSendCustomerEmails: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 3,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.8s',
    metrics: { dailyContacts: 15, weeklyAppointments: 8, closeRate: '32%' },
    teamMembers: ['sales-002', 'sales-003', 'sales-004', 'sales-005', 'sales-006', 'sales-007', 'sales-008', 'sales-009', 'sales-010', 'sales-011', 'sales-012', 'sales-013', 'sales-014', 'sales-015', 'sales-016', 'sales-017', 'sales-018', 'sales-019', 'sales-020', 'sales-021', 'sales-022', 'sales-023', 'sales-024', 'sales-025', 'sales-026', 'sales-027', 'sales-028', 'sales-029', 'sales-030', 'sales-031', 'sales-032', 'sales-033', 'sales-034', 'sales-035', 'sales-036', 'sales-037', 'sales-038', 'sales-039', 'sales-040', 'sales-041', 'sales-042']
  },
  {
    id: 'sales-002',
    name: 'Regional Director – North',
    role: 'Regional Sales Director (North)',
    department: 'Sales',
    category: 'Leadership',
    description: 'Manages sales operations and team performance for the Northern territory, ensuring quota attainment and customer satisfaction.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages Northern territory sales',
      'Coaches and develops reps',
      'Monitors regional performance',
      'Handles escalated deals',
      'Coordinates territory planning',
      'Reports to CSO on metrics'
    ],
    primaryResponsibilities: [
      'Manage Northern territory team',
      'Achieve regional revenue targets',
      'Coach and develop sales reps',
      'Handle deal escalations',
      'Report regional performance'
    ],
    recommendedTasks: [
      'Territory reviews',
      'Rep coaching',
      'Deal support',
      'Pipeline reviews',
      'Performance reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true,
      canApproveDeals: true,
      canSendCustomerEmails: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.2s',
    metrics: { dailyContacts: 25, weeklyAppointments: 15, closeRate: '28%' }
  },
  {
    id: 'sales-003',
    name: 'Regional Director – South',
    role: 'Regional Sales Director (South)',
    department: 'Sales',
    category: 'Leadership',
    description: 'Manages sales operations and team performance for the Southern territory, ensuring quota attainment and customer satisfaction.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages Southern territory sales',
      'Coaches and develops reps',
      'Monitors regional performance',
      'Handles escalated deals',
      'Coordinates territory planning',
      'Reports to CSO on metrics'
    ],
    primaryResponsibilities: [
      'Manage Southern territory team',
      'Achieve regional revenue targets',
      'Coach and develop sales reps',
      'Handle deal escalations',
      'Report regional performance'
    ],
    recommendedTasks: [
      'Territory reviews',
      'Rep coaching',
      'Deal support',
      'Pipeline reviews',
      'Performance reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true,
      canApproveDeals: true,
      canSendCustomerEmails: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 20,
    tasksCompleted7Days: 134,
    avgResponseTime: '1.1s',
    metrics: { dailyContacts: 28, weeklyAppointments: 18, closeRate: '30%' }
  },
  {
    id: 'sales-004',
    name: 'Inside Sales Manager',
    role: 'Inside Sales Manager',
    department: 'Sales',
    category: 'Leadership',
    description: 'Manages the inside sales team handling inbound leads, phone sales, and virtual appointments.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages inside sales team',
      'Monitors call metrics',
      'Trains phone sales techniques',
      'Handles inbound escalations',
      'Optimizes conversion rates',
      'Coordinates with marketing'
    ],
    primaryResponsibilities: [
      'Lead inside sales operations',
      'Train and coach phone reps',
      'Monitor call quality and metrics',
      'Optimize lead conversion',
      'Report on inside sales KPIs'
    ],
    recommendedTasks: [
      'Call monitoring',
      'Rep coaching',
      'Conversion optimization',
      'Lead distribution',
      'Performance tracking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canSendCustomerEmails: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 12 },
    tasksInQueue: 8,
    tasksToday: 28,
    tasksCompleted7Days: 186,
    avgResponseTime: '0.9s',
    metrics: { dailyContacts: 45, weeklyAppointments: 32, closeRate: '24%' }
  },
  {
    id: 'sales-005',
    name: 'Outside Sales Manager',
    role: 'Outside Sales Manager',
    department: 'Sales',
    category: 'Leadership',
    description: 'Manages the field sales team conducting in-person appointments, site visits, and on-location estimates.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages field sales team',
      'Coordinates territory coverage',
      'Trains in-person selling',
      'Handles field escalations',
      'Optimizes route efficiency',
      'Reviews site visit quality'
    ],
    primaryResponsibilities: [
      'Lead outside sales operations',
      'Train field sales reps',
      'Coordinate territory assignments',
      'Monitor appointment completion',
      'Report on field sales KPIs'
    ],
    recommendedTasks: [
      'Route planning',
      'Field coaching',
      'Appointment review',
      'Territory optimization',
      'Performance tracking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true,
      canSendCustomerEmails: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 7,
    tasksToday: 24,
    tasksCompleted7Days: 168,
    avgResponseTime: '1.0s',
    metrics: { dailyContacts: 20, weeklyAppointments: 25, closeRate: '35%' }
  },
  {
    id: 'sales-006',
    name: 'Sales Trainer Agent',
    role: 'Field Sales Trainer & Coaching',
    department: 'Sales',
    category: 'Leadership',
    description: 'Provides ongoing training, coaching, and skill development for all sales team members.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Delivers sales training programs',
      'Provides one-on-one coaching',
      'Creates training materials',
      'Evaluates rep skills',
      'Tracks training completion',
      'Identifies skill gaps'
    ],
    primaryResponsibilities: [
      'Develop training curriculum',
      'Conduct coaching sessions',
      'Evaluate rep performance',
      'Track skill development',
      'Report on training metrics'
    ],
    recommendedTasks: [
      'Training delivery',
      'Coaching sessions',
      'Skill assessments',
      'Material creation',
      'Progress tracking'
    ],
    modulesAssigned: ['Sales Hub', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canViewPerformance: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 10,
    tasksCompleted7Days: 68,
    avgResponseTime: '2.0s',
    metrics: { dailyContacts: 8, weeklyAppointments: 12, closeRate: 'N/A' }
  },
  {
    id: 'sales-007',
    name: 'Performance Agent',
    role: 'Sales Performance & Accountability',
    department: 'Sales',
    category: 'Leadership',
    description: 'Monitors individual and team sales performance, tracks KPIs, and ensures accountability to targets.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks sales KPIs',
      'Monitors quota attainment',
      'Generates performance reports',
      'Identifies underperformers',
      'Creates accountability plans',
      'Benchmarks against targets'
    ],
    primaryResponsibilities: [
      'Monitor sales performance',
      'Track quota progress',
      'Generate performance reports',
      'Flag performance issues',
      'Support improvement plans'
    ],
    recommendedTasks: [
      'KPI tracking',
      'Performance reporting',
      'Quota monitoring',
      'Issue flagging',
      'Benchmarking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canViewPerformance: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 14,
    tasksCompleted7Days: 96,
    avgResponseTime: '1.5s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-008',
    name: 'Compliance Agent',
    role: 'Sales Compliance & Documentation',
    department: 'Sales',
    category: 'Leadership',
    description: 'Ensures all sales activities comply with company policies, regulations, and documentation standards.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Reviews sales documentation',
      'Ensures policy compliance',
      'Audits sales processes',
      'Maintains compliance records',
      'Flags violations',
      'Updates compliance guidelines'
    ],
    primaryResponsibilities: [
      'Audit sales documentation',
      'Ensure regulatory compliance',
      'Review contract terms',
      'Maintain compliance records',
      'Report compliance issues'
    ],
    recommendedTasks: [
      'Document audits',
      'Compliance reviews',
      'Policy updates',
      'Violation flagging',
      'Record maintenance'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canViewEstimates: true,
      canAuditDocuments: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 2,
    tasksToday: 8,
    tasksCompleted7Days: 54,
    avgResponseTime: '2.2s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-009',
    name: 'Commission Agent',
    role: 'Commission & Compensation',
    department: 'Sales',
    category: 'Leadership',
    description: 'Calculates and tracks sales commissions, bonus structures, and compensation for the sales team.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Calculates commissions',
      'Tracks bonus eligibility',
      'Generates commission reports',
      'Handles compensation disputes',
      'Maintains payout records',
      'Forecasts commission expenses'
    ],
    primaryResponsibilities: [
      'Calculate sales commissions',
      'Track bonus attainment',
      'Generate payout reports',
      'Resolve compensation questions',
      'Maintain accurate records'
    ],
    recommendedTasks: [
      'Commission calculation',
      'Bonus tracking',
      'Payout processing',
      'Dispute resolution',
      'Report generation'
    ],
    modulesAssigned: ['Sales Hub', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canViewCommissions: true,
      canCalculatePayouts: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 3,
    tasksToday: 6,
    tasksCompleted7Days: 42,
    avgResponseTime: '2.5s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },

  // --- Sales Reps ---
  {
    id: 'sales-010',
    name: 'Senior Rep – Tier 1',
    role: 'Senior Sales Representative (Tier 1)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'Top-tier senior sales rep handling high-value opportunities, complex deals, and enterprise-level customers.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles complex deals',
      'Manages enterprise accounts',
      'Conducts consultative selling',
      'Creates custom proposals',
      'Negotiates contract terms',
      'Mentors junior reps'
    ],
    primaryResponsibilities: [
      'Close high-value deals',
      'Manage strategic accounts',
      'Develop custom solutions',
      'Negotiate favorable terms',
      'Achieve quota targets'
    ],
    recommendedTasks: [
      'Enterprise sales',
      'Complex negotiations',
      'Custom proposals',
      'Account management',
      'Deal closing'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '1.5s',
    metrics: { dailyContacts: 12, weeklyAppointments: 8, closeRate: '42%' }
  },
  {
    id: 'sales-011',
    name: 'Senior Rep – Tier 2',
    role: 'Senior Sales Representative (Tier 2)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'Experienced senior sales rep handling mid-to-high value opportunities with proven closing skills.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles mid-high value deals',
      'Conducts consultative selling',
      'Creates proposals',
      'Negotiates pricing',
      'Manages accounts',
      'Supports team members'
    ],
    primaryResponsibilities: [
      'Close mid-high value deals',
      'Manage assigned accounts',
      'Develop proposals',
      'Negotiate terms',
      'Achieve quota targets'
    ],
    recommendedTasks: [
      'Consultative sales',
      'Proposal creation',
      'Account management',
      'Negotiation',
      'Deal closing'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true
    },
    limits: { maxTasksPerHour: 18, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 10,
    tasksCompleted7Days: 68,
    avgResponseTime: '1.3s',
    metrics: { dailyContacts: 15, weeklyAppointments: 10, closeRate: '38%' }
  },
  {
    id: 'sales-012',
    name: 'Senior Rep – Tier 3',
    role: 'Senior Sales Representative (Tier 3)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'Senior sales rep handling standard high-quality opportunities with strong track record.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles quality opportunities',
      'Conducts sales presentations',
      'Creates estimates',
      'Follows sales process',
      'Maintains pipeline',
      'Achieves targets'
    ],
    primaryResponsibilities: [
      'Close assigned opportunities',
      'Conduct presentations',
      'Create accurate estimates',
      'Follow sales process',
      'Meet quota requirements'
    ],
    recommendedTasks: [
      'Sales presentations',
      'Estimate creation',
      'Pipeline management',
      'Follow-up calls',
      'Deal closing'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 6 },
    tasksInQueue: 6,
    tasksToday: 12,
    tasksCompleted7Days: 78,
    avgResponseTime: '1.2s',
    metrics: { dailyContacts: 18, weeklyAppointments: 12, closeRate: '35%' }
  },
  {
    id: 'sales-013',
    name: 'Sales Rep – Tier 1',
    role: 'Sales Representative (Tier 1)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'Skilled sales representative handling standard opportunities with consistent performance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles standard opportunities',
      'Conducts sales calls',
      'Creates estimates',
      'Follows sales process',
      'Updates CRM records',
      'Meets activity targets'
    ],
    primaryResponsibilities: [
      'Work assigned opportunities',
      'Conduct sales presentations',
      'Create estimates',
      'Maintain CRM accuracy',
      'Achieve quota'
    ],
    recommendedTasks: [
      'Sales calls',
      'Presentations',
      'Estimates',
      'CRM updates',
      'Follow-ups'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 7,
    tasksToday: 16,
    tasksCompleted7Days: 108,
    avgResponseTime: '1.0s',
    metrics: { dailyContacts: 22, weeklyAppointments: 14, closeRate: '30%' }
  },
  {
    id: 'sales-014',
    name: 'Sales Rep – Tier 2',
    role: 'Sales Representative (Tier 2)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'Developing sales representative building skills and pipeline with regular coaching.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Works assigned leads',
      'Conducts sales calls',
      'Creates basic estimates',
      'Follows sales playbook',
      'Updates records',
      'Seeks coaching'
    ],
    primaryResponsibilities: [
      'Develop assigned leads',
      'Conduct presentations',
      'Create estimates',
      'Follow sales process',
      'Learn and improve'
    ],
    recommendedTasks: [
      'Lead development',
      'Sales calls',
      'Basic estimates',
      'CRM updates',
      'Training'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true
    },
    limits: { maxTasksPerHour: 28, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 18,
    tasksCompleted7Days: 118,
    avgResponseTime: '0.9s',
    metrics: { dailyContacts: 25, weeklyAppointments: 12, closeRate: '26%' }
  },
  {
    id: 'sales-015',
    name: 'Sales Rep – Tier 3',
    role: 'Sales Representative (Tier 3)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'Growing sales representative focused on activity and skill development.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Works new leads',
      'Makes outbound calls',
      'Assists with estimates',
      'Follows playbook',
      'Maintains activity',
      'Receives coaching'
    ],
    primaryResponsibilities: [
      'Develop new leads',
      'Maintain high activity',
      'Follow sales process',
      'Support senior reps',
      'Continuously improve'
    ],
    recommendedTasks: [
      'Lead outreach',
      'Activity calls',
      'Estimate support',
      'CRM updates',
      'Skill building'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 9,
    tasksToday: 22,
    tasksCompleted7Days: 148,
    avgResponseTime: '0.8s',
    metrics: { dailyContacts: 30, weeklyAppointments: 10, closeRate: '22%' }
  },
  {
    id: 'sales-016',
    name: 'Junior Rep – Tier 1',
    role: 'Junior Sales Representative (Tier 1)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'Entry-level sales rep in training focused on learning the sales process and building foundational skills.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Learns sales process',
      'Shadows senior reps',
      'Makes introductory calls',
      'Updates CRM records',
      'Attends training',
      'Builds product knowledge'
    ],
    primaryResponsibilities: [
      'Learn sales fundamentals',
      'Shadow experienced reps',
      'Make supervised calls',
      'Maintain CRM records',
      'Complete training modules'
    ],
    recommendedTasks: [
      'Training completion',
      'Shadowing sessions',
      'Intro calls',
      'CRM entry',
      'Product learning'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.0s',
    metrics: { dailyContacts: 20, weeklyAppointments: 5, closeRate: '15%' }
  },
  {
    id: 'sales-017',
    name: 'Junior Rep – Tier 2',
    role: 'Junior Sales Representative (Tier 2)',
    department: 'Sales',
    category: 'Sales Reps',
    description: 'New sales rep completing onboarding and initial training program.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Completes onboarding',
      'Learns company products',
      'Observes sales calls',
      'Practices scripts',
      'Studies materials',
      'Asks questions'
    ],
    primaryResponsibilities: [
      'Complete onboarding',
      'Learn product offerings',
      'Observe senior reps',
      'Practice sales scripts',
      'Build foundational skills'
    ],
    recommendedTasks: [
      'Onboarding tasks',
      'Product training',
      'Call observation',
      'Script practice',
      'Knowledge tests'
    ],
    modulesAssigned: ['Sales Hub', 'CRM'],
    permissions: {
      canReadLeads: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 78,
    avgResponseTime: '1.2s',
    metrics: { dailyContacts: 10, weeklyAppointments: 2, closeRate: '10%' }
  },

  // --- Lead Intake & Qualification ---
  {
    id: 'sales-018',
    name: 'SDR Agent',
    role: 'Lead Qualification Specialist (SDR)',
    department: 'Sales',
    category: 'Lead Intake & Qualification',
    description: 'Qualifies inbound leads, determines fit, and routes qualified opportunities to appropriate sales reps.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Qualifies inbound leads',
      'Assesses customer fit',
      'Gathers requirements',
      'Routes to sales reps',
      'Updates lead status',
      'Tracks qualification metrics'
    ],
    primaryResponsibilities: [
      'Qualify all inbound leads',
      'Assess customer needs',
      'Route qualified leads',
      'Maintain lead records',
      'Meet qualification targets'
    ],
    recommendedTasks: [
      'Lead qualification',
      'Needs assessment',
      'Lead routing',
      'Status updates',
      'Metric tracking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 12 },
    tasksInQueue: 12,
    tasksToday: 45,
    tasksCompleted7Days: 312,
    avgResponseTime: '0.6s',
    metrics: { dailyContacts: 60, weeklyAppointments: 25, closeRate: 'N/A' }
  },
  {
    id: 'sales-019',
    name: 'Inbound Call Agent',
    role: 'Inbound Call & Inquiry Handling',
    department: 'Sales',
    category: 'Lead Intake & Qualification',
    description: 'Handles incoming phone calls, answers inquiries, and captures lead information from prospective customers.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Answers inbound calls',
      'Captures lead information',
      'Answers product questions',
      'Schedules callbacks',
      'Routes to specialists',
      'Logs all interactions'
    ],
    primaryResponsibilities: [
      'Handle incoming calls',
      'Capture accurate lead data',
      'Answer basic questions',
      'Route complex inquiries',
      'Maintain call records'
    ],
    recommendedTasks: [
      'Call handling',
      'Lead capture',
      'Question answering',
      'Callback scheduling',
      'Call logging'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 52,
    tasksCompleted7Days: 368,
    avgResponseTime: '0.4s',
    metrics: { dailyContacts: 75, weeklyAppointments: 20, closeRate: 'N/A' }
  },
  {
    id: 'sales-020',
    name: 'Missed Call Agent',
    role: 'Missed Call Follow-Up Agent',
    department: 'Sales',
    category: 'Lead Intake & Qualification',
    description: 'Follows up on missed calls within minutes to re-engage potential customers and capture their information.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks missed calls',
      'Initiates rapid callbacks',
      'Re-engages prospects',
      'Captures lead info',
      'Schedules appointments',
      'Reports callback metrics'
    ],
    primaryResponsibilities: [
      'Monitor missed calls',
      'Execute rapid callbacks',
      'Re-engage prospects',
      'Capture lead information',
      'Schedule follow-ups'
    ],
    recommendedTasks: [
      'Callback execution',
      'Lead re-engagement',
      'Info capture',
      'Appointment setting',
      'Metric reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true
    },
    limits: { maxTasksPerHour: 55, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 38,
    tasksCompleted7Days: 264,
    avgResponseTime: '0.5s',
    metrics: { dailyContacts: 50, weeklyAppointments: 18, closeRate: 'N/A' }
  },
  {
    id: 'sales-021',
    name: 'Outbound Dialer Agent',
    role: 'Outbound Dialing Agent',
    department: 'Sales',
    category: 'Lead Intake & Qualification',
    description: 'Makes proactive outbound calls to cold leads, aged leads, and marketing lists to generate appointments.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Makes cold calls',
      'Works aged lead lists',
      'Generates appointments',
      'Qualifies interest',
      'Handles objections',
      'Tracks dial metrics'
    ],
    primaryResponsibilities: [
      'Execute outbound calls',
      'Work assigned lists',
      'Generate appointments',
      'Qualify prospects',
      'Meet dial targets'
    ],
    recommendedTasks: [
      'Cold calling',
      'List working',
      'Appointment generation',
      'Objection handling',
      'Dial tracking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true
    },
    limits: { maxTasksPerHour: 70, maxConcurrentTasks: 8 },
    tasksInQueue: 15,
    tasksToday: 65,
    tasksCompleted7Days: 456,
    avgResponseTime: '0.3s',
    metrics: { dailyContacts: 100, weeklyAppointments: 15, closeRate: 'N/A' }
  },
  {
    id: 'sales-022',
    name: 'Appointment Setter',
    role: 'Appointment Setter / Calendar Booking',
    department: 'Sales',
    category: 'Lead Intake & Qualification',
    description: 'Schedules appointments between qualified leads and sales representatives, managing calendar availability.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Schedules appointments',
      'Manages calendar availability',
      'Sends confirmations',
      'Handles rescheduling',
      'Optimizes rep schedules',
      'Tracks appointment metrics'
    ],
    primaryResponsibilities: [
      'Book customer appointments',
      'Manage rep calendars',
      'Send confirmations',
      'Handle reschedules',
      'Optimize scheduling'
    ],
    recommendedTasks: [
      'Appointment booking',
      'Calendar management',
      'Confirmation sending',
      'Reschedule handling',
      'Schedule optimization'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Scheduling / Calendar'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canModifySchedules: true
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 10 },
    tasksInQueue: 10,
    tasksToday: 42,
    tasksCompleted7Days: 294,
    avgResponseTime: '0.7s',
    metrics: { dailyContacts: 55, weeklyAppointments: 45, closeRate: 'N/A' }
  },
  {
    id: 'sales-023',
    name: 'Lead Routing Agent',
    role: 'Lead Routing & Distribution',
    department: 'Sales',
    category: 'Lead Intake & Qualification',
    description: 'Routes and distributes leads to appropriate sales reps based on territory, capacity, and specialization.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Routes leads by territory',
      'Balances rep workloads',
      'Applies routing rules',
      'Handles overflow',
      'Tracks distribution metrics',
      'Optimizes assignment'
    ],
    primaryResponsibilities: [
      'Route leads accurately',
      'Balance team capacity',
      'Apply routing logic',
      'Handle edge cases',
      'Report distribution metrics'
    ],
    recommendedTasks: [
      'Lead routing',
      'Workload balancing',
      'Rule application',
      'Overflow handling',
      'Metric tracking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Automations'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canManageRouting: true
    },
    limits: { maxTasksPerHour: 100, maxConcurrentTasks: 15 },
    tasksInQueue: 8,
    tasksToday: 85,
    tasksCompleted7Days: 598,
    avgResponseTime: '0.2s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-024',
    name: 'CRM Hygiene Agent',
    role: 'CRM Data Hygiene & Cleanup',
    department: 'Sales',
    category: 'Lead Intake & Qualification',
    description: 'Maintains CRM data quality by identifying duplicates, cleaning records, and ensuring data accuracy.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Identifies duplicate records',
      'Merges duplicate entries',
      'Validates data accuracy',
      'Standardizes formats',
      'Archives old records',
      'Reports data quality'
    ],
    primaryResponsibilities: [
      'Identify and merge duplicates',
      'Validate data accuracy',
      'Standardize record formats',
      'Archive stale records',
      'Report data quality metrics'
    ],
    recommendedTasks: [
      'Duplicate detection',
      'Record merging',
      'Data validation',
      'Format standardization',
      'Quality reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canDeleteRecords: true
    },
    limits: { maxTasksPerHour: 80, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 68,
    tasksCompleted7Days: 476,
    avgResponseTime: '0.4s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },

  // --- Pipeline & Sales Ops ---
  {
    id: 'sales-025',
    name: 'Pipeline Manager Agent',
    role: 'Pipeline Management Agent',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Monitors and manages the sales pipeline, tracks deal progression, and identifies stalled opportunities.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors pipeline health',
      'Tracks deal progression',
      'Identifies stalled deals',
      'Forecasts pipeline value',
      'Alerts on aging opportunities',
      'Reports pipeline metrics'
    ],
    primaryResponsibilities: [
      'Monitor pipeline health',
      'Track opportunity stages',
      'Flag stalled deals',
      'Generate pipeline reports',
      'Support forecasting'
    ],
    recommendedTasks: [
      'Pipeline monitoring',
      'Stage tracking',
      'Stall identification',
      'Report generation',
      'Forecast support'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 24,
    tasksCompleted7Days: 168,
    avgResponseTime: '1.0s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-026',
    name: 'Estimate Prep Agent',
    role: 'Estimate Preparation Agent',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Prepares accurate estimates and quotes based on customer requirements and pricing guidelines.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates accurate estimates',
      'Applies pricing rules',
      'Calculates project costs',
      'Generates quote documents',
      'Handles revisions',
      'Tracks estimate metrics'
    ],
    primaryResponsibilities: [
      'Prepare accurate estimates',
      'Apply correct pricing',
      'Calculate all costs',
      'Generate professional quotes',
      'Process revisions quickly'
    ],
    recommendedTasks: [
      'Estimate creation',
      'Pricing application',
      'Cost calculation',
      'Quote generation',
      'Revision handling'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canCreateEstimates: true,
      canViewPricing: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 8,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.5s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-027',
    name: 'Proposal Writer Agent',
    role: 'Proposal Writer & Contract Drafting',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Creates professional proposals and drafts contracts for customer review and signature.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Writes professional proposals',
      'Drafts contract documents',
      'Customizes templates',
      'Incorporates specifications',
      'Handles revisions',
      'Ensures compliance'
    ],
    primaryResponsibilities: [
      'Create compelling proposals',
      'Draft accurate contracts',
      'Customize for each customer',
      'Incorporate all specs',
      'Ensure legal compliance'
    ],
    recommendedTasks: [
      'Proposal writing',
      'Contract drafting',
      'Template customization',
      'Spec incorporation',
      'Revision processing'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canViewEstimates: true,
      canCreateDocuments: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 4 },
    tasksInQueue: 5,
    tasksToday: 10,
    tasksCompleted7Days: 68,
    avgResponseTime: '2.5s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-028',
    name: 'Nurture Sequence Agent',
    role: 'Follow-Up & Nurture Sequence',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Executes automated follow-up sequences to nurture leads through the sales cycle.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Executes nurture sequences',
      'Sends timed follow-ups',
      'Personalizes messages',
      'Tracks engagement',
      'Triggers escalations',
      'Reports sequence metrics'
    ],
    primaryResponsibilities: [
      'Execute nurture campaigns',
      'Send timely follow-ups',
      'Personalize outreach',
      'Track engagement signals',
      'Escalate hot leads'
    ],
    recommendedTasks: [
      'Sequence execution',
      'Follow-up sending',
      'Personalization',
      'Engagement tracking',
      'Escalation handling'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Automations'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canSendEmails: true
    },
    limits: { maxTasksPerHour: 100, maxConcurrentTasks: 20 },
    tasksInQueue: 25,
    tasksToday: 95,
    tasksCompleted7Days: 668,
    avgResponseTime: '0.3s',
    metrics: { dailyContacts: 150, weeklyAppointments: 12, closeRate: 'N/A' }
  },
  {
    id: 'sales-029',
    name: 'Lost Leads Agent',
    role: 'Lost Leads Recovery Agent',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Re-engages lost leads and closed-lost opportunities to identify potential recovery opportunities.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Identifies lost leads',
      'Initiates re-engagement',
      'Assesses recovery potential',
      'Updates lead status',
      'Routes recovered leads',
      'Tracks recovery metrics'
    ],
    primaryResponsibilities: [
      'Review lost opportunities',
      'Execute re-engagement outreach',
      'Assess recovery potential',
      'Route recovered leads',
      'Report recovery metrics'
    ],
    recommendedTasks: [
      'Lost lead review',
      'Re-engagement outreach',
      'Recovery assessment',
      'Lead routing',
      'Metric reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 10,
    tasksToday: 32,
    tasksCompleted7Days: 224,
    avgResponseTime: '0.8s',
    metrics: { dailyContacts: 45, weeklyAppointments: 8, closeRate: '12%' }
  },
  {
    id: 'sales-030',
    name: 'Rehash Agent',
    role: 'Rehash & Win-Back Agent',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Works with customers who previously declined to identify new opportunities and win back their business.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Identifies rehash opportunities',
      'Re-presents value proposition',
      'Addresses previous objections',
      'Offers updated solutions',
      'Negotiates win-back deals',
      'Tracks rehash success'
    ],
    primaryResponsibilities: [
      'Review declined opportunities',
      'Re-engage past prospects',
      'Address previous concerns',
      'Present updated offers',
      'Close win-back deals'
    ],
    recommendedTasks: [
      'Rehash identification',
      'Re-engagement calls',
      'Objection handling',
      'Offer presentation',
      'Deal closing'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true,
      canCreateEstimates: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 7,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.2s',
    metrics: { dailyContacts: 35, weeklyAppointments: 10, closeRate: '18%' }
  },
  {
    id: 'sales-031',
    name: 'Opportunity Scoring Agent',
    role: 'Opportunity Scoring & Prioritization',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Scores and prioritizes opportunities based on likelihood to close, deal value, and strategic fit.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Scores opportunities',
      'Applies scoring models',
      'Prioritizes by potential',
      'Identifies high-value deals',
      'Updates priority rankings',
      'Reports scoring metrics'
    ],
    primaryResponsibilities: [
      'Score all opportunities',
      'Apply consistent criteria',
      'Prioritize pipeline',
      'Flag high-potential deals',
      'Report on scoring accuracy'
    ],
    recommendedTasks: [
      'Opportunity scoring',
      'Model application',
      'Priority ranking',
      'High-value flagging',
      'Accuracy reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canUpdateOpportunities: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 48,
    tasksCompleted7Days: 336,
    avgResponseTime: '0.5s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-032',
    name: 'Forecasting Agent',
    role: 'Sales Forecasting & Revenue Projection',
    department: 'Sales',
    category: 'Pipeline & Sales Ops',
    description: 'Generates sales forecasts and revenue projections based on pipeline data and historical trends.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Generates sales forecasts',
      'Analyzes historical trends',
      'Projects revenue',
      'Models scenarios',
      'Identifies risks',
      'Reports forecast accuracy'
    ],
    primaryResponsibilities: [
      'Create accurate forecasts',
      'Analyze sales trends',
      'Project revenue outcomes',
      'Model multiple scenarios',
      'Track forecast accuracy'
    ],
    recommendedTasks: [
      'Forecast generation',
      'Trend analysis',
      'Revenue projection',
      'Scenario modeling',
      'Accuracy tracking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canViewPipeline: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },

  // --- Marketing-Sales Integration ---
  {
    id: 'sales-033',
    name: 'Marketing Lead Sync Agent',
    role: 'Marketing Lead Sync Agent',
    department: 'Sales',
    category: 'Marketing-Sales Integration',
    description: 'Syncs marketing-generated leads into the sales pipeline and ensures proper attribution and handoff.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Syncs marketing leads',
      'Validates lead data',
      'Applies attribution',
      'Routes to sales',
      'Tracks handoff metrics',
      'Reports sync status'
    ],
    primaryResponsibilities: [
      'Sync all marketing leads',
      'Validate lead quality',
      'Apply proper attribution',
      'Execute timely handoffs',
      'Report on marketing-sales alignment'
    ],
    recommendedTasks: [
      'Lead syncing',
      'Data validation',
      'Attribution tagging',
      'Sales handoff',
      'Sync reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Marketing Hub'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canViewMarketing: true
    },
    limits: { maxTasksPerHour: 80, maxConcurrentTasks: 12 },
    tasksInQueue: 10,
    tasksToday: 65,
    tasksCompleted7Days: 458,
    avgResponseTime: '0.3s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-034',
    name: 'Ads Lead Verification Agent',
    role: 'Ads Lead Verification Agent',
    department: 'Sales',
    category: 'Marketing-Sales Integration',
    description: 'Verifies and validates leads generated from paid advertising campaigns before routing to sales.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Verifies ad-generated leads',
      'Validates contact info',
      'Filters spam/bots',
      'Confirms lead intent',
      'Tags lead source',
      'Reports verification metrics'
    ],
    primaryResponsibilities: [
      'Verify all ad leads',
      'Filter invalid entries',
      'Confirm contact accuracy',
      'Tag campaign source',
      'Report lead quality'
    ],
    recommendedTasks: [
      'Lead verification',
      'Spam filtering',
      'Contact validation',
      'Source tagging',
      'Quality reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canDeleteRecords: true
    },
    limits: { maxTasksPerHour: 90, maxConcurrentTasks: 15 },
    tasksInQueue: 14,
    tasksToday: 78,
    tasksCompleted7Days: 548,
    avgResponseTime: '0.2s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-035',
    name: 'Attribution Agent',
    role: 'Attribution Tracking & Reporting',
    department: 'Sales',
    category: 'Marketing-Sales Integration',
    description: 'Tracks marketing attribution, measures campaign ROI, and reports on lead source performance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks attribution data',
      'Measures campaign ROI',
      'Reports source performance',
      'Analyzes channel effectiveness',
      'Creates attribution reports',
      'Identifies top performers'
    ],
    primaryResponsibilities: [
      'Track all lead attribution',
      'Measure marketing ROI',
      'Report on source performance',
      'Analyze channel mix',
      'Identify high-performing sources'
    ],
    recommendedTasks: [
      'Attribution tracking',
      'ROI calculation',
      'Source reporting',
      'Channel analysis',
      'Performance identification'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Reporting / Analytics', 'Marketing Hub'],
    permissions: {
      canReadLeads: true,
      canViewMarketing: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.5s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },

  // --- Customer Experience ---
  {
    id: 'sales-036',
    name: 'Welcome Agent',
    role: 'Customer Welcome & Onboarding',
    department: 'Sales',
    category: 'Customer Experience',
    description: 'Welcomes new customers after sale, provides onboarding information, and ensures smooth transition.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Sends welcome communications',
      'Provides onboarding info',
      'Answers initial questions',
      'Sets expectations',
      'Introduces next steps',
      'Tracks onboarding completion'
    ],
    primaryResponsibilities: [
      'Welcome new customers',
      'Deliver onboarding materials',
      'Answer initial questions',
      'Set clear expectations',
      'Ensure smooth handoff'
    ],
    recommendedTasks: [
      'Welcome outreach',
      'Onboarding delivery',
      'Question handling',
      'Expectation setting',
      'Handoff coordination'
    ],
    modulesAssigned: ['Sales Hub', 'CRM'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canSendEmails: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.0s',
    metrics: { dailyContacts: 30, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-037',
    name: 'Ops Handoff Agent',
    role: 'Post-Sale Handoff to Operations',
    department: 'Sales',
    category: 'Customer Experience',
    description: 'Manages the handoff of closed deals to the operations team for project execution.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Coordinates sales-to-ops handoff',
      'Transfers deal information',
      'Schedules kickoff meetings',
      'Ensures documentation complete',
      'Tracks handoff status',
      'Resolves handoff issues'
    ],
    primaryResponsibilities: [
      'Execute smooth handoffs',
      'Transfer complete information',
      'Schedule kickoff activities',
      'Verify documentation',
      'Track handoff completion'
    ],
    recommendedTasks: [
      'Handoff coordination',
      'Info transfer',
      'Kickoff scheduling',
      'Doc verification',
      'Status tracking'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Operations Hub'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canAccessOps: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 16,
    tasksCompleted7Days: 112,
    avgResponseTime: '1.2s',
    metrics: { dailyContacts: 20, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-038',
    name: 'Review Capture Agent',
    role: 'Customer Review & Testimonial Capture',
    department: 'Sales',
    category: 'Customer Experience',
    description: 'Captures customer reviews, testimonials, and referrals from satisfied customers after project completion.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Requests customer reviews',
      'Captures testimonials',
      'Asks for referrals',
      'Tracks review completion',
      'Follows up on requests',
      'Reports review metrics'
    ],
    primaryResponsibilities: [
      'Request reviews from customers',
      'Capture testimonials',
      'Generate referral requests',
      'Track submission rates',
      'Report on review metrics'
    ],
    recommendedTasks: [
      'Review requests',
      'Testimonial capture',
      'Referral generation',
      'Follow-up outreach',
      'Metric reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canSendEmails: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.9s',
    metrics: { dailyContacts: 40, weeklyAppointments: 0, closeRate: 'N/A' }
  },

  // --- Admin & Support ---
  {
    id: 'sales-039',
    name: 'CRM Admin Agent',
    role: 'Sales CRM Administrator',
    department: 'Sales',
    category: 'Admin & Support',
    description: 'Administers the sales CRM system, manages configurations, and supports user issues.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Administers CRM settings',
      'Manages user permissions',
      'Creates custom fields',
      'Builds reports and dashboards',
      'Troubleshoots issues',
      'Trains users on CRM'
    ],
    primaryResponsibilities: [
      'Administer CRM system',
      'Manage configurations',
      'Support user requests',
      'Build custom reports',
      'Train team members'
    ],
    recommendedTasks: [
      'System administration',
      'Config management',
      'User support',
      'Report building',
      'Training delivery'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads', 'Reporting / Analytics'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true,
      canAdministerCRM: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 82,
    avgResponseTime: '1.8s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-040',
    name: 'Doc Filing Agent',
    role: 'Documentation & Compliance Filing',
    department: 'Sales',
    category: 'Admin & Support',
    description: 'Manages document filing, organizes sales records, and ensures compliance documentation is complete.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Files sales documents',
      'Organizes records',
      'Ensures compliance docs',
      'Maintains file structure',
      'Archives old records',
      'Retrieves documents'
    ],
    primaryResponsibilities: [
      'File all sales documents',
      'Organize record structure',
      'Ensure compliance filing',
      'Maintain archives',
      'Support document retrieval'
    ],
    recommendedTasks: [
      'Document filing',
      'Record organization',
      'Compliance verification',
      'Archive maintenance',
      'Retrieval support'
    ],
    modulesAssigned: ['Sales Hub', 'CRM'],
    permissions: {
      canReadLeads: true,
      canManageDocuments: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 42,
    tasksCompleted7Days: 294,
    avgResponseTime: '0.6s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-041',
    name: 'Data Entry Agent',
    role: 'Data Entry & Record Updating',
    department: 'Sales',
    category: 'Admin & Support',
    description: 'Handles data entry tasks, updates customer records, and maintains accurate information in the CRM.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Enters customer data',
      'Updates CRM records',
      'Corrects data errors',
      'Standardizes information',
      'Processes bulk updates',
      'Validates data accuracy'
    ],
    primaryResponsibilities: [
      'Enter new customer data',
      'Update existing records',
      'Correct data errors',
      'Maintain data standards',
      'Process bulk updates'
    ],
    recommendedTasks: [
      'Data entry',
      'Record updates',
      'Error correction',
      'Standardization',
      'Bulk processing'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Leads'],
    permissions: {
      canReadLeads: true,
      canWriteLeads: true
    },
    limits: { maxTasksPerHour: 80, maxConcurrentTasks: 12 },
    tasksInQueue: 18,
    tasksToday: 72,
    tasksCompleted7Days: 504,
    avgResponseTime: '0.4s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },
  {
    id: 'sales-042',
    name: 'Proofreading Agent',
    role: 'Proposal Proofreading & Error Check',
    department: 'Sales',
    category: 'Admin & Support',
    description: 'Reviews and proofreads proposals, estimates, and contracts for errors before sending to customers.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Reviews proposals for errors',
      'Checks estimate accuracy',
      'Verifies contract terms',
      'Corrects formatting issues',
      'Ensures brand consistency',
      'Reports error patterns'
    ],
    primaryResponsibilities: [
      'Proofread all proposals',
      'Verify estimate accuracy',
      'Check contract details',
      'Ensure formatting quality',
      'Report common errors'
    ],
    recommendedTasks: [
      'Proposal review',
      'Estimate verification',
      'Contract checking',
      'Format correction',
      'Error reporting'
    ],
    modulesAssigned: ['Sales Hub', 'CRM', 'Estimates'],
    permissions: {
      canReadLeads: true,
      canViewEstimates: true,
      canEditDocuments: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.5s',
    metrics: { dailyContacts: 0, weeklyAppointments: 0, closeRate: 'N/A' }
  },

  // ============ CALL CENTER DEPARTMENT ============
  // --- Leadership ---
  {
    id: 'cc-001',
    name: 'VP Call Center Ops',
    role: 'VP of Call Center Operations',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Executive leader overseeing all call center operations including inbound, outbound, customer success, quality assurance, and workforce management.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Oversees all call center divisions',
      'Sets service level standards',
      'Develops call center strategy',
      'Manages department budget',
      'Reviews performance metrics',
      'Coordinates cross-division initiatives'
    ],
    primaryResponsibilities: [
      'Lead call center operations',
      'Set KPI targets and standards',
      'Manage division managers',
      'Ensure customer satisfaction goals',
      'Drive operational excellence'
    ],
    recommendedTasks: [
      'Strategic planning',
      'Performance reviews',
      'Budget management',
      'KPI analysis',
      'Division coordination'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts', 'Scheduling', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canScheduleAppointments: true,
      canReadAnalytics: true,
      canManageStaff: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 15,
    tasksCompleted7Days: 98,
    avgResponseTime: '2.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '15%', resolutionRate: '94%' },
    teamMembers: Array.from({length: 59}, (_, i) => `cc-${String(i + 2).padStart(3, '0')}`)
  },
  {
    id: 'cc-002',
    name: 'Inbound Division Manager',
    role: 'Call Center Manager – Inbound',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Manages the inbound call center team handling all incoming customer calls, inquiries, and service requests.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages inbound call team',
      'Monitors call queues',
      'Optimizes answer rates',
      'Coaches inbound agents',
      'Analyzes inbound metrics',
      'Handles escalations'
    ],
    primaryResponsibilities: [
      'Lead inbound call operations',
      'Maintain service levels',
      'Coach and develop agents',
      'Handle complex escalations',
      'Report on inbound KPIs'
    ],
    recommendedTasks: [
      'Queue monitoring',
      'Agent coaching',
      'Escalation handling',
      'Performance tracking',
      'Service optimization'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.2s',
    metrics: { callsToday: 0, avgHandleTime: '4:30', avgWaitReduction: '20%', resolutionRate: '92%' }
  },
  {
    id: 'cc-003',
    name: 'Outbound Division Manager',
    role: 'Call Center Manager – Outbound',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Manages the outbound call center team executing proactive customer outreach, campaigns, and follow-up calls.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages outbound call team',
      'Coordinates calling campaigns',
      'Tracks dial metrics',
      'Coaches outbound agents',
      'Optimizes contact rates',
      'Manages call lists'
    ],
    primaryResponsibilities: [
      'Lead outbound call operations',
      'Execute calling campaigns',
      'Coach and develop agents',
      'Optimize dial strategies',
      'Report on outbound KPIs'
    ],
    recommendedTasks: [
      'Campaign management',
      'Agent coaching',
      'List optimization',
      'Performance tracking',
      'Dial strategy'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 28,
    tasksCompleted7Days: 186,
    avgResponseTime: '1.0s',
    metrics: { callsToday: 0, avgHandleTime: '3:15', avgWaitReduction: 'N/A', resolutionRate: '88%' }
  },
  {
    id: 'cc-004',
    name: 'Customer Success Manager',
    role: 'Call Center Manager – Customer Success',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Manages customer success operations including retention, satisfaction, and lifecycle management calls.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages customer success team',
      'Drives retention strategies',
      'Monitors satisfaction scores',
      'Coaches success agents',
      'Handles escalated complaints',
      'Analyzes customer feedback'
    ],
    primaryResponsibilities: [
      'Lead customer success operations',
      'Improve retention rates',
      'Manage complaint resolution',
      'Drive satisfaction improvements',
      'Report on CSAT metrics'
    ],
    recommendedTasks: [
      'Retention strategy',
      'Complaint review',
      'CSAT analysis',
      'Agent coaching',
      'Process improvement'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.5s',
    metrics: { callsToday: 0, avgHandleTime: '6:00', avgWaitReduction: '10%', resolutionRate: '96%' }
  },
  {
    id: 'cc-005',
    name: 'WFM Agent',
    role: 'Workforce Management & Staffing',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Manages workforce planning, scheduling, and staffing optimization to ensure adequate coverage at all times.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Forecasts call volumes',
      'Creates agent schedules',
      'Optimizes staffing levels',
      'Monitors real-time adherence',
      'Manages shift coverage',
      'Tracks attendance'
    ],
    primaryResponsibilities: [
      'Forecast staffing needs',
      'Create optimal schedules',
      'Monitor real-time adherence',
      'Manage shift swaps',
      'Report on workforce metrics'
    ],
    recommendedTasks: [
      'Schedule creation',
      'Volume forecasting',
      'Adherence monitoring',
      'Coverage optimization',
      'Attendance tracking'
    ],
    modulesAssigned: ['Call Center', 'Scheduling', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canScheduleAppointments: true,
      canReadAnalytics: true,
      canManageStaff: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '2.5s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '25%', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-006',
    name: 'Call Routing Admin',
    role: 'Call Routing & Queue Logic Admin',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Administers call routing rules, queue configurations, and IVR logic to optimize call distribution.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Configures call routing',
      'Manages queue logic',
      'Optimizes IVR flows',
      'Sets skill-based routing',
      'Monitors queue health',
      'Adjusts routing rules'
    ],
    primaryResponsibilities: [
      'Configure routing rules',
      'Optimize queue distribution',
      'Manage IVR configuration',
      'Monitor routing efficiency',
      'Adjust based on patterns'
    ],
    recommendedTasks: [
      'Routing configuration',
      'Queue optimization',
      'IVR updates',
      'Efficiency analysis',
      'Rule adjustments'
    ],
    modulesAssigned: ['Call Center', 'Automations'],
    permissions: {
      canReadCallLogs: true,
      canConfigureRouting: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 4 },
    tasksInQueue: 2,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '3.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '30%', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-007',
    name: 'Compliance Agent',
    role: 'Compliance, Legal & Call Recording',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Ensures all call center operations comply with legal requirements, TCPA regulations, and recording policies.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors compliance adherence',
      'Reviews call recordings',
      'Ensures TCPA compliance',
      'Manages consent records',
      'Handles compliance audits',
      'Updates compliance policies'
    ],
    primaryResponsibilities: [
      'Ensure regulatory compliance',
      'Audit call recordings',
      'Manage consent documentation',
      'Handle compliance issues',
      'Update policies and procedures'
    ],
    recommendedTasks: [
      'Compliance audits',
      'Recording reviews',
      'Policy updates',
      'Consent management',
      'Issue resolution'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canAuditRecordings: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 10,
    tasksCompleted7Days: 68,
    avgResponseTime: '2.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '100%' }
  },
  {
    id: 'cc-008',
    name: 'Training Agent',
    role: 'Call Center Training & Development',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Develops and delivers training programs to improve agent skills, product knowledge, and customer handling.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates training programs',
      'Delivers agent training',
      'Assesses skill gaps',
      'Develops training materials',
      'Tracks training completion',
      'Coaches underperformers'
    ],
    primaryResponsibilities: [
      'Develop training curriculum',
      'Deliver training sessions',
      'Assess agent competencies',
      'Track skill development',
      'Support onboarding'
    ],
    recommendedTasks: [
      'Training delivery',
      'Material creation',
      'Skill assessment',
      'Onboarding support',
      'Progress tracking'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canViewPerformance: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 4 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 54,
    avgResponseTime: '2.5s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-009',
    name: 'Knowledge Base Agent',
    role: 'Knowledge Base & SOP Maintenance',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Maintains the knowledge base, SOPs, and reference materials used by call center agents.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Maintains knowledge base',
      'Updates SOPs',
      'Creates reference guides',
      'Organizes documentation',
      'Reviews content accuracy',
      'Tracks article usage'
    ],
    primaryResponsibilities: [
      'Maintain knowledge base',
      'Update procedures',
      'Create help articles',
      'Ensure content accuracy',
      'Track knowledge gaps'
    ],
    recommendedTasks: [
      'Content updates',
      'SOP maintenance',
      'Article creation',
      'Accuracy reviews',
      'Gap analysis'
    ],
    modulesAssigned: ['Call Center'],
    permissions: {
      canReadCallLogs: true,
      canEditKnowledgeBase: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 82,
    avgResponseTime: '2.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-010',
    name: 'Escalation Oversight Agent',
    role: 'Call Escalation Oversight',
    department: 'Call Center',
    category: 'Leadership',
    description: 'Oversees all escalated calls, ensures proper resolution, and identifies systemic issues requiring attention.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Reviews escalated calls',
      'Ensures proper resolution',
      'Identifies patterns',
      'Coordinates with teams',
      'Tracks escalation metrics',
      'Recommends improvements'
    ],
    primaryResponsibilities: [
      'Oversee escalation process',
      'Ensure timely resolution',
      'Identify root causes',
      'Recommend process improvements',
      'Report escalation trends'
    ],
    recommendedTasks: [
      'Escalation review',
      'Resolution tracking',
      'Pattern analysis',
      'Process improvement',
      'Trend reporting'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 16,
    tasksCompleted7Days: 112,
    avgResponseTime: '1.5s',
    metrics: { callsToday: 8, avgHandleTime: '8:00', avgWaitReduction: 'N/A', resolutionRate: '98%' }
  },

  // --- Inbound Team ---
  {
    id: 'cc-011',
    name: 'Inbound Agent – Tier 1',
    role: 'Inbound Call Reception (Tier 1)',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Front-line agent handling incoming customer calls, basic inquiries, and initial issue triage.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Answers inbound calls',
      'Handles basic inquiries',
      'Triages customer issues',
      'Creates service tickets',
      'Routes complex calls',
      'Updates customer records'
    ],
    primaryResponsibilities: [
      'Answer incoming calls promptly',
      'Handle basic customer inquiries',
      'Create accurate tickets',
      'Route calls appropriately',
      'Maintain call quality'
    ],
    recommendedTasks: [
      'Call answering',
      'Basic inquiries',
      'Ticket creation',
      'Call routing',
      'Record updates'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 8 },
    tasksInQueue: 12,
    tasksToday: 85,
    tasksCompleted7Days: 598,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 45, avgHandleTime: '3:30', avgWaitReduction: '0%', resolutionRate: '78%' }
  },
  {
    id: 'cc-012',
    name: 'Inbound Agent – Tier 2',
    role: 'Inbound Call Reception (Tier 2)',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Advanced agent handling complex inquiries, technical issues, and escalated customer calls.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles complex inquiries',
      'Resolves technical issues',
      'Manages escalated calls',
      'Provides detailed solutions',
      'Coordinates with specialists',
      'Mentors Tier 1 agents'
    ],
    primaryResponsibilities: [
      'Handle complex customer issues',
      'Resolve escalated calls',
      'Provide expert solutions',
      'Support Tier 1 agents',
      'Document resolutions'
    ],
    recommendedTasks: [
      'Complex inquiries',
      'Escalation handling',
      'Technical support',
      'Agent mentoring',
      'Resolution documentation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 6 },
    tasksInQueue: 8,
    tasksToday: 52,
    tasksCompleted7Days: 364,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 28, avgHandleTime: '5:45', avgWaitReduction: '0%', resolutionRate: '92%' }
  },
  {
    id: 'cc-013',
    name: 'Lead Intake Agent',
    role: 'New Lead Intake Agent',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Captures new lead information from inbound calls, qualifies interest, and enters data into CRM.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Captures lead information',
      'Qualifies lead interest',
      'Enters CRM data',
      'Routes to sales',
      'Schedules callbacks',
      'Tracks lead sources'
    ],
    primaryResponsibilities: [
      'Capture complete lead info',
      'Qualify lead potential',
      'Enter accurate CRM data',
      'Route leads to sales',
      'Track conversion metrics'
    ],
    recommendedTasks: [
      'Lead capture',
      'Qualification calls',
      'CRM entry',
      'Sales routing',
      'Source tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canCreateLeads: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 8 },
    tasksInQueue: 10,
    tasksToday: 68,
    tasksCompleted7Days: 476,
    avgResponseTime: '0.4s',
    metrics: { callsToday: 38, avgHandleTime: '4:00', avgWaitReduction: '0%', resolutionRate: '95%' }
  },
  {
    id: 'cc-014',
    name: 'Missed Call Agent',
    role: 'Missed Call Response Agent',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Responds to missed calls within 15 minutes to re-engage potential customers before they contact competitors.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors missed calls',
      'Executes rapid callbacks',
      'Re-engages prospects',
      'Captures lead info',
      'Tracks response times',
      'Reports callback metrics'
    ],
    primaryResponsibilities: [
      'Monitor missed call queue',
      'Execute 15-minute callbacks',
      'Re-engage missed callers',
      'Capture lead information',
      'Maintain response SLA'
    ],
    recommendedTasks: [
      'Missed call monitoring',
      'Rapid callbacks',
      'Lead re-engagement',
      'Info capture',
      'SLA tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canCreateLeads: true
    },
    limits: { maxTasksPerHour: 55, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 42,
    tasksCompleted7Days: 294,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 32, avgHandleTime: '3:00', avgWaitReduction: '90%', resolutionRate: '85%' }
  },
  {
    id: 'cc-015',
    name: 'Customer Info Agent',
    role: 'Customer Question & Information',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Answers customer questions about services, pricing, processes, and provides detailed information.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Answers service questions',
      'Provides pricing info',
      'Explains processes',
      'Uses knowledge base',
      'Documents inquiries',
      'Routes complex questions'
    ],
    primaryResponsibilities: [
      'Answer customer questions',
      'Provide accurate information',
      'Explain services clearly',
      'Use knowledge resources',
      'Document all inquiries'
    ],
    recommendedTasks: [
      'Question answering',
      'Info provision',
      'Process explanation',
      'Documentation',
      'Question routing'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 55,
    tasksCompleted7Days: 386,
    avgResponseTime: '0.4s',
    metrics: { callsToday: 35, avgHandleTime: '3:45', avgWaitReduction: '0%', resolutionRate: '90%' }
  },
  {
    id: 'cc-016',
    name: 'Warranty Intake Agent',
    role: 'Repair & Warranty Inquiry Intake',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Handles warranty and repair inquiries, validates coverage, and creates service tickets.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles warranty inquiries',
      'Validates coverage',
      'Creates repair tickets',
      'Schedules service calls',
      'Documents issues',
      'Routes to operations'
    ],
    primaryResponsibilities: [
      'Handle warranty calls',
      'Validate coverage status',
      'Create service tickets',
      'Schedule repairs',
      'Coordinate with operations'
    ],
    recommendedTasks: [
      'Warranty validation',
      'Ticket creation',
      'Service scheduling',
      'Issue documentation',
      'Ops coordination'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts', 'Scheduling'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canScheduleAppointments: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.6s',
    metrics: { callsToday: 18, avgHandleTime: '5:30', avgWaitReduction: '0%', resolutionRate: '94%' }
  },
  {
    id: 'cc-017',
    name: 'Emergency Call Agent',
    role: 'Emergency / High Priority Handler',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Handles urgent and emergency calls requiring immediate attention and expedited resolution.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles emergency calls',
      'Prioritizes urgent issues',
      'Escalates immediately',
      'Coordinates rapid response',
      'Documents emergencies',
      'Follows emergency protocols'
    ],
    primaryResponsibilities: [
      'Handle emergency situations',
      'Prioritize urgent calls',
      'Execute rapid escalations',
      'Coordinate emergency response',
      'Document all incidents'
    ],
    recommendedTasks: [
      'Emergency handling',
      'Priority assessment',
      'Rapid escalation',
      'Response coordination',
      'Incident documentation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canEscalate: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 5 },
    tasksInQueue: 2,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '0.1s',
    metrics: { callsToday: 8, avgHandleTime: '6:00', avgWaitReduction: '95%', resolutionRate: '100%' }
  },
  {
    id: 'cc-018',
    name: 'Callback Queue Agent',
    role: 'Callback Queue Manager',
    department: 'Call Center',
    category: 'Inbound Team',
    description: 'Manages the callback queue, prioritizes pending callbacks, and ensures timely customer contact.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages callback queue',
      'Prioritizes callbacks',
      'Assigns to agents',
      'Tracks completion',
      'Monitors SLA compliance',
      'Reports queue metrics'
    ],
    primaryResponsibilities: [
      'Manage callback queue',
      'Prioritize by urgency',
      'Assign to available agents',
      'Track callback completion',
      'Maintain SLA compliance'
    ],
    recommendedTasks: [
      'Queue management',
      'Priority assignment',
      'Agent allocation',
      'Completion tracking',
      'SLA monitoring'
    ],
    modulesAssigned: ['Call Center', 'CRM'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canManageQueue: true
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 10 },
    tasksInQueue: 15,
    tasksToday: 65,
    tasksCompleted7Days: 456,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '40%', resolutionRate: 'N/A' }
  },

  // --- Outbound Team ---
  {
    id: 'cc-019',
    name: 'Outbound Dialer – Primary',
    role: 'Outbound Dialing Agent (Primary)',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Primary outbound caller executing proactive customer outreach and lead contact campaigns.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Executes outbound calls',
      'Works call lists',
      'Qualifies prospects',
      'Sets appointments',
      'Updates call outcomes',
      'Meets dial targets'
    ],
    primaryResponsibilities: [
      'Execute outbound campaigns',
      'Work assigned call lists',
      'Qualify customer interest',
      'Set appointments',
      'Document all outcomes'
    ],
    recommendedTasks: [
      'Outbound dialing',
      'List execution',
      'Lead qualification',
      'Appointment setting',
      'Outcome documentation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads', 'Scheduling'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canScheduleAppointments: true
    },
    limits: { maxTasksPerHour: 70, maxConcurrentTasks: 8 },
    tasksInQueue: 20,
    tasksToday: 95,
    tasksCompleted7Days: 668,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 85, avgHandleTime: '2:30', avgWaitReduction: 'N/A', resolutionRate: '35%' }
  },
  {
    id: 'cc-020',
    name: 'Outbound Dialer – Follow-Up',
    role: 'Outbound Dialing Agent (Follow-Up)',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Executes follow-up calls to leads and customers who require additional contact.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Executes follow-up calls',
      'Re-contacts prospects',
      'Advances opportunities',
      'Updates lead status',
      'Schedules next actions',
      'Tracks follow-up completion'
    ],
    primaryResponsibilities: [
      'Execute follow-up calls',
      'Re-engage prior contacts',
      'Advance sales opportunities',
      'Update lead statuses',
      'Schedule future follow-ups'
    ],
    recommendedTasks: [
      'Follow-up calls',
      'Re-engagement',
      'Status updates',
      'Next action scheduling',
      'Completion tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 8 },
    tasksInQueue: 18,
    tasksToday: 78,
    tasksCompleted7Days: 548,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 65, avgHandleTime: '3:00', avgWaitReduction: 'N/A', resolutionRate: '42%' }
  },
  {
    id: 'cc-021',
    name: 'Cold Outreach Agent',
    role: 'Cold Outreach Agent',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Contacts cold leads and new prospects who have not previously engaged with the company.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Contacts cold leads',
      'Introduces company services',
      'Qualifies initial interest',
      'Handles objections',
      'Generates warm leads',
      'Tracks cold call metrics'
    ],
    primaryResponsibilities: [
      'Execute cold outreach',
      'Introduce services',
      'Qualify interest level',
      'Handle initial objections',
      'Convert to warm leads'
    ],
    recommendedTasks: [
      'Cold calling',
      'Service introduction',
      'Interest qualification',
      'Objection handling',
      'Lead conversion'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canCreateLeads: true
    },
    limits: { maxTasksPerHour: 80, maxConcurrentTasks: 8 },
    tasksInQueue: 25,
    tasksToday: 110,
    tasksCompleted7Days: 772,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 100, avgHandleTime: '2:00', avgWaitReduction: 'N/A', resolutionRate: '22%' }
  },
  {
    id: 'cc-022',
    name: 'Rehash Calling Agent',
    role: 'Rehash / Win-Back Calling',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Re-contacts customers who previously declined to win back their business with new offers.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Contacts declined customers',
      'Presents new offers',
      'Addresses previous objections',
      'Re-qualifies interest',
      'Wins back business',
      'Tracks rehash success'
    ],
    primaryResponsibilities: [
      'Contact declined leads',
      'Present updated offers',
      'Address prior objections',
      'Win back customers',
      'Track success rates'
    ],
    recommendedTasks: [
      'Rehash calling',
      'Offer presentation',
      'Objection handling',
      'Win-back closes',
      'Success tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 6 },
    tasksInQueue: 12,
    tasksToday: 48,
    tasksCompleted7Days: 336,
    avgResponseTime: '0.4s',
    metrics: { callsToday: 40, avgHandleTime: '4:00', avgWaitReduction: 'N/A', resolutionRate: '28%' }
  },
  {
    id: 'cc-023',
    name: 'Long-Term Nurture Agent',
    role: 'Long-Term Nurture Call Agent',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Maintains contact with long-term prospects through periodic check-in calls and relationship building.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Executes nurture calls',
      'Builds relationships',
      'Provides value updates',
      'Identifies timing signals',
      'Maintains engagement',
      'Tracks nurture progress'
    ],
    primaryResponsibilities: [
      'Execute periodic check-ins',
      'Build customer relationships',
      'Share valuable updates',
      'Identify buying signals',
      'Maintain long-term engagement'
    ],
    recommendedTasks: [
      'Nurture calls',
      'Relationship building',
      'Value sharing',
      'Signal identification',
      'Engagement tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 6 },
    tasksInQueue: 8,
    tasksToday: 32,
    tasksCompleted7Days: 224,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 25, avgHandleTime: '4:30', avgWaitReduction: 'N/A', resolutionRate: '18%' }
  },
  {
    id: 'cc-024',
    name: 'Aging Lead Agent',
    role: 'Aging Lead Re-Engagement',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Re-engages leads that have gone cold or inactive to revive sales opportunities.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Identifies aging leads',
      'Executes re-engagement calls',
      'Assesses current interest',
      'Updates lead status',
      'Revives opportunities',
      'Tracks revival rates'
    ],
    primaryResponsibilities: [
      'Work aging lead lists',
      'Execute re-engagement outreach',
      'Assess current needs',
      'Update lead information',
      'Revive dormant opportunities'
    ],
    recommendedTasks: [
      'Aging lead calls',
      'Re-engagement outreach',
      'Need assessment',
      'Status updates',
      'Opportunity revival'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 8 },
    tasksInQueue: 15,
    tasksToday: 58,
    tasksCompleted7Days: 406,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 50, avgHandleTime: '3:15', avgWaitReduction: 'N/A', resolutionRate: '24%' }
  },
  {
    id: 'cc-025',
    name: 'Campaign Calling Agent',
    role: 'Event / Campaign-Specific Calling',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Executes calls for specific marketing campaigns, events, and promotional initiatives.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Executes campaign calls',
      'Promotes events',
      'Delivers campaign messaging',
      'Tracks campaign responses',
      'Collects registrations',
      'Reports campaign results'
    ],
    primaryResponsibilities: [
      'Execute campaign-specific calls',
      'Deliver promotional messaging',
      'Generate event registrations',
      'Track campaign performance',
      'Report on results'
    ],
    recommendedTasks: [
      'Campaign execution',
      'Event promotion',
      'Message delivery',
      'Registration collection',
      'Result reporting'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Leads', 'Automations'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 55, maxConcurrentTasks: 8 },
    tasksInQueue: 10,
    tasksToday: 62,
    tasksCompleted7Days: 434,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 55, avgHandleTime: '2:45', avgWaitReduction: 'N/A', resolutionRate: '32%' }
  },
  {
    id: 'cc-026',
    name: 'Voicemail Drop Agent',
    role: 'Voicemail Drop Automation',
    department: 'Call Center',
    category: 'Outbound Team',
    description: 'Manages automated voicemail drops for efficient outbound messaging at scale.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages voicemail campaigns',
      'Schedules VM drops',
      'Personalizes messages',
      'Tracks delivery rates',
      'Monitors callbacks',
      'Optimizes messaging'
    ],
    primaryResponsibilities: [
      'Manage VM drop campaigns',
      'Schedule automated drops',
      'Track delivery metrics',
      'Monitor callback rates',
      'Optimize message content'
    ],
    recommendedTasks: [
      'Campaign management',
      'Drop scheduling',
      'Delivery tracking',
      'Callback monitoring',
      'Message optimization'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Automations'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canManageAutomations: true
    },
    limits: { maxTasksPerHour: 100, maxConcurrentTasks: 15 },
    tasksInQueue: 8,
    tasksToday: 125,
    tasksCompleted7Days: 876,
    avgResponseTime: '0.1s',
    metrics: { callsToday: 200, avgHandleTime: '0:30', avgWaitReduction: 'N/A', resolutionRate: '12%' }
  },

  // --- Appointment Setting ---
  {
    id: 'cc-027',
    name: 'Appointment Setter – Standard',
    role: 'Appointment Setter (Standard)',
    department: 'Call Center',
    category: 'Appointment Setting',
    description: 'Sets standard appointments for sales consultations and service visits.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Sets customer appointments',
      'Checks availability',
      'Sends confirmations',
      'Updates calendars',
      'Handles rescheduling',
      'Tracks appointment metrics'
    ],
    primaryResponsibilities: [
      'Book customer appointments',
      'Verify availability',
      'Send confirmations',
      'Process reschedules',
      'Track booking rates'
    ],
    recommendedTasks: [
      'Appointment booking',
      'Availability checking',
      'Confirmation sending',
      'Reschedule handling',
      'Metric tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Scheduling'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canScheduleAppointments: true
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 8 },
    tasksInQueue: 12,
    tasksToday: 58,
    tasksCompleted7Days: 406,
    avgResponseTime: '0.4s',
    metrics: { callsToday: 42, avgHandleTime: '3:30', avgWaitReduction: 'N/A', resolutionRate: '88%' }
  },
  {
    id: 'cc-028',
    name: 'Appointment Setter – Priority',
    role: 'Appointment Setter (High Priority)',
    department: 'Call Center',
    category: 'Appointment Setting',
    description: 'Handles high-priority appointment requests requiring expedited scheduling.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles urgent appointments',
      'Finds expedited slots',
      'Coordinates with teams',
      'Prioritizes VIP customers',
      'Manages same-day requests',
      'Tracks priority metrics'
    ],
    primaryResponsibilities: [
      'Book urgent appointments',
      'Find expedited availability',
      'Coordinate rapid scheduling',
      'Handle VIP requests',
      'Maintain priority SLA'
    ],
    recommendedTasks: [
      'Priority booking',
      'Expedited scheduling',
      'VIP handling',
      'Same-day requests',
      'SLA compliance'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Scheduling'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canScheduleAppointments: true,
      canOverrideSchedule: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 18, avgHandleTime: '4:00', avgWaitReduction: '80%', resolutionRate: '95%' }
  },
  {
    id: 'cc-029',
    name: 'Calendar Optimizer Agent',
    role: 'Advanced Calendar Optimization',
    department: 'Call Center',
    category: 'Appointment Setting',
    description: 'Optimizes calendar efficiency by managing gaps, travel time, and appointment clustering.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Optimizes calendar density',
      'Manages travel time',
      'Clusters appointments',
      'Reduces gaps',
      'Balances workloads',
      'Improves efficiency'
    ],
    primaryResponsibilities: [
      'Optimize calendar utilization',
      'Minimize travel time',
      'Cluster appointments geographically',
      'Reduce scheduling gaps',
      'Balance rep workloads'
    ],
    recommendedTasks: [
      'Calendar optimization',
      'Travel reduction',
      'Gap management',
      'Workload balancing',
      'Efficiency reporting'
    ],
    modulesAssigned: ['Call Center', 'Scheduling', 'Automations'],
    permissions: {
      canScheduleAppointments: true,
      canModifySchedules: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 5 },
    tasksInQueue: 6,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.8s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '35%', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-030',
    name: 'Double-Booking Agent',
    role: 'Double-Booking Prevention',
    department: 'Call Center',
    category: 'Appointment Setting',
    description: 'Monitors and prevents double-bookings across all calendars and resources.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors for conflicts',
      'Prevents double-bookings',
      'Resolves overlaps',
      'Validates availability',
      'Alerts on conflicts',
      'Tracks prevention metrics'
    ],
    primaryResponsibilities: [
      'Monitor all calendars',
      'Prevent scheduling conflicts',
      'Resolve existing overlaps',
      'Validate before booking',
      'Alert on potential issues'
    ],
    recommendedTasks: [
      'Conflict monitoring',
      'Prevention checks',
      'Overlap resolution',
      'Validation runs',
      'Alert handling'
    ],
    modulesAssigned: ['Call Center', 'Scheduling'],
    permissions: {
      canScheduleAppointments: true,
      canModifySchedules: true
    },
    limits: { maxTasksPerHour: 100, maxConcurrentTasks: 20 },
    tasksInQueue: 3,
    tasksToday: 85,
    tasksCompleted7Days: 598,
    avgResponseTime: '0.1s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '100%' }
  },
  {
    id: 'cc-031',
    name: 'Confirmation Agent',
    role: 'Scheduling Confirmation & Reminders',
    department: 'Call Center',
    category: 'Appointment Setting',
    description: 'Sends appointment confirmations and reminders to reduce no-shows.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Sends confirmations',
      'Schedules reminders',
      'Handles confirmations',
      'Processes cancellations',
      'Tracks response rates',
      'Reduces no-shows'
    ],
    primaryResponsibilities: [
      'Send appointment confirmations',
      'Execute reminder sequences',
      'Process customer responses',
      'Handle cancellations',
      'Reduce no-show rates'
    ],
    recommendedTasks: [
      'Confirmation sending',
      'Reminder scheduling',
      'Response processing',
      'Cancellation handling',
      'No-show reduction'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Scheduling', 'Automations'],
    permissions: {
      canReadCallLogs: true,
      canScheduleAppointments: true,
      canSendNotifications: true
    },
    limits: { maxTasksPerHour: 80, maxConcurrentTasks: 15 },
    tasksInQueue: 20,
    tasksToday: 125,
    tasksCompleted7Days: 876,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '92%' }
  },
  {
    id: 'cc-032',
    name: 'Round Robin Scheduler',
    role: 'Multi-Rep Routing & Round Robin',
    department: 'Call Center',
    category: 'Appointment Setting',
    description: 'Distributes appointments across sales reps using round-robin and skill-based routing.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Routes appointments',
      'Balances rep loads',
      'Applies routing rules',
      'Considers skills',
      'Tracks distribution',
      'Ensures fairness'
    ],
    primaryResponsibilities: [
      'Distribute appointments fairly',
      'Balance rep workloads',
      'Apply skill-based routing',
      'Track distribution metrics',
      'Ensure fair allocation'
    ],
    recommendedTasks: [
      'Appointment routing',
      'Load balancing',
      'Rule application',
      'Distribution tracking',
      'Fairness monitoring'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Scheduling', 'Automations'],
    permissions: {
      canScheduleAppointments: true,
      canModifySchedules: true,
      canManageRouting: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 12 },
    tasksInQueue: 10,
    tasksToday: 75,
    tasksCompleted7Days: 526,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '98%' }
  },

  // --- Customer Success ---
  {
    id: 'cc-033',
    name: 'Status Update Agent',
    role: 'Customer Status Update Agent',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Proactively contacts customers to provide status updates on their projects or requests.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Provides status updates',
      'Answers status questions',
      'Sets expectations',
      'Documents interactions',
      'Escalates delays',
      'Maintains satisfaction'
    ],
    primaryResponsibilities: [
      'Provide proactive updates',
      'Answer status inquiries',
      'Set accurate expectations',
      'Document all communications',
      'Escalate issues promptly'
    ],
    recommendedTasks: [
      'Status calls',
      'Update provision',
      'Question handling',
      'Expectation setting',
      'Issue escalation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 45,
    tasksCompleted7Days: 316,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 35, avgHandleTime: '3:45', avgWaitReduction: 'N/A', resolutionRate: '94%' }
  },
  {
    id: 'cc-034',
    name: 'Pre-Project Prep Agent',
    role: 'Pre-Project Customer Prep',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Contacts customers before project start to prepare them and ensure readiness.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Prepares customers',
      'Explains process',
      'Answers questions',
      'Confirms readiness',
      'Documents prep calls',
      'Coordinates with ops'
    ],
    primaryResponsibilities: [
      'Contact customers pre-project',
      'Explain what to expect',
      'Answer preparation questions',
      'Confirm customer readiness',
      'Coordinate with operations'
    ],
    recommendedTasks: [
      'Pre-project calls',
      'Process explanation',
      'Readiness confirmation',
      'Question handling',
      'Ops coordination'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 6,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.6s',
    metrics: { callsToday: 22, avgHandleTime: '5:00', avgWaitReduction: 'N/A', resolutionRate: '96%' }
  },
  {
    id: 'cc-035',
    name: 'Post-Project Agent',
    role: 'Post-Project Follow-Up',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Follows up with customers after project completion to ensure satisfaction.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Conducts follow-up calls',
      'Assesses satisfaction',
      'Addresses concerns',
      'Requests reviews',
      'Documents feedback',
      'Escalates issues'
    ],
    primaryResponsibilities: [
      'Execute post-project calls',
      'Assess customer satisfaction',
      'Address any concerns',
      'Request reviews/referrals',
      'Document all feedback'
    ],
    recommendedTasks: [
      'Follow-up calls',
      'Satisfaction assessment',
      'Concern resolution',
      'Review requests',
      'Feedback documentation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 6 },
    tasksInQueue: 7,
    tasksToday: 32,
    tasksCompleted7Days: 224,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 25, avgHandleTime: '4:30', avgWaitReduction: 'N/A', resolutionRate: '92%' }
  },
  {
    id: 'cc-036',
    name: 'Survey Agent',
    role: 'Survey & Feedback Collection',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Collects customer feedback through surveys and captures satisfaction data.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Conducts phone surveys',
      'Collects NPS scores',
      'Captures feedback',
      'Documents responses',
      'Identifies trends',
      'Reports survey results'
    ],
    primaryResponsibilities: [
      'Execute survey calls',
      'Collect NPS and CSAT scores',
      'Document detailed feedback',
      'Identify improvement areas',
      'Report on survey results'
    ],
    recommendedTasks: [
      'Survey execution',
      'Score collection',
      'Feedback capture',
      'Trend identification',
      'Result reporting'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 10,
    tasksToday: 48,
    tasksCompleted7Days: 336,
    avgResponseTime: '0.4s',
    metrics: { callsToday: 38, avgHandleTime: '3:00', avgWaitReduction: 'N/A', resolutionRate: '85%' }
  },
  {
    id: 'cc-037',
    name: 'Complaint Intake Agent',
    role: 'Complaint Intake Agent',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Receives and documents customer complaints, initiating the resolution process.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Receives complaints',
      'Documents issues',
      'Shows empathy',
      'Creates tickets',
      'Routes for resolution',
      'Sets expectations'
    ],
    primaryResponsibilities: [
      'Receive customer complaints',
      'Document issues thoroughly',
      'Show empathy and concern',
      'Create resolution tickets',
      'Set resolution expectations'
    ],
    recommendedTasks: [
      'Complaint reception',
      'Issue documentation',
      'Ticket creation',
      'Routing',
      'Expectation setting'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canCreateTickets: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 15, avgHandleTime: '6:00', avgWaitReduction: 'N/A', resolutionRate: '100%' }
  },
  {
    id: 'cc-038',
    name: 'Escalation Resolution Agent',
    role: 'Escalation Resolution Agent',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Handles escalated customer issues requiring senior attention and complex resolution.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles escalated issues',
      'Provides senior support',
      'Negotiates resolutions',
      'Coordinates with leadership',
      'Documents resolutions',
      'Prevents recurrence'
    ],
    primaryResponsibilities: [
      'Resolve escalated issues',
      'Provide expert support',
      'Negotiate fair resolutions',
      'Coordinate with leadership',
      'Prevent future escalations'
    ],
    recommendedTasks: [
      'Escalation handling',
      'Resolution negotiation',
      'Leadership coordination',
      'Documentation',
      'Prevention planning'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canApproveResolutions: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 3,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 10, avgHandleTime: '12:00', avgWaitReduction: 'N/A', resolutionRate: '96%' }
  },
  {
    id: 'cc-039',
    name: 'Retention Agent',
    role: 'Retention & Cancellation Prevention',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Handles cancellation requests and works to retain customers through problem resolution.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles cancellation calls',
      'Identifies concerns',
      'Offers solutions',
      'Negotiates retention',
      'Documents outcomes',
      'Tracks save rates'
    ],
    primaryResponsibilities: [
      'Handle cancellation requests',
      'Identify underlying concerns',
      'Offer retention solutions',
      'Save at-risk customers',
      'Track retention metrics'
    ],
    recommendedTasks: [
      'Cancellation handling',
      'Concern identification',
      'Solution offering',
      'Save attempts',
      'Metric tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canOfferDiscounts: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 16,
    tasksCompleted7Days: 112,
    avgResponseTime: '0.4s',
    metrics: { callsToday: 12, avgHandleTime: '8:00', avgWaitReduction: 'N/A', resolutionRate: '72%' }
  },
  {
    id: 'cc-040',
    name: 'CLV Nurture Agent',
    role: 'Customer Lifetime Value Nurture',
    department: 'Call Center',
    category: 'Customer Success',
    description: 'Nurtures high-value customers to maximize lifetime value through proactive engagement.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Nurtures VIP customers',
      'Identifies upsell opportunities',
      'Builds relationships',
      'Provides VIP service',
      'Tracks CLV metrics',
      'Generates referrals'
    ],
    primaryResponsibilities: [
      'Nurture high-value customers',
      'Identify expansion opportunities',
      'Build strong relationships',
      'Deliver exceptional service',
      'Maximize customer lifetime value'
    ],
    recommendedTasks: [
      'VIP engagement',
      'Upsell identification',
      'Relationship building',
      'Service excellence',
      'Referral generation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 4 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '0.6s',
    metrics: { callsToday: 15, avgHandleTime: '6:30', avgWaitReduction: 'N/A', resolutionRate: '98%' }
  },

  // --- Quality & Compliance ---
  {
    id: 'cc-041',
    name: 'Transcription Agent',
    role: 'Call Transcription Agent',
    department: 'Call Center',
    category: 'Quality & Compliance',
    description: 'Transcribes call recordings for quality review, training, and compliance purposes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Transcribes call recordings',
      'Identifies key moments',
      'Tags conversations',
      'Creates searchable records',
      'Supports QA reviews',
      'Enables compliance audits'
    ],
    primaryResponsibilities: [
      'Transcribe call recordings',
      'Tag important moments',
      'Create searchable archives',
      'Support quality reviews',
      'Enable compliance auditing'
    ],
    recommendedTasks: [
      'Call transcription',
      'Moment tagging',
      'Archive creation',
      'QA support',
      'Audit enablement'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canAccessRecordings: true,
      canCreateTranscripts: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 5 },
    tasksInQueue: 25,
    tasksToday: 45,
    tasksCompleted7Days: 316,
    avgResponseTime: '5.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-042',
    name: 'QA Scoring Agent',
    role: 'Call QA Scoring Agent',
    department: 'Call Center',
    category: 'Quality & Compliance',
    description: 'Evaluates and scores call quality against defined standards and criteria.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Scores call quality',
      'Applies QA criteria',
      'Identifies coaching needs',
      'Documents evaluations',
      'Tracks quality trends',
      'Reports QA metrics'
    ],
    primaryResponsibilities: [
      'Evaluate call quality',
      'Score against standards',
      'Identify improvement areas',
      'Document evaluations',
      'Report quality metrics'
    ],
    recommendedTasks: [
      'Call scoring',
      'Criteria application',
      'Coaching identification',
      'Evaluation documentation',
      'Trend reporting'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canAccessRecordings: true,
      canScoreCalls: true
    },
    limits: { maxTasksPerHour: 12, maxConcurrentTasks: 4 },
    tasksInQueue: 15,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '8.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-043',
    name: 'Performance Eval Agent',
    role: 'Agent Performance Evaluation',
    department: 'Call Center',
    category: 'Quality & Compliance',
    description: 'Evaluates individual agent performance across multiple metrics and dimensions.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Evaluates agent performance',
      'Analyzes multiple metrics',
      'Identifies top performers',
      'Flags underperformers',
      'Creates performance reports',
      'Supports coaching'
    ],
    primaryResponsibilities: [
      'Evaluate agent performance',
      'Analyze performance data',
      'Identify performance gaps',
      'Create evaluation reports',
      'Support improvement plans'
    ],
    recommendedTasks: [
      'Performance evaluation',
      'Metric analysis',
      'Gap identification',
      'Report creation',
      'Coaching support'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canViewPerformance: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 4 },
    tasksInQueue: 8,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '3.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-044',
    name: 'Recording Storage Agent',
    role: 'Call Recording Storage & Indexing',
    department: 'Call Center',
    category: 'Quality & Compliance',
    description: 'Manages call recording storage, indexing, and retrieval for compliance and training.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages recording storage',
      'Indexes call recordings',
      'Enables search and retrieval',
      'Manages retention policies',
      'Ensures secure storage',
      'Supports compliance'
    ],
    primaryResponsibilities: [
      'Store call recordings securely',
      'Index for easy retrieval',
      'Manage retention policies',
      'Enable compliance access',
      'Maintain storage health'
    ],
    recommendedTasks: [
      'Storage management',
      'Recording indexing',
      'Retrieval support',
      'Retention enforcement',
      'Compliance support'
    ],
    modulesAssigned: ['Call Center'],
    permissions: {
      canAccessRecordings: true,
      canManageStorage: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 85,
    tasksCompleted7Days: 598,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-045',
    name: 'TCPA Compliance Agent',
    role: 'Compliance Monitoring Agent',
    department: 'Call Center',
    category: 'Quality & Compliance',
    description: 'Monitors compliance with TCPA, FDCPA, and other regulatory requirements.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors regulatory compliance',
      'Checks consent records',
      'Validates DNC compliance',
      'Audits calling practices',
      'Flags violations',
      'Reports compliance status'
    ],
    primaryResponsibilities: [
      'Monitor TCPA compliance',
      'Validate consent records',
      'Enforce DNC requirements',
      'Audit calling practices',
      'Report compliance metrics'
    ],
    recommendedTasks: [
      'Compliance monitoring',
      'Consent validation',
      'DNC enforcement',
      'Practice auditing',
      'Status reporting'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canAuditCompliance: true,
      canFlagViolations: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 35,
    tasksCompleted7Days: 246,
    avgResponseTime: '1.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '100%' }
  },
  {
    id: 'cc-046',
    name: 'Sentiment Analysis Agent',
    role: 'Sentiment Analysis Review',
    department: 'Call Center',
    category: 'Quality & Compliance',
    description: 'Analyzes call sentiment to identify customer satisfaction levels and potential issues.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Analyzes call sentiment',
      'Identifies negative trends',
      'Flags at-risk customers',
      'Detects escalation signals',
      'Reports sentiment metrics',
      'Triggers interventions'
    ],
    primaryResponsibilities: [
      'Analyze customer sentiment',
      'Identify negative patterns',
      'Flag at-risk interactions',
      'Detect escalation triggers',
      'Report sentiment trends'
    ],
    recommendedTasks: [
      'Sentiment analysis',
      'Pattern identification',
      'Risk flagging',
      'Trigger detection',
      'Trend reporting'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canAccessRecordings: true,
      canAnalyzeSentiment: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 20,
    tasksToday: 65,
    tasksCompleted7Days: 456,
    avgResponseTime: '2.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },

  // --- Analytics & Systems ---
  {
    id: 'cc-047',
    name: 'KPI Dashboard Agent',
    role: 'Call Center Analytics & KPI Dashboard',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Generates and maintains call center KPI dashboards and performance analytics.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Creates KPI dashboards',
      'Generates performance reports',
      'Tracks real-time metrics',
      'Identifies trends',
      'Alerts on anomalies',
      'Supports data-driven decisions'
    ],
    primaryResponsibilities: [
      'Maintain KPI dashboards',
      'Generate analytics reports',
      'Track real-time performance',
      'Identify metric trends',
      'Alert on performance issues'
    ],
    recommendedTasks: [
      'Dashboard maintenance',
      'Report generation',
      'Real-time tracking',
      'Trend analysis',
      'Anomaly alerting'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canReadAnalytics: true,
      canCreateReports: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 6,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '1.5s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-048',
    name: 'Abandon Rate Agent',
    role: 'Abandon Rate Tracking & Optimization',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Monitors and works to reduce call abandonment rates through analysis and optimization.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks abandon rates',
      'Identifies causes',
      'Recommends improvements',
      'Monitors queue health',
      'Triggers staffing alerts',
      'Reports optimization results'
    ],
    primaryResponsibilities: [
      'Track abandonment metrics',
      'Identify root causes',
      'Recommend improvements',
      'Monitor wait times',
      'Alert on high abandonment'
    ],
    recommendedTasks: [
      'Rate tracking',
      'Cause analysis',
      'Improvement recommendations',
      'Queue monitoring',
      'Alert management'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '45%', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-049',
    name: 'AHT Optimization Agent',
    role: 'AHT (Average Handle Time) Optimization',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Analyzes and optimizes average handle time to improve efficiency without sacrificing quality.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Analyzes handle times',
      'Identifies inefficiencies',
      'Recommends process improvements',
      'Tracks AHT by call type',
      'Benchmarks performance',
      'Reports optimization gains'
    ],
    primaryResponsibilities: [
      'Analyze AHT patterns',
      'Identify efficiency opportunities',
      'Recommend process changes',
      'Track improvements',
      'Balance speed and quality'
    ],
    recommendedTasks: [
      'AHT analysis',
      'Efficiency identification',
      'Process recommendations',
      'Benchmark tracking',
      'Improvement reporting'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.5s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '20%', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-050',
    name: 'FCR Analysis Agent',
    role: 'First Contact Resolution Analysis',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Analyzes first contact resolution rates and identifies opportunities to resolve more issues on first call.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks FCR rates',
      'Identifies repeat contacts',
      'Analyzes resolution patterns',
      'Recommends improvements',
      'Benchmarks against goals',
      'Reports FCR metrics'
    ],
    primaryResponsibilities: [
      'Track FCR performance',
      'Identify repeat contact causes',
      'Analyze resolution patterns',
      'Recommend training needs',
      'Report improvement opportunities'
    ],
    recommendedTasks: [
      'FCR tracking',
      'Repeat analysis',
      'Pattern identification',
      'Training recommendations',
      'Metric reporting'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canReadAnalytics: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 16,
    tasksCompleted7Days: 112,
    avgResponseTime: '1.5s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '82%' }
  },
  {
    id: 'cc-051',
    name: 'Attribution Agent',
    role: 'Lead Attribution & Source Tracking',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Tracks call source attribution to measure marketing and campaign effectiveness.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Tracks call sources',
      'Attributes to campaigns',
      'Measures channel ROI',
      'Links calls to marketing',
      'Reports source performance',
      'Identifies top channels'
    ],
    primaryResponsibilities: [
      'Track all call sources',
      'Attribute to marketing campaigns',
      'Measure channel effectiveness',
      'Report ROI by source',
      'Identify highest performers'
    ],
    recommendedTasks: [
      'Source tracking',
      'Campaign attribution',
      'ROI measurement',
      'Performance reporting',
      'Channel analysis'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canReadAnalytics: true,
      canViewMarketing: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '1.2s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-052',
    name: 'SLA Enforcement Agent',
    role: 'SLA Enforcement Agent',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Monitors and enforces service level agreements across all call center operations.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors SLA compliance',
      'Tracks response times',
      'Alerts on SLA breaches',
      'Reports compliance rates',
      'Identifies at-risk SLAs',
      'Recommends improvements'
    ],
    primaryResponsibilities: [
      'Monitor SLA performance',
      'Track compliance metrics',
      'Alert on potential breaches',
      'Report SLA status',
      'Recommend corrective actions'
    ],
    recommendedTasks: [
      'SLA monitoring',
      'Compliance tracking',
      'Breach alerting',
      'Status reporting',
      'Action recommendations'
    ],
    modulesAssigned: ['Call Center', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canReadAnalytics: true,
      canSendAlerts: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 35,
    tasksCompleted7Days: 246,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '97%' }
  },
  {
    id: 'cc-053',
    name: 'Forecasting Agent',
    role: 'Forecasting & Staffing Predictions',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Forecasts call volumes and staffing needs to ensure optimal coverage.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Forecasts call volumes',
      'Predicts staffing needs',
      'Models scenarios',
      'Analyzes historical patterns',
      'Recommends schedules',
      'Reports forecast accuracy'
    ],
    primaryResponsibilities: [
      'Forecast call volumes',
      'Predict staffing requirements',
      'Model various scenarios',
      'Analyze historical trends',
      'Track forecast accuracy'
    ],
    recommendedTasks: [
      'Volume forecasting',
      'Staffing prediction',
      'Scenario modeling',
      'Trend analysis',
      'Accuracy tracking'
    ],
    modulesAssigned: ['Call Center', 'Scheduling', 'Reporting / Analytics'],
    permissions: {
      canReadCallLogs: true,
      canReadAnalytics: true,
      canCreateForecasts: true
    },
    limits: { maxTasksPerHour: 15, maxConcurrentTasks: 4 },
    tasksInQueue: 3,
    tasksToday: 10,
    tasksCompleted7Days: 68,
    avgResponseTime: '3.0s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '35%', resolutionRate: 'N/A' }
  },
  {
    id: 'cc-054',
    name: 'System Health Agent',
    role: 'Call Center System Health & Uptime',
    department: 'Call Center',
    category: 'Analytics & Systems',
    description: 'Monitors call center system health, uptime, and performance to ensure reliability.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Monitors system health',
      'Tracks uptime metrics',
      'Alerts on issues',
      'Diagnoses problems',
      'Coordinates fixes',
      'Reports system status'
    ],
    primaryResponsibilities: [
      'Monitor system health',
      'Ensure high uptime',
      'Alert on outages',
      'Diagnose system issues',
      'Coordinate with IT'
    ],
    recommendedTasks: [
      'Health monitoring',
      'Uptime tracking',
      'Issue alerting',
      'Problem diagnosis',
      'Fix coordination'
    ],
    modulesAssigned: ['Call Center'],
    permissions: {
      canMonitorSystems: true,
      canSendAlerts: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 10 },
    tasksInQueue: 2,
    tasksToday: 45,
    tasksCompleted7Days: 316,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: 'N/A', resolutionRate: '99.9%' }
  },

  // --- Specialty Support ---
  {
    id: 'cc-055',
    name: 'Multi-Language Agent',
    role: 'Multi-Language Support Agent',
    department: 'Call Center',
    category: 'Specialty Support',
    description: 'Provides customer support in multiple languages to serve diverse customer base.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Supports multiple languages',
      'Handles Spanish calls',
      'Translates communications',
      'Routes language-specific calls',
      'Documents in native language',
      'Bridges communication gaps'
    ],
    primaryResponsibilities: [
      'Provide multilingual support',
      'Handle non-English calls',
      'Translate as needed',
      'Route appropriately',
      'Document in primary language'
    ],
    recommendedTasks: [
      'Multilingual calls',
      'Translation support',
      'Language routing',
      'Documentation',
      'Communication bridging'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.5s',
    metrics: { callsToday: 22, avgHandleTime: '5:00', avgWaitReduction: '0%', resolutionRate: '88%' }
  },
  {
    id: 'cc-056',
    name: 'Accessibility Agent',
    role: 'Accessibility Support Agent',
    department: 'Call Center',
    category: 'Specialty Support',
    description: 'Provides specialized support for customers with accessibility needs.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Supports accessibility needs',
      'Handles TTY/TDD calls',
      'Provides extra patience',
      'Offers alternative formats',
      'Documents accommodations',
      'Ensures ADA compliance'
    ],
    primaryResponsibilities: [
      'Support customers with disabilities',
      'Handle accessibility requests',
      'Provide accommodations',
      'Ensure ADA compliance',
      'Document special needs'
    ],
    recommendedTasks: [
      'Accessibility support',
      'TTY handling',
      'Accommodation provision',
      'Compliance assurance',
      'Need documentation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 4 },
    tasksInQueue: 2,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '0.4s',
    metrics: { callsToday: 8, avgHandleTime: '7:00', avgWaitReduction: '0%', resolutionRate: '95%' }
  },
  {
    id: 'cc-057',
    name: 'Chat Support Agent',
    role: 'Chat Support Agent (Website + SMS)',
    department: 'Call Center',
    category: 'Specialty Support',
    description: 'Handles customer inquiries via website chat and SMS messaging channels.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles web chat',
      'Responds to SMS',
      'Manages multiple chats',
      'Provides quick answers',
      'Routes complex issues',
      'Documents chat interactions'
    ],
    primaryResponsibilities: [
      'Handle web chat inquiries',
      'Respond to SMS messages',
      'Manage concurrent chats',
      'Provide fast responses',
      'Route when needed'
    ],
    recommendedTasks: [
      'Web chat handling',
      'SMS responses',
      'Multi-chat management',
      'Quick answers',
      'Issue routing'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canHandleChat: true
    },
    limits: { maxTasksPerHour: 80, maxConcurrentTasks: 12 },
    tasksInQueue: 15,
    tasksToday: 95,
    tasksCompleted7Days: 668,
    avgResponseTime: '0.2s',
    metrics: { callsToday: 0, avgHandleTime: '4:00', avgWaitReduction: '60%', resolutionRate: '85%' }
  },
  {
    id: 'cc-058',
    name: 'Email Support Agent',
    role: 'Email Support Agent',
    department: 'Call Center',
    category: 'Specialty Support',
    description: 'Handles customer inquiries and support requests submitted via email.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Handles email inquiries',
      'Drafts professional responses',
      'Manages email queue',
      'Prioritizes by urgency',
      'Documents interactions',
      'Escalates when needed'
    ],
    primaryResponsibilities: [
      'Handle email support requests',
      'Draft professional responses',
      'Manage email backlog',
      'Prioritize appropriately',
      'Maintain response SLAs'
    ],
    recommendedTasks: [
      'Email response',
      'Queue management',
      'Priority handling',
      'Documentation',
      'Escalation routing'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canSendEmails: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 25,
    tasksToday: 55,
    tasksCompleted7Days: 386,
    avgResponseTime: '1.0s',
    metrics: { callsToday: 0, avgHandleTime: '8:00', avgWaitReduction: 'N/A', resolutionRate: '90%' }
  },
  {
    id: 'cc-059',
    name: 'Omni-Channel Agent',
    role: 'Omni-Channel Integration Agent',
    department: 'Call Center',
    category: 'Specialty Support',
    description: 'Coordinates customer interactions across all channels for seamless experience.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Manages cross-channel interactions',
      'Unifies customer view',
      'Routes across channels',
      'Maintains context',
      'Tracks channel preferences',
      'Ensures seamless experience'
    ],
    primaryResponsibilities: [
      'Coordinate cross-channel support',
      'Maintain unified customer view',
      'Route to appropriate channels',
      'Preserve interaction context',
      'Ensure seamless transitions'
    ],
    recommendedTasks: [
      'Channel coordination',
      'Context preservation',
      'Cross-channel routing',
      'Experience optimization',
      'Preference tracking'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts', 'Automations'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canManageRouting: true
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 65,
    tasksCompleted7Days: 456,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 0, avgHandleTime: 'N/A', avgWaitReduction: '25%', resolutionRate: '92%' }
  },
  {
    id: 'cc-060',
    name: 'Identity Verification Agent',
    role: 'Customer Identity Verification',
    department: 'Call Center',
    category: 'Specialty Support',
    description: 'Verifies customer identity before providing sensitive information or account access.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: '',
    capabilities: [
      'Verifies customer identity',
      'Asks security questions',
      'Validates account ownership',
      'Flags suspicious activity',
      'Documents verification',
      'Ensures data security'
    ],
    primaryResponsibilities: [
      'Verify customer identities',
      'Protect account security',
      'Ask verification questions',
      'Document all verifications',
      'Flag suspicious requests'
    ],
    recommendedTasks: [
      'Identity verification',
      'Security validation',
      'Question protocol',
      'Suspicious flagging',
      'Documentation'
    ],
    modulesAssigned: ['Call Center', 'CRM', 'Contacts'],
    permissions: {
      canReadCallLogs: true,
      canWriteCallLogs: true,
      canUpdateCRM: true,
      canVerifyIdentity: true
    },
    limits: { maxTasksPerHour: 60, maxConcurrentTasks: 10 },
    tasksInQueue: 5,
    tasksToday: 75,
    tasksCompleted7Days: 526,
    avgResponseTime: '0.3s',
    metrics: { callsToday: 65, avgHandleTime: '1:30', avgWaitReduction: '0%', resolutionRate: '100%' }
  },

  // ============ HUMAN RESOURCES DEPARTMENT ============
  // --- Leadership & Compliance ---
  {
    id: 'hr-001',
    name: 'CHRO Agent',
    role: 'Chief Human Resources Officer',
    department: 'Human Resources',
    category: 'Leadership & Compliance',
    description: 'Executive leader overseeing all HR operations including talent acquisition, employee relations, compliance, culture, payroll support, benefits, and workforce analytics.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Oversees all HR divisions and strategy',
      'Sets HR policies and culture initiatives',
      'Manages HR department budget',
      'Reviews workforce analytics',
      'Coordinates cross-functional HR initiatives',
      'Ensures regulatory compliance'
    ],
    primaryResponsibilities: [
      'Lead human resources operations',
      'Set workforce development strategy',
      'Manage HR division leaders',
      'Ensure employee satisfaction goals',
      'Drive organizational culture'
    ],
    recommendedTasks: [
      'Strategic planning',
      'Policy reviews',
      'Budget management',
      'Workforce analytics',
      'Leadership coordination'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations', 'Reporting / Analytics', 'Document Center'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: true,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 8 },
    tasksInQueue: 3,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '2.0s',
    metrics: { casesHandled: 45, requestsResolved: 38, pendingActions: 7, avgResolutionTime: '4h', engagementScore: '92%' },
    teamMembers: Array.from({length: 49}, (_, i) => `hr-${String(i + 2).padStart(3, '0')}`)
  },
  {
    id: 'hr-002',
    name: 'Talent & Culture Director',
    role: 'HR Director – Talent & Culture',
    department: 'Human Resources',
    category: 'Leadership & Compliance',
    description: 'Leads talent acquisition strategy, employer branding, and organizational culture initiatives.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Develops talent acquisition strategy',
      'Manages employer branding',
      'Leads culture initiatives',
      'Oversees recruiting team',
      'Analyzes talent metrics',
      'Builds employee engagement programs'
    ],
    primaryResponsibilities: [
      'Lead talent acquisition',
      'Drive culture programs',
      'Manage recruiting operations',
      'Build employer brand',
      'Track talent KPIs'
    ],
    recommendedTasks: [
      'Recruiting strategy',
      'Culture programs',
      'Employer branding',
      'Talent analytics',
      'Team leadership'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.5s',
    metrics: { casesHandled: 52, requestsResolved: 48, pendingActions: 4, avgResolutionTime: '3h', engagementScore: '94%' }
  },
  {
    id: 'hr-003',
    name: 'Compliance Director',
    role: 'HR Director – Compliance & Employee Relations',
    department: 'Human Resources',
    category: 'Leadership & Compliance',
    description: 'Oversees labor law compliance, employee relations, workplace policies, and risk management.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Ensures labor law compliance',
      'Manages employee relations issues',
      'Develops workplace policies',
      'Oversees risk management',
      'Handles sensitive HR matters',
      'Coordinates with legal team'
    ],
    primaryResponsibilities: [
      'Maintain regulatory compliance',
      'Resolve employee relations issues',
      'Update HR policies',
      'Manage workplace risk',
      'Handle investigations'
    ],
    recommendedTasks: [
      'Compliance audits',
      'Policy updates',
      'Employee relations',
      'Risk assessment',
      'Investigation management'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Document Center', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: true,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.8s',
    metrics: { casesHandled: 67, requestsResolved: 58, pendingActions: 9, avgResolutionTime: '6h', engagementScore: '89%' }
  },
  {
    id: 'hr-004',
    name: 'HR Operations Manager',
    role: 'HR Operations Manager',
    department: 'Human Resources',
    category: 'Leadership & Compliance',
    description: 'Manages day-to-day HR operations, systems, workflows, and process optimization.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages HR operations',
      'Optimizes HR processes',
      'Oversees HRIS systems',
      'Coordinates HR workflows',
      'Tracks operational metrics',
      'Manages HR vendors'
    ],
    primaryResponsibilities: [
      'Oversee daily HR operations',
      'Optimize HR processes',
      'Manage HR technology',
      'Coordinate workflows',
      'Track operational KPIs'
    ],
    recommendedTasks: [
      'Operations management',
      'Process optimization',
      'System administration',
      'Workflow coordination',
      'Vendor management'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.2s',
    metrics: { casesHandled: 78, requestsResolved: 72, pendingActions: 6, avgResolutionTime: '2h', engagementScore: '91%' }
  },
  {
    id: 'hr-005',
    name: 'Labor Law Agent',
    role: 'Labor Law & Compliance Agent',
    department: 'Human Resources',
    category: 'Leadership & Compliance',
    description: 'Monitors labor law changes, ensures compliance, and advises on employment regulations.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Monitors labor law changes',
      'Ensures regulatory compliance',
      'Advises on employment law',
      'Reviews HR practices',
      'Prepares compliance reports',
      'Coordinates with legal'
    ],
    primaryResponsibilities: [
      'Track labor law updates',
      'Ensure compliance',
      'Advise on regulations',
      'Review HR practices',
      'Prepare audit documentation'
    ],
    recommendedTasks: [
      'Law monitoring',
      'Compliance checks',
      'Legal advisories',
      'Practice reviews',
      'Audit preparation'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.5s',
    metrics: { casesHandled: 42, requestsResolved: 38, pendingActions: 4, avgResolutionTime: '3h', engagementScore: '88%' }
  },
  {
    id: 'hr-006',
    name: 'Policy Admin Agent',
    role: 'Policy & Handbook Administration Agent',
    department: 'Human Resources',
    category: 'Leadership & Compliance',
    description: 'Manages company policies, employee handbook, and SOP documentation.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages company policies',
      'Updates employee handbook',
      'Creates SOP documentation',
      'Distributes policy updates',
      'Tracks policy acknowledgments',
      'Archives historical versions'
    ],
    primaryResponsibilities: [
      'Maintain policy documentation',
      'Update employee handbook',
      'Create SOPs',
      'Distribute policy changes',
      'Track acknowledgments'
    ],
    recommendedTasks: [
      'Policy updates',
      'Handbook revisions',
      'SOP creation',
      'Distribution tracking',
      'Archive management'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 3,
    tasksToday: 10,
    tasksCompleted7Days: 70,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 35, requestsResolved: 32, pendingActions: 3, avgResolutionTime: '2h', engagementScore: '90%' }
  },
  {
    id: 'hr-007',
    name: 'Risk Response Agent',
    role: 'Risk & Incident Response Agent',
    department: 'Human Resources',
    category: 'Leadership & Compliance',
    description: 'Handles workplace incidents, risk assessment, and emergency HR response protocols.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Handles workplace incidents',
      'Conducts risk assessments',
      'Manages emergency protocols',
      'Documents incidents',
      'Coordinates response teams',
      'Implements preventive measures'
    ],
    primaryResponsibilities: [
      'Respond to incidents',
      'Assess workplace risks',
      'Manage emergencies',
      'Document incidents',
      'Implement prevention'
    ],
    recommendedTasks: [
      'Incident response',
      'Risk assessment',
      'Emergency coordination',
      'Documentation',
      'Prevention planning'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 2,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 28, requestsResolved: 26, pendingActions: 2, avgResolutionTime: '1h', engagementScore: '93%' }
  },

  // --- Recruiting & Talent Acquisition ---
  {
    id: 'hr-008',
    name: 'TA Manager',
    role: 'Recruiting Manager (TA Manager)',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Leads the talent acquisition team, manages recruiting strategy, and oversees all hiring processes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Leads recruiting team',
      'Develops hiring strategy',
      'Manages talent pipeline',
      'Oversees interview process',
      'Tracks recruiting metrics',
      'Builds employer brand'
    ],
    primaryResponsibilities: [
      'Lead recruiting operations',
      'Develop hiring strategy',
      'Manage talent pipeline',
      'Oversee interviewers',
      'Track recruiting KPIs'
    ],
    recommendedTasks: [
      'Team leadership',
      'Strategy development',
      'Pipeline management',
      'Metrics tracking',
      'Brand building'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 5,
    tasksToday: 20,
    tasksCompleted7Days: 140,
    avgResponseTime: '1.2s',
    metrics: { casesHandled: 65, requestsResolved: 58, pendingActions: 7, avgResolutionTime: '4h', engagementScore: '91%' }
  },
  {
    id: 'hr-009',
    name: 'Field Recruiter',
    role: 'Senior Recruiter – Construction & Field Roles',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Specializes in recruiting for construction, field operations, and technical trade positions.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Recruits field workers',
      'Sources construction talent',
      'Screens technical candidates',
      'Conducts field interviews',
      'Manages trade job postings',
      'Builds industry networks'
    ],
    primaryResponsibilities: [
      'Recruit field positions',
      'Source trade talent',
      'Screen candidates',
      'Conduct interviews',
      'Build talent networks'
    ],
    recommendedTasks: [
      'Field recruiting',
      'Candidate sourcing',
      'Technical screening',
      'Interview coordination',
      'Network building'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 85, requestsResolved: 72, pendingActions: 13, avgResolutionTime: '6h', engagementScore: '88%' }
  },
  {
    id: 'hr-010',
    name: 'Office Recruiter',
    role: 'Senior Recruiter – Administrative & Office Roles',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Specializes in recruiting for administrative, office, and corporate positions.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Recruits office staff',
      'Sources admin talent',
      'Screens professional candidates',
      'Conducts corporate interviews',
      'Manages office job postings',
      'Builds professional networks'
    ],
    primaryResponsibilities: [
      'Recruit office positions',
      'Source admin talent',
      'Screen candidates',
      'Conduct interviews',
      'Build professional networks'
    ],
    recommendedTasks: [
      'Office recruiting',
      'Candidate sourcing',
      'Professional screening',
      'Interview coordination',
      'Network building'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 24,
    tasksCompleted7Days: 168,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 72, requestsResolved: 65, pendingActions: 7, avgResolutionTime: '5h', engagementScore: '90%' }
  },
  {
    id: 'hr-011',
    name: 'Junior Recruiter',
    role: 'Junior Recruiter / Sourcer',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Supports recruiting efforts through candidate sourcing, initial outreach, and pipeline building.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Sources candidates',
      'Conducts initial outreach',
      'Builds talent pipeline',
      'Updates ATS records',
      'Supports senior recruiters',
      'Researches talent pools'
    ],
    primaryResponsibilities: [
      'Source candidates',
      'Initial outreach',
      'Build pipeline',
      'Update ATS',
      'Support recruiting team'
    ],
    recommendedTasks: [
      'Candidate sourcing',
      'Outreach campaigns',
      'Pipeline building',
      'ATS updates',
      'Research tasks'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 35,
    tasksCompleted7Days: 245,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 120, requestsResolved: 105, pendingActions: 15, avgResolutionTime: '2h', engagementScore: '87%' }
  },
  {
    id: 'hr-012',
    name: 'Screening Agent',
    role: 'Candidate Screening & Evaluation Agent',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Screens resumes, evaluates candidate qualifications, and performs initial assessments.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Screens resumes and applications',
      'Evaluates candidate qualifications',
      'Conducts initial assessments',
      'Scores candidates',
      'Flags top talent',
      'Filters unqualified applicants'
    ],
    primaryResponsibilities: [
      'Screen applications',
      'Evaluate qualifications',
      'Conduct assessments',
      'Score candidates',
      'Identify top talent'
    ],
    recommendedTasks: [
      'Resume screening',
      'Qualification checks',
      'Initial assessments',
      'Candidate scoring',
      'Talent flagging'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 12 },
    tasksInQueue: 18,
    tasksToday: 55,
    tasksCompleted7Days: 385,
    avgResponseTime: '0.5s',
    metrics: { casesHandled: 200, requestsResolved: 185, pendingActions: 15, avgResolutionTime: '30m', engagementScore: '92%' }
  },
  {
    id: 'hr-013',
    name: 'Interview Coordinator',
    role: 'Interview Coordination Agent',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Schedules interviews, coordinates with hiring managers, and manages interview logistics.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Schedules interviews',
      'Coordinates with hiring managers',
      'Manages interview logistics',
      'Sends calendar invites',
      'Prepares interview materials',
      'Handles rescheduling'
    ],
    primaryResponsibilities: [
      'Schedule interviews',
      'Coordinate stakeholders',
      'Manage logistics',
      'Send invitations',
      'Prepare materials'
    ],
    recommendedTasks: [
      'Interview scheduling',
      'Calendar coordination',
      'Logistics management',
      'Material preparation',
      'Rescheduling'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 10,
    tasksToday: 32,
    tasksCompleted7Days: 224,
    avgResponseTime: '0.6s',
    metrics: { casesHandled: 110, requestsResolved: 102, pendingActions: 8, avgResolutionTime: '1h', engagementScore: '94%' }
  },
  {
    id: 'hr-014',
    name: 'ATS Optimization Agent',
    role: 'Job Posting & ATS Optimization Agent',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Creates job postings, optimizes for search visibility, and manages ATS configuration.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Creates job postings',
      'Optimizes for SEO/visibility',
      'Manages ATS configuration',
      'Distributes to job boards',
      'Tracks posting performance',
      'A/B tests job descriptions'
    ],
    primaryResponsibilities: [
      'Create job postings',
      'Optimize visibility',
      'Manage ATS',
      'Distribute postings',
      'Track performance'
    ],
    recommendedTasks: [
      'Posting creation',
      'SEO optimization',
      'ATS management',
      'Board distribution',
      'Performance tracking'
    ],
    modulesAssigned: ['HR Hub', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 55, requestsResolved: 50, pendingActions: 5, avgResolutionTime: '2h', engagementScore: '89%' }
  },
  {
    id: 'hr-015',
    name: 'Background Check Agent',
    role: 'Background Check & Verification Agent',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Initiates background checks, verifies references, and validates candidate credentials.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Initiates background checks',
      'Verifies references',
      'Validates credentials',
      'Tracks verification status',
      'Flags discrepancies',
      'Coordinates with vendors'
    ],
    primaryResponsibilities: [
      'Initiate background checks',
      'Verify references',
      'Validate credentials',
      'Track status',
      'Report findings'
    ],
    recommendedTasks: [
      'Background checks',
      'Reference verification',
      'Credential validation',
      'Status tracking',
      'Discrepancy flagging'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 8,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 75, requestsResolved: 68, pendingActions: 7, avgResolutionTime: '24h', engagementScore: '91%' }
  },
  {
    id: 'hr-016',
    name: 'Offer Letter Agent',
    role: 'Offer Letter Drafting Agent',
    department: 'Human Resources',
    category: 'Recruiting & Talent Acquisition',
    description: 'Drafts offer letters, manages offer approvals, and tracks offer acceptance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Drafts offer letters',
      'Manages offer approvals',
      'Tracks offer acceptance',
      'Negotiates terms',
      'Processes signatures',
      'Archives offer documents'
    ],
    primaryResponsibilities: [
      'Draft offer letters',
      'Manage approvals',
      'Track acceptance',
      'Process negotiations',
      'Archive documents'
    ],
    recommendedTasks: [
      'Offer drafting',
      'Approval routing',
      'Acceptance tracking',
      'Negotiation support',
      'Document archiving'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: true,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.2s',
    metrics: { casesHandled: 42, requestsResolved: 38, pendingActions: 4, avgResolutionTime: '4h', engagementScore: '93%' }
  },

  // --- Onboarding & Offboarding ---
  {
    id: 'hr-017',
    name: 'Onboarding Specialist',
    role: 'Employee Onboarding Specialist Agent',
    department: 'Human Resources',
    category: 'Onboarding & Offboarding',
    description: 'Manages the complete onboarding experience for new hires from offer acceptance to first day.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages onboarding process',
      'Creates onboarding checklists',
      'Coordinates with departments',
      'Sends welcome communications',
      'Schedules orientation',
      'Tracks onboarding progress'
    ],
    primaryResponsibilities: [
      'Manage onboarding',
      'Create checklists',
      'Coordinate departments',
      'Send communications',
      'Track progress'
    ],
    recommendedTasks: [
      'Onboarding management',
      'Checklist creation',
      'Welcome emails',
      'Orientation scheduling',
      'Progress tracking'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 85, requestsResolved: 78, pendingActions: 7, avgResolutionTime: '2h', engagementScore: '95%' }
  },
  {
    id: 'hr-018',
    name: 'New Hire Doc Agent',
    role: 'New Hire Documentation Agent',
    department: 'Human Resources',
    category: 'Onboarding & Offboarding',
    description: 'Collects, processes, and manages all new hire documentation and forms.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Collects new hire documents',
      'Processes paperwork',
      'Validates document completeness',
      'Manages digital signatures',
      'Archives employee files',
      'Sends document reminders'
    ],
    primaryResponsibilities: [
      'Collect documents',
      'Process paperwork',
      'Validate completeness',
      'Manage signatures',
      'Archive files'
    ],
    recommendedTasks: [
      'Document collection',
      'Paperwork processing',
      'Validation checks',
      'Signature tracking',
      'File archiving'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 10,
    tasksToday: 32,
    tasksCompleted7Days: 224,
    avgResponseTime: '0.6s',
    metrics: { casesHandled: 95, requestsResolved: 88, pendingActions: 7, avgResolutionTime: '1h', engagementScore: '92%' }
  },
  {
    id: 'hr-019',
    name: 'I-9 Verification Agent',
    role: 'I-9 & Eligibility Verification Agent',
    department: 'Human Resources',
    category: 'Onboarding & Offboarding',
    description: 'Manages I-9 compliance, E-Verify submissions, and work eligibility documentation.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages I-9 compliance',
      'Processes E-Verify submissions',
      'Validates work authorization',
      'Tracks document expiration',
      'Ensures compliance deadlines',
      'Archives I-9 forms'
    ],
    primaryResponsibilities: [
      'Manage I-9 process',
      'Submit E-Verify',
      'Validate authorization',
      'Track expirations',
      'Ensure compliance'
    ],
    recommendedTasks: [
      'I-9 processing',
      'E-Verify submission',
      'Authorization validation',
      'Expiration tracking',
      'Compliance monitoring'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 62, requestsResolved: 58, pendingActions: 4, avgResolutionTime: '2h', engagementScore: '94%' }
  },
  {
    id: 'hr-020',
    name: 'Training Coordinator',
    role: 'Training & Orientation Coordinator Agent',
    department: 'Human Resources',
    category: 'Onboarding & Offboarding',
    description: 'Coordinates new hire training sessions, orientation programs, and initial skill development.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Coordinates training sessions',
      'Schedules orientation programs',
      'Assigns training modules',
      'Tracks completion status',
      'Manages training resources',
      'Evaluates training effectiveness'
    ],
    primaryResponsibilities: [
      'Coordinate training',
      'Schedule orientation',
      'Assign modules',
      'Track completion',
      'Evaluate effectiveness'
    ],
    recommendedTasks: [
      'Training coordination',
      'Orientation scheduling',
      'Module assignment',
      'Completion tracking',
      'Effectiveness evaluation'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 7,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '0.7s',
    metrics: { casesHandled: 72, requestsResolved: 68, pendingActions: 4, avgResolutionTime: '3h', engagementScore: '93%' }
  },
  {
    id: 'hr-021',
    name: 'Provisioning Agent',
    role: 'Equipment / Access Provisioning Agent',
    department: 'Human Resources',
    category: 'Onboarding & Offboarding',
    description: 'Coordinates equipment setup, system access provisioning, and workspace preparation for new hires.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Coordinates equipment setup',
      'Provisions system access',
      'Prepares workspaces',
      'Tracks asset assignment',
      'Coordinates with IT',
      'Manages access requests'
    ],
    primaryResponsibilities: [
      'Coordinate equipment',
      'Provision access',
      'Prepare workspace',
      'Track assets',
      'Coordinate IT'
    ],
    recommendedTasks: [
      'Equipment coordination',
      'Access provisioning',
      'Workspace setup',
      'Asset tracking',
      'IT coordination'
    ],
    modulesAssigned: ['HR Hub', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 52, requestsResolved: 48, pendingActions: 4, avgResolutionTime: '4h', engagementScore: '90%' }
  },
  {
    id: 'hr-022',
    name: 'Offboarding Agent',
    role: 'Offboarding & Exit Interview Agent',
    department: 'Human Resources',
    category: 'Onboarding & Offboarding',
    description: 'Manages employee separations, conducts exit interviews, and processes offboarding tasks.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages employee separations',
      'Conducts exit interviews',
      'Processes final documentation',
      'Coordinates access revocation',
      'Collects company property',
      'Analyzes turnover data'
    ],
    primaryResponsibilities: [
      'Manage separations',
      'Conduct exit interviews',
      'Process documentation',
      'Coordinate access removal',
      'Collect property'
    ],
    recommendedTasks: [
      'Separation processing',
      'Exit interviews',
      'Final documentation',
      'Access revocation',
      'Property collection'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 28, requestsResolved: 25, pendingActions: 3, avgResolutionTime: '6h', engagementScore: '88%' }
  },

  // --- Employee Relations & Support ---
  {
    id: 'hr-023',
    name: 'ER Manager Agent',
    role: 'Employee Relations Manager Agent',
    department: 'Human Resources',
    category: 'Employee Relations & Support',
    description: 'Leads employee relations initiatives, manages complex employee issues, and ensures positive workplace culture.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Leads employee relations',
      'Manages complex issues',
      'Ensures positive culture',
      'Advises on ER matters',
      'Oversees investigations',
      'Builds manager relationships'
    ],
    primaryResponsibilities: [
      'Lead ER initiatives',
      'Manage complex issues',
      'Ensure positive culture',
      'Advise stakeholders',
      'Oversee investigations'
    ],
    recommendedTasks: [
      'ER leadership',
      'Issue management',
      'Culture initiatives',
      'Stakeholder advising',
      'Investigation oversight'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Document Center', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.5s',
    metrics: { casesHandled: 48, requestsResolved: 42, pendingActions: 6, avgResolutionTime: '8h', engagementScore: '89%' }
  },
  {
    id: 'hr-024',
    name: 'Conflict Resolution Agent',
    role: 'Conflict Resolution & Mediation Agent',
    department: 'Human Resources',
    category: 'Employee Relations & Support',
    description: 'Mediates workplace conflicts, facilitates difficult conversations, and resolves interpersonal issues.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Mediates workplace conflicts',
      'Facilitates difficult conversations',
      'Resolves interpersonal issues',
      'Documents resolutions',
      'Provides conflict coaching',
      'Escalates as needed'
    ],
    primaryResponsibilities: [
      'Mediate conflicts',
      'Facilitate conversations',
      'Resolve issues',
      'Document resolutions',
      'Coach on conflict'
    ],
    recommendedTasks: [
      'Conflict mediation',
      'Conversation facilitation',
      'Issue resolution',
      'Resolution documentation',
      'Conflict coaching'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s',
    metrics: { casesHandled: 25, requestsResolved: 22, pendingActions: 3, avgResolutionTime: '12h', engagementScore: '87%' }
  },
  {
    id: 'hr-025',
    name: 'PTO Tracking Agent',
    role: 'Attendance & PTO Tracking Agent',
    department: 'Human Resources',
    category: 'Employee Relations & Support',
    description: 'Manages attendance tracking, PTO requests, and leave balance administration.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages attendance tracking',
      'Processes PTO requests',
      'Administers leave balances',
      'Tracks attendance patterns',
      'Generates absence reports',
      'Handles leave policies'
    ],
    primaryResponsibilities: [
      'Track attendance',
      'Process PTO requests',
      'Manage leave balances',
      'Monitor patterns',
      'Generate reports'
    ],
    recommendedTasks: [
      'Attendance tracking',
      'PTO processing',
      'Balance management',
      'Pattern monitoring',
      'Report generation'
    ],
    modulesAssigned: ['HR Hub', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 12 },
    tasksInQueue: 15,
    tasksToday: 45,
    tasksCompleted7Days: 315,
    avgResponseTime: '0.5s',
    metrics: { casesHandled: 150, requestsResolved: 142, pendingActions: 8, avgResolutionTime: '30m', engagementScore: '94%' }
  },
  {
    id: 'hr-026',
    name: 'HR Help Desk Agent',
    role: 'HR Help Desk / Employee Support Agent',
    department: 'Human Resources',
    category: 'Employee Relations & Support',
    description: 'Provides frontline HR support, answers employee questions, and resolves routine HR inquiries.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Provides HR support',
      'Answers employee questions',
      'Resolves routine inquiries',
      'Routes complex issues',
      'Maintains FAQ knowledge',
      'Tracks support tickets'
    ],
    primaryResponsibilities: [
      'Provide frontline support',
      'Answer questions',
      'Resolve inquiries',
      'Route complex issues',
      'Track tickets'
    ],
    recommendedTasks: [
      'Support responses',
      'Question answering',
      'Inquiry resolution',
      'Issue routing',
      'Ticket tracking'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 15 },
    tasksInQueue: 12,
    tasksToday: 55,
    tasksCompleted7Days: 385,
    avgResponseTime: '0.4s',
    metrics: { casesHandled: 180, requestsResolved: 172, pendingActions: 8, avgResolutionTime: '20m', engagementScore: '92%' }
  },
  {
    id: 'hr-027',
    name: 'Internal Comms Agent',
    role: 'Internal Communications Agent',
    department: 'Human Resources',
    category: 'Employee Relations & Support',
    description: 'Manages internal HR communications, announcements, and employee newsletters.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages internal communications',
      'Creates HR announcements',
      'Produces employee newsletters',
      'Coordinates messaging',
      'Maintains communication calendar',
      'Measures communication effectiveness'
    ],
    primaryResponsibilities: [
      'Manage communications',
      'Create announcements',
      'Produce newsletters',
      'Coordinate messaging',
      'Measure effectiveness'
    ],
    recommendedTasks: [
      'Communication management',
      'Announcement creation',
      'Newsletter production',
      'Message coordination',
      'Effectiveness tracking'
    ],
    modulesAssigned: ['HR Hub', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 42, requestsResolved: 40, pendingActions: 2, avgResolutionTime: '2h', engagementScore: '91%' }
  },
  {
    id: 'hr-028',
    name: 'Engagement Agent',
    role: 'Workplace Satisfaction & Engagement Agent',
    department: 'Human Resources',
    category: 'Employee Relations & Support',
    description: 'Monitors employee satisfaction, manages engagement surveys, and implements improvement initiatives.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Monitors employee satisfaction',
      'Manages engagement surveys',
      'Analyzes engagement data',
      'Implements improvements',
      'Tracks eNPS scores',
      'Recommends initiatives'
    ],
    primaryResponsibilities: [
      'Monitor satisfaction',
      'Manage surveys',
      'Analyze data',
      'Implement improvements',
      'Track metrics'
    ],
    recommendedTasks: [
      'Satisfaction monitoring',
      'Survey management',
      'Data analysis',
      'Initiative implementation',
      'Metric tracking'
    ],
    modulesAssigned: ['HR Hub', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.2s',
    metrics: { casesHandled: 45, requestsResolved: 42, pendingActions: 3, avgResolutionTime: '4h', engagementScore: '93%' }
  },
  {
    id: 'hr-029',
    name: 'Feedback Monitor Agent',
    role: 'Anonymous Feedback Monitor Agent',
    department: 'Human Resources',
    category: 'Employee Relations & Support',
    description: 'Monitors anonymous feedback channels, identifies trends, and escalates concerns appropriately.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Monitors anonymous feedback',
      'Identifies feedback trends',
      'Escalates concerns',
      'Protects anonymity',
      'Generates feedback reports',
      'Recommends actions'
    ],
    primaryResponsibilities: [
      'Monitor feedback',
      'Identify trends',
      'Escalate concerns',
      'Protect anonymity',
      'Generate reports'
    ],
    recommendedTasks: [
      'Feedback monitoring',
      'Trend identification',
      'Concern escalation',
      'Anonymity protection',
      'Report generation'
    ],
    modulesAssigned: ['HR Hub', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 65, requestsResolved: 58, pendingActions: 7, avgResolutionTime: '3h', engagementScore: '90%' }
  },

  // --- Training & Culture ---
  {
    id: 'hr-030',
    name: 'L&D Manager Agent',
    role: 'Learning & Development Manager Agent',
    department: 'Human Resources',
    category: 'Training & Culture',
    description: 'Leads learning and development strategy, manages training programs, and oversees skill development.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Leads L&D strategy',
      'Manages training programs',
      'Oversees skill development',
      'Evaluates training ROI',
      'Builds learning paths',
      'Coordinates with departments'
    ],
    primaryResponsibilities: [
      'Lead L&D strategy',
      'Manage programs',
      'Oversee development',
      'Evaluate ROI',
      'Build learning paths'
    ],
    recommendedTasks: [
      'Strategy leadership',
      'Program management',
      'Development oversight',
      'ROI evaluation',
      'Path building'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.5s',
    metrics: { casesHandled: 45, requestsResolved: 42, pendingActions: 3, avgResolutionTime: '4h', engagementScore: '92%' }
  },
  {
    id: 'hr-031',
    name: 'Training Builder Agent',
    role: 'Training Program Builder Agent',
    department: 'Human Resources',
    category: 'Training & Culture',
    description: 'Creates and develops training content, courses, and learning materials.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Creates training content',
      'Develops courses',
      'Builds learning materials',
      'Designs assessments',
      'Updates curriculum',
      'Incorporates feedback'
    ],
    primaryResponsibilities: [
      'Create content',
      'Develop courses',
      'Build materials',
      'Design assessments',
      'Update curriculum'
    ],
    recommendedTasks: [
      'Content creation',
      'Course development',
      'Material building',
      'Assessment design',
      'Curriculum updates'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s',
    metrics: { casesHandled: 28, requestsResolved: 25, pendingActions: 3, avgResolutionTime: '8h', engagementScore: '89%' }
  },
  {
    id: 'hr-032',
    name: 'Skills Assessment Agent',
    role: 'Skills Assessment & Certification Agent',
    department: 'Human Resources',
    category: 'Training & Culture',
    description: 'Conducts skills assessments, manages certifications, and tracks competency development.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Conducts skills assessments',
      'Manages certifications',
      'Tracks competency development',
      'Identifies skill gaps',
      'Recommends training',
      'Validates credentials'
    ],
    primaryResponsibilities: [
      'Conduct assessments',
      'Manage certifications',
      'Track competencies',
      'Identify gaps',
      'Recommend training'
    ],
    recommendedTasks: [
      'Skills assessment',
      'Certification management',
      'Competency tracking',
      'Gap identification',
      'Training recommendation'
    ],
    modulesAssigned: ['HR Hub', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 58, requestsResolved: 54, pendingActions: 4, avgResolutionTime: '3h', engagementScore: '91%' }
  },
  {
    id: 'hr-033',
    name: 'Culture Agent',
    role: 'Culture & Core Values Reinforcement Agent',
    department: 'Human Resources',
    category: 'Training & Culture',
    description: 'Promotes company culture, reinforces core values, and drives cultural initiatives.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Promotes company culture',
      'Reinforces core values',
      'Drives cultural initiatives',
      'Recognizes value champions',
      'Organizes culture events',
      'Measures cultural alignment'
    ],
    primaryResponsibilities: [
      'Promote culture',
      'Reinforce values',
      'Drive initiatives',
      'Recognize champions',
      'Organize events'
    ],
    recommendedTasks: [
      'Culture promotion',
      'Values reinforcement',
      'Initiative driving',
      'Champion recognition',
      'Event organization'
    ],
    modulesAssigned: ['HR Hub', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.2s',
    metrics: { casesHandled: 38, requestsResolved: 35, pendingActions: 3, avgResolutionTime: '4h', engagementScore: '95%' }
  },
  {
    id: 'hr-034',
    name: 'Leadership Dev Agent',
    role: 'Leadership Development & Coaching Agent',
    department: 'Human Resources',
    category: 'Training & Culture',
    description: 'Develops leadership programs, provides coaching support, and builds management capabilities.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Develops leadership programs',
      'Provides coaching support',
      'Builds management capabilities',
      'Identifies high potentials',
      'Creates development plans',
      'Facilitates leadership training'
    ],
    primaryResponsibilities: [
      'Develop programs',
      'Provide coaching',
      'Build capabilities',
      'Identify potentials',
      'Create plans'
    ],
    recommendedTasks: [
      'Program development',
      'Coaching sessions',
      'Capability building',
      'Potential identification',
      'Plan creation'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 10,
    tasksCompleted7Days: 70,
    avgResponseTime: '1.8s',
    metrics: { casesHandled: 32, requestsResolved: 30, pendingActions: 2, avgResolutionTime: '6h', engagementScore: '93%' }
  },
  {
    id: 'hr-035',
    name: 'PIP Agent',
    role: 'Performance Improvement Plan (PIP) Agent',
    department: 'Human Resources',
    category: 'Training & Culture',
    description: 'Manages performance improvement plans, tracks progress, and supports struggling employees.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages PIPs',
      'Tracks improvement progress',
      'Supports struggling employees',
      'Documents performance issues',
      'Coordinates with managers',
      'Recommends actions'
    ],
    primaryResponsibilities: [
      'Manage PIPs',
      'Track progress',
      'Support employees',
      'Document issues',
      'Coordinate managers'
    ],
    recommendedTasks: [
      'PIP management',
      'Progress tracking',
      'Employee support',
      'Issue documentation',
      'Manager coordination'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 4,
    tasksToday: 10,
    tasksCompleted7Days: 70,
    avgResponseTime: '1.5s',
    metrics: { casesHandled: 35, requestsResolved: 30, pendingActions: 5, avgResolutionTime: '12h', engagementScore: '85%' }
  },

  // --- Payroll & Benefits Support ---
  {
    id: 'hr-036',
    name: 'Payroll Oversight Agent',
    role: 'Payroll Oversight Agent',
    department: 'Human Resources',
    category: 'Payroll & Benefits Support',
    description: 'Oversees payroll processes, validates payroll data, and ensures accurate compensation delivery (placeholder logic).',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Oversees payroll processes',
      'Validates payroll data',
      'Ensures accurate compensation',
      'Reviews payroll exceptions',
      'Coordinates with finance',
      'Tracks payroll metrics'
    ],
    primaryResponsibilities: [
      'Oversee payroll',
      'Validate data',
      'Ensure accuracy',
      'Review exceptions',
      'Coordinate finance'
    ],
    recommendedTasks: [
      'Payroll oversight',
      'Data validation',
      'Accuracy checks',
      'Exception review',
      'Finance coordination'
    ],
    modulesAssigned: ['HR Hub', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: true,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.2s',
    metrics: { casesHandled: 52, requestsResolved: 48, pendingActions: 4, avgResolutionTime: '3h', engagementScore: '90%' }
  },
  {
    id: 'hr-037',
    name: 'Timecard Agent',
    role: 'Timecard Validation Agent',
    department: 'Human Resources',
    category: 'Payroll & Benefits Support',
    description: 'Validates timecards, flags discrepancies, and ensures accurate time tracking.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Validates timecards',
      'Flags discrepancies',
      'Ensures accurate tracking',
      'Reviews overtime',
      'Checks compliance',
      'Generates time reports'
    ],
    primaryResponsibilities: [
      'Validate timecards',
      'Flag discrepancies',
      'Ensure accuracy',
      'Review overtime',
      'Check compliance'
    ],
    recommendedTasks: [
      'Timecard validation',
      'Discrepancy flagging',
      'Accuracy checks',
      'Overtime review',
      'Compliance checking'
    ],
    modulesAssigned: ['HR Hub', 'Automations', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 10,
    tasksToday: 35,
    tasksCompleted7Days: 245,
    avgResponseTime: '0.6s',
    metrics: { casesHandled: 120, requestsResolved: 115, pendingActions: 5, avgResolutionTime: '1h', engagementScore: '92%' }
  },
  {
    id: 'hr-038',
    name: 'Compensation Agent',
    role: 'Compensation & Market Benchmarking Agent',
    department: 'Human Resources',
    category: 'Payroll & Benefits Support',
    description: 'Analyzes market compensation data, benchmarks roles, and supports pay equity initiatives.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Analyzes market data',
      'Benchmarks roles',
      'Supports pay equity',
      'Reviews compensation structures',
      'Recommends adjustments',
      'Generates comp reports'
    ],
    primaryResponsibilities: [
      'Analyze market data',
      'Benchmark roles',
      'Support pay equity',
      'Review structures',
      'Recommend adjustments'
    ],
    recommendedTasks: [
      'Market analysis',
      'Role benchmarking',
      'Equity support',
      'Structure review',
      'Adjustment recommendations'
    ],
    modulesAssigned: ['HR Hub', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: true,
      canAccessCompensation: true,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s',
    metrics: { casesHandled: 25, requestsResolved: 22, pendingActions: 3, avgResolutionTime: '8h', engagementScore: '88%' }
  },
  {
    id: 'hr-039',
    name: 'Benefits Enrollment Agent',
    role: 'Benefits Enrollment Agent',
    department: 'Human Resources',
    category: 'Payroll & Benefits Support',
    description: 'Manages benefits enrollment, assists employees with plan selection, and processes changes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages benefits enrollment',
      'Assists plan selection',
      'Processes benefit changes',
      'Handles open enrollment',
      'Explains benefit options',
      'Tracks enrollment status'
    ],
    primaryResponsibilities: [
      'Manage enrollment',
      'Assist selection',
      'Process changes',
      'Handle open enrollment',
      'Explain options'
    ],
    recommendedTasks: [
      'Enrollment management',
      'Selection assistance',
      'Change processing',
      'Open enrollment',
      'Option explanation'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 85, requestsResolved: 78, pendingActions: 7, avgResolutionTime: '2h', engagementScore: '91%' }
  },
  {
    id: 'hr-040',
    name: 'Workers Comp Agent',
    role: 'Workers Compensation Support Agent',
    department: 'Human Resources',
    category: 'Payroll & Benefits Support',
    description: 'Supports workers compensation claims, coordinates with insurance, and manages injury documentation.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Supports WC claims',
      'Coordinates with insurance',
      'Manages injury documentation',
      'Tracks claim status',
      'Ensures compliance',
      'Facilitates return-to-work'
    ],
    primaryResponsibilities: [
      'Support claims',
      'Coordinate insurance',
      'Manage documentation',
      'Track status',
      'Ensure compliance'
    ],
    recommendedTasks: [
      'Claim support',
      'Insurance coordination',
      'Documentation management',
      'Status tracking',
      'Compliance monitoring'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.2s',
    metrics: { casesHandled: 38, requestsResolved: 34, pendingActions: 4, avgResolutionTime: '24h', engagementScore: '87%' }
  },
  {
    id: 'hr-041',
    name: 'Benefits Liaison Agent',
    role: 'Insurance & Benefit Claims Liaison Agent',
    department: 'Human Resources',
    category: 'Payroll & Benefits Support',
    description: 'Liaises with insurance providers, assists with benefit claims, and resolves coverage issues.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Liaises with insurance',
      'Assists benefit claims',
      'Resolves coverage issues',
      'Advocates for employees',
      'Tracks claim resolutions',
      'Provides benefits guidance'
    ],
    primaryResponsibilities: [
      'Liaise with providers',
      'Assist claims',
      'Resolve issues',
      'Advocate for employees',
      'Track resolutions'
    ],
    recommendedTasks: [
      'Provider liaison',
      'Claim assistance',
      'Issue resolution',
      'Employee advocacy',
      'Resolution tracking'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 62, requestsResolved: 55, pendingActions: 7, avgResolutionTime: '6h', engagementScore: '89%' }
  },

  // --- Performance & Analytics ---
  {
    id: 'hr-042',
    name: 'KPI Tracking Agent',
    role: 'KPI & Performance Tracking Agent',
    department: 'Human Resources',
    category: 'Performance & Analytics',
    description: 'Tracks employee KPIs, monitors performance metrics, and generates performance reports.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Tracks employee KPIs',
      'Monitors performance metrics',
      'Generates performance reports',
      'Identifies performance trends',
      'Alerts on thresholds',
      'Supports goal tracking'
    ],
    primaryResponsibilities: [
      'Track KPIs',
      'Monitor metrics',
      'Generate reports',
      'Identify trends',
      'Alert on issues'
    ],
    recommendedTasks: [
      'KPI tracking',
      'Metric monitoring',
      'Report generation',
      'Trend identification',
      'Threshold alerts'
    ],
    modulesAssigned: ['HR Hub', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.6s',
    metrics: { casesHandled: 95, requestsResolved: 90, pendingActions: 5, avgResolutionTime: '1h', engagementScore: '92%' }
  },
  {
    id: 'hr-043',
    name: 'Review Prep Agent',
    role: 'Annual Review Preparation Agent',
    department: 'Human Resources',
    category: 'Performance & Analytics',
    description: 'Prepares annual review materials, compiles performance data, and supports the review process.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Prepares review materials',
      'Compiles performance data',
      'Supports review process',
      'Schedules review meetings',
      'Tracks review completion',
      'Archives review documents'
    ],
    primaryResponsibilities: [
      'Prepare materials',
      'Compile data',
      'Support process',
      'Schedule meetings',
      'Track completion'
    ],
    recommendedTasks: [
      'Material preparation',
      'Data compilation',
      'Process support',
      'Meeting scheduling',
      'Completion tracking'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.0s',
    metrics: { casesHandled: 52, requestsResolved: 48, pendingActions: 4, avgResolutionTime: '4h', engagementScore: '90%' }
  },
  {
    id: 'hr-044',
    name: 'Turnover Analytics Agent',
    role: 'Employee Metrics & Turnover Analytics Agent',
    department: 'Human Resources',
    category: 'Performance & Analytics',
    description: 'Analyzes employee metrics, tracks turnover trends, and provides workforce insights.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Analyzes employee metrics',
      'Tracks turnover trends',
      'Provides workforce insights',
      'Predicts attrition risk',
      'Identifies retention factors',
      'Generates analytics reports'
    ],
    primaryResponsibilities: [
      'Analyze metrics',
      'Track turnover',
      'Provide insights',
      'Predict attrition',
      'Identify factors'
    ],
    recommendedTasks: [
      'Metric analysis',
      'Turnover tracking',
      'Insight provision',
      'Attrition prediction',
      'Factor identification'
    ],
    modulesAssigned: ['HR Hub', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.5s',
    metrics: { casesHandled: 42, requestsResolved: 38, pendingActions: 4, avgResolutionTime: '6h', engagementScore: '91%' }
  },
  {
    id: 'hr-045',
    name: 'Workforce Planning Agent',
    role: 'Workforce Planning & Headcount Forecasting Agent',
    department: 'Human Resources',
    category: 'Performance & Analytics',
    description: 'Plans workforce needs, forecasts headcount requirements, and supports capacity planning.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Plans workforce needs',
      'Forecasts headcount',
      'Supports capacity planning',
      'Analyzes hiring trends',
      'Models scenarios',
      'Recommends staffing levels'
    ],
    primaryResponsibilities: [
      'Plan workforce',
      'Forecast headcount',
      'Support capacity',
      'Analyze trends',
      'Model scenarios'
    ],
    recommendedTasks: [
      'Workforce planning',
      'Headcount forecasting',
      'Capacity support',
      'Trend analysis',
      'Scenario modeling'
    ],
    modulesAssigned: ['HR Hub', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: true,
      canAccessCompensation: true,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s',
    metrics: { casesHandled: 28, requestsResolved: 25, pendingActions: 3, avgResolutionTime: '8h', engagementScore: '89%' }
  },
  {
    id: 'hr-046',
    name: 'Succession Planning Agent',
    role: 'Succession Planning Agent',
    department: 'Human Resources',
    category: 'Performance & Analytics',
    description: 'Develops succession plans, identifies key talent, and supports leadership continuity.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Develops succession plans',
      'Identifies key talent',
      'Supports leadership continuity',
      'Tracks readiness levels',
      'Builds talent pipelines',
      'Recommends development'
    ],
    primaryResponsibilities: [
      'Develop plans',
      'Identify talent',
      'Support continuity',
      'Track readiness',
      'Build pipelines'
    ],
    recommendedTasks: [
      'Plan development',
      'Talent identification',
      'Continuity support',
      'Readiness tracking',
      'Pipeline building'
    ],
    modulesAssigned: ['HR Hub', 'CRM: Internal Staff', 'Reporting / Analytics'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '1.8s',
    metrics: { casesHandled: 28, requestsResolved: 25, pendingActions: 3, avgResolutionTime: '12h', engagementScore: '90%' }
  },

  // --- Administration ---
  {
    id: 'hr-047',
    name: 'File Management Agent',
    role: 'HR File Management & Auditing Agent',
    department: 'Human Resources',
    category: 'Administration',
    description: 'Manages HR file organization, conducts file audits, and ensures document compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages HR files',
      'Conducts file audits',
      'Ensures document compliance',
      'Organizes employee records',
      'Maintains file retention',
      'Flags missing documents'
    ],
    primaryResponsibilities: [
      'Manage files',
      'Conduct audits',
      'Ensure compliance',
      'Organize records',
      'Maintain retention'
    ],
    recommendedTasks: [
      'File management',
      'Audit execution',
      'Compliance checks',
      'Record organization',
      'Retention maintenance'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.6s',
    metrics: { casesHandled: 85, requestsResolved: 80, pendingActions: 5, avgResolutionTime: '1h', engagementScore: '91%' }
  },
  {
    id: 'hr-048',
    name: 'Document Encryption Agent',
    role: 'Confidential Document Encryption Agent',
    department: 'Human Resources',
    category: 'Administration',
    description: 'Manages document encryption, ensures data security, and protects confidential HR information.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Manages document encryption',
      'Ensures data security',
      'Protects confidential info',
      'Controls access levels',
      'Monitors security compliance',
      'Handles secure sharing'
    ],
    primaryResponsibilities: [
      'Manage encryption',
      'Ensure security',
      'Protect information',
      'Control access',
      'Monitor compliance'
    ],
    recommendedTasks: [
      'Encryption management',
      'Security assurance',
      'Information protection',
      'Access control',
      'Compliance monitoring'
    ],
    modulesAssigned: ['HR Hub', 'Document Center'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 42, requestsResolved: 40, pendingActions: 2, avgResolutionTime: '2h', engagementScore: '93%' }
  },
  {
    id: 'hr-049',
    name: 'Investigation Doc Agent',
    role: 'Internal Investigation Documentation Agent',
    department: 'Human Resources',
    category: 'Administration',
    description: 'Documents internal investigations, maintains investigation records, and ensures proper evidence handling.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Documents investigations',
      'Maintains investigation records',
      'Ensures evidence handling',
      'Creates investigation timelines',
      'Secures sensitive materials',
      'Supports legal compliance'
    ],
    primaryResponsibilities: [
      'Document investigations',
      'Maintain records',
      'Handle evidence',
      'Create timelines',
      'Secure materials'
    ],
    recommendedTasks: [
      'Investigation documentation',
      'Record maintenance',
      'Evidence handling',
      'Timeline creation',
      'Material security'
    ],
    modulesAssigned: ['HR Hub', 'Document Center'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: true,
      canViewPerformanceData: true,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 2,
    tasksToday: 6,
    tasksCompleted7Days: 42,
    avgResponseTime: '1.5s',
    metrics: { casesHandled: 18, requestsResolved: 16, pendingActions: 2, avgResolutionTime: '12h', engagementScore: '88%' }
  },
  {
    id: 'hr-050',
    name: 'HR Case Doc Agent',
    role: 'Meeting Minutes & HR Case Documentation Agent',
    department: 'Human Resources',
    category: 'Administration',
    description: 'Documents HR meetings, maintains case records, and ensures accurate meeting minutes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T08:00:00Z',
    updatedAt: '2025-12-11T08:00:00Z',
    personaPrompt: 'Define how this HR agent communicates, handles sensitive information, and manages internal workflow.',
    capabilities: [
      'Documents HR meetings',
      'Maintains case records',
      'Creates meeting minutes',
      'Tracks action items',
      'Archives case files',
      'Ensures documentation accuracy'
    ],
    primaryResponsibilities: [
      'Document meetings',
      'Maintain case records',
      'Create minutes',
      'Track actions',
      'Archive files'
    ],
    recommendedTasks: [
      'Meeting documentation',
      'Case maintenance',
      'Minutes creation',
      'Action tracking',
      'File archiving'
    ],
    modulesAssigned: ['HR Hub', 'Document Center', 'Automations'],
    permissions: {
      canReadHRFiles: true,
      canUpdateEmployeeRecords: false,
      canViewPerformanceData: false,
      canAccessCompensation: false,
      canCommunicateExternally: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '0.8s',
    metrics: { casesHandled: 52, requestsResolved: 48, pendingActions: 4, avgResolutionTime: '2h', engagementScore: '90%' }
  },

  // ============ FINANCE & ACCOUNTING DEPARTMENT ============
  // --- Leadership ---
  {
    id: 'fin-001',
    name: 'CFO Agent',
    role: 'Chief Financial Officer',
    department: 'Finance & Accounting',
    category: 'Leadership',
    description: 'Executive leader overseeing all financial operations including budgeting, accounting, job costing, compliance, forecasting, and financial reporting.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Oversees all financial operations',
      'Sets financial strategy and policies',
      'Manages department budget',
      'Reviews financial forecasts',
      'Ensures regulatory compliance',
      'Coordinates with executive team'
    ],
    primaryResponsibilities: [
      'Lead financial operations',
      'Set fiscal strategy',
      'Manage finance team',
      'Ensure profitability',
      'Drive cost optimization'
    ],
    recommendedTasks: [
      'Strategic planning',
      'Budget reviews',
      'Financial analysis',
      'Compliance oversight',
      'Executive reporting'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Job Costing', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: true,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '2.0s',
    metrics: { transactionsProcessed: 150, errorsDetected: 12, costSavingsIdentified: '$45,000', pendingQueue: 4, forecastAccuracy: '94%' },
    teamMembers: Array.from({length: 29}, (_, i) => `fin-${String(i + 2).padStart(3, '0')}`)
  },
  {
    id: 'fin-002',
    name: 'Financial Controller',
    role: 'Finance Director / Financial Controller',
    department: 'Finance & Accounting',
    category: 'Leadership',
    description: 'Directs accounting operations, financial reporting, and ensures accuracy of financial records.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Directs accounting operations',
      'Oversees financial reporting',
      'Ensures record accuracy',
      'Manages month-end close',
      'Reviews financial statements',
      'Coordinates audits'
    ],
    primaryResponsibilities: [
      'Direct accounting operations',
      'Oversee financial reporting',
      'Ensure record accuracy',
      'Manage close process',
      'Coordinate audits'
    ],
    recommendedTasks: [
      'Financial review',
      'Statement preparation',
      'Audit coordination',
      'Close management',
      'Compliance checks'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Job Costing'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: true,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.5s',
    metrics: { transactionsProcessed: 280, errorsDetected: 18, costSavingsIdentified: '$28,000', pendingQueue: 5, forecastAccuracy: '92%' }
  },
  {
    id: 'fin-003',
    name: 'Assistant Controller',
    role: 'Assistant Controller / Senior Accounting Agent',
    department: 'Finance & Accounting',
    category: 'Leadership',
    description: 'Supports the Controller with day-to-day accounting operations and supervises accounting staff.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Supports Controller',
      'Supervises accounting staff',
      'Reviews transactions',
      'Prepares reconciliations',
      'Manages daily operations',
      'Assists with reporting'
    ],
    primaryResponsibilities: [
      'Support Controller',
      'Supervise accounting',
      'Review transactions',
      'Prepare reconciliations',
      'Manage daily ops'
    ],
    recommendedTasks: [
      'Staff supervision',
      'Transaction review',
      'Reconciliation prep',
      'Daily operations',
      'Report assistance'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Job Costing'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '1.2s',
    metrics: { transactionsProcessed: 350, errorsDetected: 22, costSavingsIdentified: '$15,000', pendingQueue: 6, forecastAccuracy: '90%' }
  },

  // --- Core Accounting ---
  {
    id: 'fin-004',
    name: 'AR Agent',
    role: 'Accounts Receivable (AR) Agent',
    department: 'Finance & Accounting',
    category: 'Core Accounting',
    description: 'Manages accounts receivable, tracks customer payments, and monitors AR aging.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Manages accounts receivable',
      'Tracks customer payments',
      'Monitors AR aging',
      'Processes deposits',
      'Reconciles customer accounts',
      'Generates AR reports'
    ],
    primaryResponsibilities: [
      'Manage AR',
      'Track payments',
      'Monitor aging',
      'Process deposits',
      'Reconcile accounts'
    ],
    recommendedTasks: [
      'Payment tracking',
      'Aging analysis',
      'Deposit processing',
      'Account reconciliation',
      'AR reporting'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'CRM (financial view)', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 12 },
    tasksInQueue: 10,
    tasksToday: 35,
    tasksCompleted7Days: 245,
    avgResponseTime: '0.8s',
    metrics: { transactionsProcessed: 520, errorsDetected: 8, costSavingsIdentified: '$5,000', pendingQueue: 10, forecastAccuracy: '95%' }
  },
  {
    id: 'fin-005',
    name: 'AP Agent',
    role: 'Accounts Payable (AP) Agent',
    department: 'Finance & Accounting',
    category: 'Core Accounting',
    description: 'Manages accounts payable, processes vendor invoices, and schedules payments.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Manages accounts payable',
      'Processes vendor invoices',
      'Schedules payments',
      'Reconciles vendor accounts',
      'Tracks payment terms',
      'Generates AP reports'
    ],
    primaryResponsibilities: [
      'Manage AP',
      'Process invoices',
      'Schedule payments',
      'Reconcile vendors',
      'Track terms'
    ],
    recommendedTasks: [
      'Invoice processing',
      'Payment scheduling',
      'Bill reconciliation',
      'Vendor management',
      'AP reporting'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 12 },
    tasksInQueue: 12,
    tasksToday: 38,
    tasksCompleted7Days: 266,
    avgResponseTime: '0.7s',
    metrics: { transactionsProcessed: 580, errorsDetected: 10, costSavingsIdentified: '$8,000', pendingQueue: 12, forecastAccuracy: '94%' }
  },
  {
    id: 'fin-006',
    name: 'Billing Agent',
    role: 'Billing & Invoicing Agent',
    department: 'Finance & Accounting',
    category: 'Core Accounting',
    description: 'Creates and manages customer invoices, billing cycles, and payment terms.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Creates customer invoices',
      'Manages billing cycles',
      'Sets payment terms',
      'Processes billing adjustments',
      'Tracks invoice status',
      'Generates billing reports'
    ],
    primaryResponsibilities: [
      'Create invoices',
      'Manage billing',
      'Set terms',
      'Process adjustments',
      'Track status'
    ],
    recommendedTasks: [
      'Invoice creation',
      'Billing management',
      'Term setting',
      'Adjustment processing',
      'Status tracking'
    ],
    modulesAssigned: ['Finance Hub', 'CRM (financial view)', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 45, maxConcurrentTasks: 12 },
    tasksInQueue: 8,
    tasksToday: 32,
    tasksCompleted7Days: 224,
    avgResponseTime: '0.6s',
    metrics: { transactionsProcessed: 450, errorsDetected: 6, costSavingsIdentified: '$3,000', pendingQueue: 8, forecastAccuracy: '96%' }
  },
  {
    id: 'fin-007',
    name: 'Collections Agent',
    role: 'Collections & Past-Due Recovery Agent',
    department: 'Finance & Accounting',
    category: 'Core Accounting',
    description: 'Manages collections processes, follows up on past-due accounts, and recovers outstanding balances.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Manages collections',
      'Follows up on past-due accounts',
      'Recovers outstanding balances',
      'Negotiates payment plans',
      'Tracks collection status',
      'Generates collection reports'
    ],
    primaryResponsibilities: [
      'Manage collections',
      'Follow up on past-due',
      'Recover balances',
      'Negotiate payments',
      'Track status'
    ],
    recommendedTasks: [
      'Collections reminders',
      'Past-due follow-up',
      'Balance recovery',
      'Payment negotiation',
      'Status tracking'
    ],
    modulesAssigned: ['Finance Hub', 'CRM (financial view)', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: false,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 15,
    tasksToday: 42,
    tasksCompleted7Days: 294,
    avgResponseTime: '0.9s',
    metrics: { transactionsProcessed: 380, errorsDetected: 4, costSavingsIdentified: '$22,000', pendingQueue: 15, forecastAccuracy: '88%' }
  },
  {
    id: 'fin-008',
    name: 'Bank Recon Agent',
    role: 'Bank Reconciliation Agent',
    department: 'Finance & Accounting',
    category: 'Core Accounting',
    description: 'Performs bank reconciliations, identifies discrepancies, and ensures account accuracy.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Performs bank reconciliations',
      'Identifies discrepancies',
      'Ensures account accuracy',
      'Matches transactions',
      'Investigates variances',
      'Generates recon reports'
    ],
    primaryResponsibilities: [
      'Perform reconciliations',
      'Identify discrepancies',
      'Ensure accuracy',
      'Match transactions',
      'Investigate variances'
    ],
    recommendedTasks: [
      'Bank reconciliation',
      'Discrepancy identification',
      'Transaction matching',
      'Variance investigation',
      'Recon reporting'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: false,
      canReadJobCostData: false,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.0s',
    metrics: { transactionsProcessed: 280, errorsDetected: 15, costSavingsIdentified: '$6,000', pendingQueue: 5, forecastAccuracy: '97%' }
  },
  {
    id: 'fin-009',
    name: 'Expense Agent',
    role: 'Expense Management Agent',
    department: 'Finance & Accounting',
    category: 'Core Accounting',
    description: 'Manages expense reports, validates receipts, and ensures compliance with expense policies.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Manages expense reports',
      'Validates receipts',
      'Ensures policy compliance',
      'Categorizes expenses',
      'Tracks reimbursements',
      'Generates expense reports'
    ],
    primaryResponsibilities: [
      'Manage expenses',
      'Validate receipts',
      'Ensure compliance',
      'Categorize expenses',
      'Track reimbursements'
    ],
    recommendedTasks: [
      'Expense management',
      'Receipt validation',
      'Policy compliance',
      'Categorization',
      'Reimbursement tracking'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.8s',
    metrics: { transactionsProcessed: 320, errorsDetected: 12, costSavingsIdentified: '$4,500', pendingQueue: 8, forecastAccuracy: '93%' }
  },
  {
    id: 'fin-010',
    name: 'Payroll Oversight Agent',
    role: 'Payroll Oversight & Alignment Agent',
    department: 'Finance & Accounting',
    category: 'Core Accounting',
    description: 'Validates payroll data, ensures alignment with financial records, and reconciles payroll transactions (placeholder logic only).',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Validates payroll data',
      'Ensures financial alignment',
      'Reconciles payroll transactions',
      'Reviews labor costs',
      'Tracks payroll variances',
      'Generates payroll reports'
    ],
    primaryResponsibilities: [
      'Validate payroll',
      'Ensure alignment',
      'Reconcile transactions',
      'Review labor costs',
      'Track variances'
    ],
    recommendedTasks: [
      'Payroll validation',
      'Alignment checks',
      'Transaction reconciliation',
      'Labor cost review',
      'Variance tracking'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.2s',
    metrics: { transactionsProcessed: 180, errorsDetected: 8, costSavingsIdentified: '$12,000', pendingQueue: 4, forecastAccuracy: '95%' }
  },

  // --- Job Costing & Profitability ---
  {
    id: 'fin-011',
    name: 'Job Costing Agent',
    role: 'Job Costing Agent',
    department: 'Finance & Accounting',
    category: 'Job Costing & Profitability',
    description: 'Tracks job costs, allocates expenses to projects, and monitors budget vs. actual spending.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Tracks job costs',
      'Allocates expenses to projects',
      'Monitors budget vs. actual',
      'Calculates labor costs',
      'Tracks material costs',
      'Generates job cost reports'
    ],
    primaryResponsibilities: [
      'Track job costs',
      'Allocate expenses',
      'Monitor budget',
      'Calculate labor',
      'Track materials'
    ],
    recommendedTasks: [
      'Cost tracking',
      'Expense allocation',
      'Budget monitoring',
      'Labor calculation',
      'Material tracking'
    ],
    modulesAssigned: ['Finance Hub', 'Job Costing', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: true,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 10,
    tasksToday: 30,
    tasksCompleted7Days: 210,
    avgResponseTime: '0.9s',
    metrics: { transactionsProcessed: 420, errorsDetected: 18, costSavingsIdentified: '$35,000', pendingQueue: 10, forecastAccuracy: '91%' }
  },
  {
    id: 'fin-012',
    name: 'PO Verification Agent',
    role: 'Purchase Order Verification Agent',
    department: 'Finance & Accounting',
    category: 'Job Costing & Profitability',
    description: 'Verifies purchase orders, matches invoices to POs, and ensures proper authorization.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Verifies purchase orders',
      'Matches invoices to POs',
      'Ensures proper authorization',
      'Tracks PO status',
      'Validates pricing',
      'Generates PO reports'
    ],
    primaryResponsibilities: [
      'Verify POs',
      'Match invoices',
      'Ensure authorization',
      'Track status',
      'Validate pricing'
    ],
    recommendedTasks: [
      'PO verification',
      'Invoice matching',
      'Authorization checks',
      'Status tracking',
      'Price validation'
    ],
    modulesAssigned: ['Finance Hub', 'Job Costing', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.7s',
    metrics: { transactionsProcessed: 380, errorsDetected: 22, costSavingsIdentified: '$18,000', pendingQueue: 8, forecastAccuracy: '93%' }
  },
  {
    id: 'fin-013',
    name: 'Cost Overrun Agent',
    role: 'Cost Overrun Detection Agent',
    department: 'Finance & Accounting',
    category: 'Job Costing & Profitability',
    description: 'Detects cost overruns, alerts on budget deviations, and analyzes spending patterns.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Detects cost overruns',
      'Alerts on budget deviations',
      'Analyzes spending patterns',
      'Identifies abnormal costs',
      'Recommends corrective actions',
      'Generates overrun reports'
    ],
    primaryResponsibilities: [
      'Detect overruns',
      'Alert on deviations',
      'Analyze patterns',
      'Identify abnormal costs',
      'Recommend actions'
    ],
    recommendedTasks: [
      'Overrun detection',
      'Deviation alerts',
      'Pattern analysis',
      'Abnormal cost ID',
      'Action recommendations'
    ],
    modulesAssigned: ['Finance Hub', 'Job Costing', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.0s',
    metrics: { transactionsProcessed: 250, errorsDetected: 35, costSavingsIdentified: '$52,000', pendingQueue: 6, forecastAccuracy: '89%' }
  },
  {
    id: 'fin-014',
    name: 'Profitability Analyst',
    role: 'Profitability Analyst Agent',
    department: 'Finance & Accounting',
    category: 'Job Costing & Profitability',
    description: 'Analyzes job profitability, calculates margins, and identifies profit improvement opportunities.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Analyzes job profitability',
      'Calculates margins',
      'Identifies profit opportunities',
      'Compares job performance',
      'Runs P&L summaries',
      'Generates profitability reports'
    ],
    primaryResponsibilities: [
      'Analyze profitability',
      'Calculate margins',
      'Identify opportunities',
      'Compare performance',
      'Run P&L summaries'
    ],
    recommendedTasks: [
      'Profitability analysis',
      'Margin calculation',
      'Opportunity identification',
      'Performance comparison',
      'P&L reporting'
    ],
    modulesAssigned: ['Finance Hub', 'Job Costing', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.5s',
    metrics: { transactionsProcessed: 180, errorsDetected: 8, costSavingsIdentified: '$65,000', pendingQueue: 4, forecastAccuracy: '92%' }
  },
  {
    id: 'fin-015',
    name: 'Margin Optimization Agent',
    role: 'Margin Optimization Agent',
    department: 'Finance & Accounting',
    category: 'Job Costing & Profitability',
    description: 'Identifies margin improvement opportunities and recommends pricing and cost optimization strategies.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Identifies margin opportunities',
      'Recommends pricing strategies',
      'Analyzes cost structures',
      'Benchmarks against industry',
      'Models pricing scenarios',
      'Generates optimization reports'
    ],
    primaryResponsibilities: [
      'Identify opportunities',
      'Recommend strategies',
      'Analyze costs',
      'Benchmark margins',
      'Model scenarios'
    ],
    recommendedTasks: [
      'Opportunity identification',
      'Strategy recommendation',
      'Cost analysis',
      'Benchmarking',
      'Scenario modeling'
    ],
    modulesAssigned: ['Finance Hub', 'Job Costing', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: true,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s',
    metrics: { transactionsProcessed: 120, errorsDetected: 5, costSavingsIdentified: '$85,000', pendingQueue: 3, forecastAccuracy: '90%' }
  },

  // --- Compliance & Audit ---
  {
    id: 'fin-016',
    name: 'Internal Audit Agent',
    role: 'Internal Audit Agent',
    department: 'Finance & Accounting',
    category: 'Compliance & Audit',
    description: 'Conducts internal audits, reviews financial processes, and ensures control compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Conducts internal audits',
      'Reviews financial processes',
      'Ensures control compliance',
      'Tests internal controls',
      'Documents findings',
      'Recommends improvements'
    ],
    primaryResponsibilities: [
      'Conduct audits',
      'Review processes',
      'Ensure compliance',
      'Test controls',
      'Document findings'
    ],
    recommendedTasks: [
      'Audit execution',
      'Process review',
      'Compliance testing',
      'Control testing',
      'Finding documentation'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 5,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.5s',
    metrics: { transactionsProcessed: 220, errorsDetected: 42, costSavingsIdentified: '$28,000', pendingQueue: 5, forecastAccuracy: '94%' }
  },
  {
    id: 'fin-017',
    name: 'Invoice Audit Agent',
    role: 'Invoice Audit & Error Detection Agent',
    department: 'Finance & Accounting',
    category: 'Compliance & Audit',
    description: 'Audits invoices for accuracy, detects errors and duplicates, and ensures billing compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Audits invoices for accuracy',
      'Detects errors and duplicates',
      'Ensures billing compliance',
      'Validates invoice data',
      'Flags discrepancies',
      'Generates audit reports'
    ],
    primaryResponsibilities: [
      'Audit invoices',
      'Detect errors',
      'Ensure compliance',
      'Validate data',
      'Flag discrepancies'
    ],
    recommendedTasks: [
      'Invoice auditing',
      'Error detection',
      'Compliance checking',
      'Data validation',
      'Discrepancy flagging'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 38,
    tasksCompleted7Days: 266,
    avgResponseTime: '0.6s',
    metrics: { transactionsProcessed: 480, errorsDetected: 65, costSavingsIdentified: '$32,000', pendingQueue: 12, forecastAccuracy: '96%' }
  },
  {
    id: 'fin-018',
    name: 'Fraud Detection Agent',
    role: 'Fraud Detection & Risk Scoring Agent',
    department: 'Finance & Accounting',
    category: 'Compliance & Audit',
    description: 'Detects potential fraud, scores transaction risk, and flags suspicious activities.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Detects potential fraud',
      'Scores transaction risk',
      'Flags suspicious activities',
      'Analyzes fraud patterns',
      'Monitors anomalies',
      'Generates fraud reports'
    ],
    primaryResponsibilities: [
      'Detect fraud',
      'Score risk',
      'Flag suspicious activity',
      'Analyze patterns',
      'Monitor anomalies'
    ],
    recommendedTasks: [
      'Fraud detection',
      'Risk scoring',
      'Activity flagging',
      'Pattern analysis',
      'Anomaly monitoring'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 50, maxConcurrentTasks: 15 },
    tasksInQueue: 8,
    tasksToday: 45,
    tasksCompleted7Days: 315,
    avgResponseTime: '0.4s',
    metrics: { transactionsProcessed: 650, errorsDetected: 28, costSavingsIdentified: '$95,000', pendingQueue: 8, forecastAccuracy: '91%' }
  },
  {
    id: 'fin-019',
    name: 'Compliance Agent',
    role: 'Financial Compliance & Record Retention Agent',
    department: 'Finance & Accounting',
    category: 'Compliance & Audit',
    description: 'Ensures financial compliance, manages record retention, and maintains audit trails.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Ensures financial compliance',
      'Manages record retention',
      'Maintains audit trails',
      'Monitors regulatory changes',
      'Documents compliance status',
      'Generates compliance reports'
    ],
    primaryResponsibilities: [
      'Ensure compliance',
      'Manage records',
      'Maintain trails',
      'Monitor regulations',
      'Document status'
    ],
    recommendedTasks: [
      'Compliance monitoring',
      'Record management',
      'Audit trail maintenance',
      'Regulatory tracking',
      'Status documentation'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.2s',
    metrics: { transactionsProcessed: 180, errorsDetected: 15, costSavingsIdentified: '$12,000', pendingQueue: 4, forecastAccuracy: '97%' }
  },

  // --- Forecasting & Reporting ---
  {
    id: 'fin-020',
    name: 'Forecasting Agent',
    role: 'Financial Forecasting & Projection Agent',
    department: 'Finance & Accounting',
    category: 'Forecasting & Reporting',
    description: 'Builds financial forecasts, creates revenue projections, and models cash flow scenarios.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Builds financial forecasts',
      'Creates revenue projections',
      'Models cash flow scenarios',
      'Analyzes trends',
      'Predicts financial outcomes',
      'Generates forecast reports'
    ],
    primaryResponsibilities: [
      'Build forecasts',
      'Create projections',
      'Model scenarios',
      'Analyze trends',
      'Predict outcomes'
    ],
    recommendedTasks: [
      'Forecast building',
      'Revenue projection',
      'Cash flow scenarios',
      'Trend analysis',
      'Outcome prediction'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: true,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.5s',
    metrics: { transactionsProcessed: 120, errorsDetected: 5, costSavingsIdentified: '$45,000', pendingQueue: 3, forecastAccuracy: '88%' }
  },
  {
    id: 'fin-021',
    name: 'Budget Monitor Agent',
    role: 'Budget Creation & Monitoring Agent',
    department: 'Finance & Accounting',
    category: 'Forecasting & Reporting',
    description: 'Creates budgets, monitors spending against budget, and alerts on variances.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Creates budgets',
      'Monitors spending vs budget',
      'Alerts on variances',
      'Tracks budget utilization',
      'Recommends adjustments',
      'Generates budget reports'
    ],
    primaryResponsibilities: [
      'Create budgets',
      'Monitor spending',
      'Alert variances',
      'Track utilization',
      'Recommend adjustments'
    ],
    recommendedTasks: [
      'Budget creation',
      'Spending monitoring',
      'Variance alerts',
      'Utilization tracking',
      'Adjustment recommendations'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: true,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.2s',
    metrics: { transactionsProcessed: 280, errorsDetected: 18, costSavingsIdentified: '$38,000', pendingQueue: 6, forecastAccuracy: '91%' }
  },
  {
    id: 'fin-022',
    name: 'Variance Analysis Agent',
    role: 'Variance Analysis Agent',
    department: 'Finance & Accounting',
    category: 'Forecasting & Reporting',
    description: 'Analyzes budget vs. actual variances, identifies root causes, and recommends corrective actions.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Analyzes budget variances',
      'Identifies root causes',
      'Recommends corrective actions',
      'Tracks variance trends',
      'Compares periods',
      'Generates variance reports'
    ],
    primaryResponsibilities: [
      'Analyze variances',
      'Identify root causes',
      'Recommend actions',
      'Track trends',
      'Compare periods'
    ],
    recommendedTasks: [
      'Variance analysis',
      'Root cause identification',
      'Action recommendation',
      'Trend tracking',
      'Period comparison'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '1.5s',
    metrics: { transactionsProcessed: 180, errorsDetected: 22, costSavingsIdentified: '$25,000', pendingQueue: 4, forecastAccuracy: '93%' }
  },
  {
    id: 'fin-023',
    name: 'Commission Validation Agent',
    role: 'Commission Calculation & Validation Agent',
    department: 'Finance & Accounting',
    category: 'Forecasting & Reporting',
    description: 'Validates commission calculations, ensures accuracy of sales commissions, and reconciles payouts.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Validates commission calculations',
      'Ensures calculation accuracy',
      'Reconciles payouts',
      'Applies commission rules',
      'Tracks commission history',
      'Generates commission reports'
    ],
    primaryResponsibilities: [
      'Validate commissions',
      'Ensure accuracy',
      'Reconcile payouts',
      'Apply rules',
      'Track history'
    ],
    recommendedTasks: [
      'Commission validation',
      'Accuracy checking',
      'Payout reconciliation',
      'Rule application',
      'History tracking'
    ],
    modulesAssigned: ['Finance Hub', 'Sales Commission Module', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.9s',
    metrics: { transactionsProcessed: 320, errorsDetected: 15, costSavingsIdentified: '$18,000', pendingQueue: 8, forecastAccuracy: '95%' }
  },
  {
    id: 'fin-024',
    name: 'Executive Reporting Agent',
    role: 'Executive Financial Reporting Agent',
    department: 'Finance & Accounting',
    category: 'Forecasting & Reporting',
    description: 'Prepares executive financial reports, dashboards, and KPI summaries for leadership.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Prepares executive reports',
      'Creates financial dashboards',
      'Summarizes KPIs',
      'Generates board materials',
      'Tracks key metrics',
      'Presents financial insights'
    ],
    primaryResponsibilities: [
      'Prepare reports',
      'Create dashboards',
      'Summarize KPIs',
      'Generate materials',
      'Track metrics'
    ],
    recommendedTasks: [
      'Report preparation',
      'Dashboard creation',
      'KPI summarization',
      'Material generation',
      'Metric tracking'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '2.0s',
    metrics: { transactionsProcessed: 80, errorsDetected: 3, costSavingsIdentified: '$0', pendingQueue: 3, forecastAccuracy: '94%' }
  },
  {
    id: 'fin-025',
    name: 'Cash Flow Agent',
    role: 'Cash Flow Monitoring Agent',
    department: 'Finance & Accounting',
    category: 'Forecasting & Reporting',
    description: 'Monitors cash flow, forecasts cash positions, and alerts on liquidity concerns.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Monitors cash flow',
      'Forecasts cash positions',
      'Alerts on liquidity concerns',
      'Tracks cash inflows/outflows',
      'Optimizes cash management',
      'Generates cash reports'
    ],
    primaryResponsibilities: [
      'Monitor cash flow',
      'Forecast positions',
      'Alert on liquidity',
      'Track flows',
      'Optimize management'
    ],
    recommendedTasks: [
      'Cash monitoring',
      'Position forecasting',
      'Liquidity alerts',
      'Flow tracking',
      'Management optimization'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.0s',
    metrics: { transactionsProcessed: 220, errorsDetected: 8, costSavingsIdentified: '$15,000', pendingQueue: 4, forecastAccuracy: '92%' }
  },

  // --- Tax & Assets ---
  {
    id: 'fin-026',
    name: 'Tax Support Agent',
    role: 'Tax Preparation Support Agent',
    department: 'Finance & Accounting',
    category: 'Tax & Assets',
    description: 'Supports tax preparation, organizes tax documentation, and tracks tax deadlines (placeholder, not legal tax prep).',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Supports tax preparation',
      'Organizes tax documentation',
      'Tracks tax deadlines',
      'Gathers tax data',
      'Coordinates with tax advisors',
      'Generates tax reports'
    ],
    primaryResponsibilities: [
      'Support tax prep',
      'Organize documentation',
      'Track deadlines',
      'Gather data',
      'Coordinate advisors'
    ],
    recommendedTasks: [
      'Tax support',
      'Documentation organization',
      'Deadline tracking',
      'Data gathering',
      'Advisor coordination'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 10,
    tasksCompleted7Days: 70,
    avgResponseTime: '1.5s',
    metrics: { transactionsProcessed: 120, errorsDetected: 5, costSavingsIdentified: '$8,000', pendingQueue: 4, forecastAccuracy: '90%' }
  },
  {
    id: 'fin-027',
    name: 'Asset Tracking Agent',
    role: 'Asset & Depreciation Tracking Agent',
    department: 'Finance & Accounting',
    category: 'Tax & Assets',
    description: 'Tracks fixed assets, calculates depreciation, and manages asset registers.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Tracks fixed assets',
      'Calculates depreciation',
      'Manages asset registers',
      'Tracks asset locations',
      'Processes disposals',
      'Generates asset reports'
    ],
    primaryResponsibilities: [
      'Track assets',
      'Calculate depreciation',
      'Manage registers',
      'Track locations',
      'Process disposals'
    ],
    recommendedTasks: [
      'Asset tracking',
      'Depreciation calculation',
      'Register management',
      'Location tracking',
      'Disposal processing'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.0s',
    metrics: { transactionsProcessed: 180, errorsDetected: 8, costSavingsIdentified: '$12,000', pendingQueue: 5, forecastAccuracy: '94%' }
  },
  {
    id: 'fin-028',
    name: 'Insurance Risk Agent',
    role: 'Insurance & Financial Risk Compliance Agent',
    department: 'Finance & Accounting',
    category: 'Tax & Assets',
    description: 'Manages insurance documentation, tracks policy renewals, and ensures financial risk compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Manages insurance documentation',
      'Tracks policy renewals',
      'Ensures risk compliance',
      'Reviews coverage adequacy',
      'Processes claims documentation',
      'Generates insurance reports'
    ],
    primaryResponsibilities: [
      'Manage insurance docs',
      'Track renewals',
      'Ensure compliance',
      'Review coverage',
      'Process claims'
    ],
    recommendedTasks: [
      'Insurance management',
      'Renewal tracking',
      'Compliance assurance',
      'Coverage review',
      'Claim processing'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '1.2s',
    metrics: { transactionsProcessed: 100, errorsDetected: 4, costSavingsIdentified: '$22,000', pendingQueue: 3, forecastAccuracy: '92%' }
  },
  {
    id: 'fin-029',
    name: 'Vendor Payment Agent',
    role: 'Vendor Payment Schedule Agent',
    department: 'Finance & Accounting',
    category: 'Tax & Assets',
    description: 'Manages vendor payment schedules, optimizes payment timing, and tracks payment terms.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Manages payment schedules',
      'Optimizes payment timing',
      'Tracks payment terms',
      'Maximizes early pay discounts',
      'Avoids late fees',
      'Generates payment reports'
    ],
    primaryResponsibilities: [
      'Manage schedules',
      'Optimize timing',
      'Track terms',
      'Maximize discounts',
      'Avoid late fees'
    ],
    recommendedTasks: [
      'Schedule management',
      'Timing optimization',
      'Term tracking',
      'Discount maximization',
      'Late fee avoidance'
    ],
    modulesAssigned: ['Finance Hub', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: true,
      canUpdateARAPEntries: true,
      canReadJobCostData: false,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.8s',
    metrics: { transactionsProcessed: 320, errorsDetected: 10, costSavingsIdentified: '$28,000', pendingQueue: 8, forecastAccuracy: '95%' }
  },
  {
    id: 'fin-030',
    name: 'Financial Doc Agent',
    role: 'Financial Document Management Agent',
    department: 'Finance & Accounting',
    category: 'Tax & Assets',
    description: 'Manages financial documents, ensures proper filing, and maintains document retention compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T09:00:00Z',
    updatedAt: '2025-12-11T09:00:00Z',
    personaPrompt: 'Define tone, behavior, auditing strictness, and analytical style for this agent.',
    capabilities: [
      'Manages financial documents',
      'Ensures proper filing',
      'Maintains retention compliance',
      'Organizes document archives',
      'Retrieves documents on request',
      'Generates document reports'
    ],
    primaryResponsibilities: [
      'Manage documents',
      'Ensure filing',
      'Maintain compliance',
      'Organize archives',
      'Retrieve documents'
    ],
    recommendedTasks: [
      'Document management',
      'Filing assurance',
      'Compliance maintenance',
      'Archive organization',
      'Document retrieval'
    ],
    modulesAssigned: ['Finance Hub', 'Reporting / Analytics', 'Automations'],
    permissions: {
      canReadFinancialRecords: true,
      canWriteFinancialRecords: false,
      canUpdateARAPEntries: false,
      canReadJobCostData: true,
      canUpdateBudgets: false,
      canModifyCRMData: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 20,
    tasksCompleted7Days: 140,
    avgResponseTime: '0.7s',
    metrics: { transactionsProcessed: 280, errorsDetected: 5, costSavingsIdentified: '$5,000', pendingQueue: 6, forecastAccuracy: '96%' }
  },

  // ============ LEGAL & COMPLIANCE DEPARTMENT ============
  // --- Leadership ---
  {
    id: 'legal-001',
    name: 'CLO Agent',
    role: 'Chief Legal Officer',
    department: 'Legal & Compliance',
    category: 'Leadership',
    description: 'Executive leader overseeing all legal operations, compliance, contracts, risk management, and regulatory affairs for the organization.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Oversees all legal operations',
      'Sets compliance strategy',
      'Manages legal department',
      'Reviews high-risk contracts',
      'Coordinates with external counsel',
      'Ensures regulatory compliance'
    ],
    primaryResponsibilities: [
      'Lead legal operations',
      'Set compliance strategy',
      'Manage legal team',
      'Oversee risk management',
      'Coordinate regulatory affairs'
    ],
    recommendedTasks: [
      'Strategic planning',
      'Contract oversight',
      'Compliance reviews',
      'Risk assessment',
      'Policy development'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Vendor Management', 'Reporting / Audit Trails', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 8 },
    tasksInQueue: 4,
    tasksToday: 12,
    tasksCompleted7Days: 84,
    avgResponseTime: '2.0s',
    metrics: { contractsReviewed: 45, complianceChecks: 120, riskEventsIdentified: 8, pendingQueue: 4, timelineAccuracy: '96%' },
    teamMembers: Array.from({length: 28}, (_, i) => `legal-${String(i + 2).padStart(3, '0')}`)
  },
  {
    id: 'legal-002',
    name: 'General Counsel Agent',
    role: 'General Counsel Agent',
    department: 'Legal & Compliance',
    category: 'Leadership',
    description: 'Provides legal advice across all departments, manages litigation matters, and ensures legal best practices.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Provides legal advice',
      'Manages litigation matters',
      'Reviews legal documents',
      'Advises on legal risk',
      'Coordinates external counsel',
      'Ensures legal compliance'
    ],
    primaryResponsibilities: [
      'Provide legal counsel',
      'Manage litigation',
      'Review documents',
      'Advise on risk',
      'Ensure compliance'
    ],
    recommendedTasks: [
      'Legal advisory',
      'Litigation management',
      'Document review',
      'Risk counseling',
      'Compliance guidance'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Vendor Management', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.8s',
    metrics: { contractsReviewed: 62, complianceChecks: 85, riskEventsIdentified: 12, pendingQueue: 5, timelineAccuracy: '94%' }
  },
  {
    id: 'legal-003',
    name: 'Compliance Director',
    role: 'Director of Compliance & Regulatory Affairs',
    department: 'Legal & Compliance',
    category: 'Leadership',
    description: 'Directs compliance programs, manages regulatory relationships, and ensures organizational adherence to laws and regulations.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Directs compliance programs',
      'Manages regulatory relationships',
      'Ensures regulatory adherence',
      'Develops compliance policies',
      'Conducts compliance audits',
      'Trains staff on compliance'
    ],
    primaryResponsibilities: [
      'Direct compliance programs',
      'Manage regulations',
      'Ensure adherence',
      'Develop policies',
      'Conduct audits'
    ],
    recommendedTasks: [
      'Compliance oversight',
      'Regulatory management',
      'Policy development',
      'Audit coordination',
      'Training programs'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'HR', 'Reporting / Audit Trails', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.5s',
    metrics: { contractsReviewed: 35, complianceChecks: 180, riskEventsIdentified: 15, pendingQueue: 6, timelineAccuracy: '95%' }
  },

  // --- Contracts & Documentation ---
  {
    id: 'legal-004',
    name: 'Contract Review Agent',
    role: 'Contract Creation & Review Agent',
    department: 'Legal & Compliance',
    category: 'Contracts & Documentation',
    description: 'Creates, reviews, and validates contracts ensuring legal compliance and risk mitigation.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Creates contracts',
      'Reviews contract terms',
      'Validates legal compliance',
      'Identifies contract risks',
      'Negotiates terms',
      'Manages contract lifecycle'
    ],
    primaryResponsibilities: [
      'Create contracts',
      'Review terms',
      'Validate compliance',
      'Identify risks',
      'Manage lifecycle'
    ],
    recommendedTasks: [
      'Contract creation',
      'Term review',
      'Compliance validation',
      'Risk identification',
      'Lifecycle management'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Vendor Management', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 10 },
    tasksInQueue: 12,
    tasksToday: 35,
    tasksCompleted7Days: 245,
    avgResponseTime: '1.0s',
    metrics: { contractsReviewed: 180, complianceChecks: 95, riskEventsIdentified: 22, pendingQueue: 12, timelineAccuracy: '92%' }
  },
  {
    id: 'legal-005',
    name: 'Document Drafting Agent',
    role: 'Legal Document Drafting Agent',
    department: 'Legal & Compliance',
    category: 'Contracts & Documentation',
    description: 'Drafts legal documents, agreements, and formal correspondence ensuring accuracy and compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Drafts legal documents',
      'Creates agreements',
      'Prepares formal correspondence',
      'Ensures document accuracy',
      'Maintains templates',
      'Reviews document compliance'
    ],
    primaryResponsibilities: [
      'Draft documents',
      'Create agreements',
      'Prepare correspondence',
      'Ensure accuracy',
      'Maintain templates'
    ],
    recommendedTasks: [
      'Document drafting',
      'Agreement creation',
      'Correspondence prep',
      'Template maintenance',
      'Compliance review'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.9s',
    metrics: { contractsReviewed: 85, complianceChecks: 120, riskEventsIdentified: 8, pendingQueue: 8, timelineAccuracy: '94%' }
  },
  {
    id: 'legal-006',
    name: 'Contract Risk Agent',
    role: 'Contract Risk Assessment Agent',
    department: 'Legal & Compliance',
    category: 'Contracts & Documentation',
    description: 'Assesses contract risks, identifies unfavorable terms, and recommends risk mitigation strategies.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Assesses contract risks',
      'Identifies unfavorable terms',
      'Scores risk levels',
      'Recommends mitigation',
      'Analyzes liability exposure',
      'Flags high-risk clauses'
    ],
    primaryResponsibilities: [
      'Assess risks',
      'Identify unfavorable terms',
      'Score risk levels',
      'Recommend mitigation',
      'Analyze liability'
    ],
    recommendedTasks: [
      'Risk assessment',
      'Term analysis',
      'Risk scoring',
      'Mitigation planning',
      'Liability analysis'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: false,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 10,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '1.2s',
    metrics: { contractsReviewed: 145, complianceChecks: 60, riskEventsIdentified: 48, pendingQueue: 10, timelineAccuracy: '91%' }
  },
  {
    id: 'legal-007',
    name: 'Vendor Contract Agent',
    role: 'Vendor Contract Verification Agent',
    department: 'Legal & Compliance',
    category: 'Contracts & Documentation',
    description: 'Verifies vendor contracts, ensures compliance with procurement policies, and validates vendor terms.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Verifies vendor contracts',
      'Ensures procurement compliance',
      'Validates vendor terms',
      'Reviews vendor documentation',
      'Tracks vendor obligations',
      'Monitors contract renewals'
    ],
    primaryResponsibilities: [
      'Verify vendor contracts',
      'Ensure compliance',
      'Validate terms',
      'Review documentation',
      'Track obligations'
    ],
    recommendedTasks: [
      'Contract verification',
      'Compliance checking',
      'Term validation',
      'Documentation review',
      'Renewal tracking'
    ],
    modulesAssigned: ['Legal Hub', 'Vendor Management', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '0.8s',
    metrics: { contractsReviewed: 125, complianceChecks: 85, riskEventsIdentified: 15, pendingQueue: 8, timelineAccuracy: '93%' }
  },
  {
    id: 'legal-008',
    name: 'Subcontractor Compliance Agent',
    role: 'Subcontractor Compliance Verification Agent',
    department: 'Legal & Compliance',
    category: 'Contracts & Documentation',
    description: 'Verifies subcontractor compliance, validates licenses and insurance, and ensures regulatory adherence.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Verifies subcontractor compliance',
      'Validates licenses',
      'Checks insurance coverage',
      'Ensures regulatory adherence',
      'Tracks certifications',
      'Monitors compliance expiration'
    ],
    primaryResponsibilities: [
      'Verify compliance',
      'Validate licenses',
      'Check insurance',
      'Ensure adherence',
      'Track certifications'
    ],
    recommendedTasks: [
      'Compliance verification',
      'License validation',
      'Insurance checking',
      'Certification tracking',
      'Expiration monitoring'
    ],
    modulesAssigned: ['Legal Hub', 'Vendor Management', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 10,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.7s',
    metrics: { contractsReviewed: 95, complianceChecks: 165, riskEventsIdentified: 18, pendingQueue: 10, timelineAccuracy: '95%' }
  },

  // --- Regulatory Compliance ---
  {
    id: 'legal-009',
    name: 'Regulatory Compliance Agent',
    role: 'Regulatory Compliance Agent',
    department: 'Legal & Compliance',
    category: 'Regulatory Compliance',
    description: 'Monitors regulatory changes, ensures organizational compliance, and implements regulatory requirements.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Monitors regulatory changes',
      'Ensures compliance',
      'Implements requirements',
      'Tracks regulatory updates',
      'Prepares compliance reports',
      'Advises on regulations'
    ],
    primaryResponsibilities: [
      'Monitor changes',
      'Ensure compliance',
      'Implement requirements',
      'Track updates',
      'Prepare reports'
    ],
    recommendedTasks: [
      'Regulatory monitoring',
      'Compliance assurance',
      'Requirement implementation',
      'Update tracking',
      'Report preparation'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '1.0s',
    metrics: { contractsReviewed: 25, complianceChecks: 220, riskEventsIdentified: 12, pendingQueue: 6, timelineAccuracy: '94%' }
  },
  {
    id: 'legal-010',
    name: 'State Contractor Rules Agent',
    role: 'State Contractor Rules Compliance Agent',
    department: 'Legal & Compliance',
    category: 'Regulatory Compliance',
    description: 'Monitors state-specific contractor regulations, ensures compliance across jurisdictions, and tracks rule changes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Monitors state contractor rules',
      'Ensures multi-state compliance',
      'Tracks rule changes',
      'Advises on state requirements',
      'Validates contractor licenses',
      'Prepares state compliance reports'
    ],
    primaryResponsibilities: [
      'Monitor state rules',
      'Ensure compliance',
      'Track changes',
      'Advise on requirements',
      'Validate licenses'
    ],
    recommendedTasks: [
      'State rule monitoring',
      'Compliance verification',
      'Change tracking',
      'License validation',
      'State reporting'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.2s',
    metrics: { contractsReviewed: 15, complianceChecks: 185, riskEventsIdentified: 10, pendingQueue: 5, timelineAccuracy: '96%' }
  },
  {
    id: 'legal-011',
    name: 'Licensing Compliance Agent',
    role: 'Permitting & Licensing Compliance Agent',
    department: 'Legal & Compliance',
    category: 'Regulatory Compliance',
    description: 'Manages permits and licenses, tracks renewals, and ensures all required authorizations are current.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Manages permits and licenses',
      'Tracks renewal dates',
      'Ensures current authorizations',
      'Prepares permit applications',
      'Monitors license requirements',
      'Alerts on expirations'
    ],
    primaryResponsibilities: [
      'Manage permits',
      'Track renewals',
      'Ensure authorizations',
      'Prepare applications',
      'Monitor requirements'
    ],
    recommendedTasks: [
      'Permit management',
      'Renewal tracking',
      'Application preparation',
      'Requirement monitoring',
      'Expiration alerts'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '0.8s',
    metrics: { contractsReviewed: 10, complianceChecks: 195, riskEventsIdentified: 5, pendingQueue: 8, timelineAccuracy: '97%' }
  },
  {
    id: 'legal-012',
    name: 'Insurance Compliance Agent',
    role: 'Insurance Compliance & Documentation Agent',
    department: 'Legal & Compliance',
    category: 'Regulatory Compliance',
    description: 'Ensures insurance compliance, validates coverage levels, and manages insurance documentation.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Ensures insurance compliance',
      'Validates coverage levels',
      'Manages insurance documentation',
      'Tracks policy renewals',
      'Verifies COI requirements',
      'Coordinates with insurance carriers'
    ],
    primaryResponsibilities: [
      'Ensure compliance',
      'Validate coverage',
      'Manage documentation',
      'Track renewals',
      'Verify COIs'
    ],
    recommendedTasks: [
      'Compliance assurance',
      'Coverage validation',
      'Documentation management',
      'Renewal tracking',
      'COI verification'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Vendor Management', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 7,
    tasksToday: 20,
    tasksCompleted7Days: 140,
    avgResponseTime: '0.9s',
    metrics: { contractsReviewed: 45, complianceChecks: 175, riskEventsIdentified: 8, pendingQueue: 7, timelineAccuracy: '95%' }
  },
  {
    id: 'legal-013',
    name: 'Policy Enforcement Agent',
    role: 'Policy Enforcement Agent',
    department: 'Legal & Compliance',
    category: 'Regulatory Compliance',
    description: 'Enforces company policies, monitors policy adherence, and addresses policy violations.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Enforces company policies',
      'Monitors policy adherence',
      'Addresses violations',
      'Investigates policy breaches',
      'Recommends corrective actions',
      'Tracks enforcement actions'
    ],
    primaryResponsibilities: [
      'Enforce policies',
      'Monitor adherence',
      'Address violations',
      'Investigate breaches',
      'Recommend actions'
    ],
    recommendedTasks: [
      'Policy enforcement',
      'Adherence monitoring',
      'Violation addressing',
      'Breach investigation',
      'Action tracking'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'HR', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.0s',
    metrics: { contractsReviewed: 15, complianceChecks: 145, riskEventsIdentified: 18, pendingQueue: 5, timelineAccuracy: '92%' }
  },
  {
    id: 'legal-014',
    name: 'Audit Defense Agent',
    role: 'Audit Defense Preparation Agent',
    department: 'Legal & Compliance',
    category: 'Regulatory Compliance',
    description: 'Prepares audit defense documentation, coordinates audit responses, and ensures audit readiness.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Prepares audit defense',
      'Builds documentation packets',
      'Coordinates audit responses',
      'Ensures audit readiness',
      'Tracks audit findings',
      'Manages remediation plans'
    ],
    primaryResponsibilities: [
      'Prepare defense',
      'Build packets',
      'Coordinate responses',
      'Ensure readiness',
      'Track findings'
    ],
    recommendedTasks: [
      'Defense preparation',
      'Packet building',
      'Response coordination',
      'Readiness assurance',
      'Finding tracking'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 10,
    tasksCompleted7Days: 70,
    avgResponseTime: '1.5s',
    metrics: { contractsReviewed: 35, complianceChecks: 95, riskEventsIdentified: 8, pendingQueue: 4, timelineAccuracy: '94%' }
  },

  // --- Risk & Investigations ---
  {
    id: 'legal-015',
    name: 'Risk Mitigation Agent',
    role: 'Risk Scoring & Mitigation Agent',
    department: 'Legal & Compliance',
    category: 'Risk & Investigations',
    description: 'Scores operational risks, identifies risk patterns, and develops mitigation strategies.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Scores operational risks',
      'Identifies risk patterns',
      'Develops mitigation strategies',
      'Monitors risk indicators',
      'Prioritizes risk responses',
      'Reports risk status'
    ],
    primaryResponsibilities: [
      'Score risks',
      'Identify patterns',
      'Develop strategies',
      'Monitor indicators',
      'Prioritize responses'
    ],
    recommendedTasks: [
      'Risk scoring',
      'Pattern identification',
      'Strategy development',
      'Indicator monitoring',
      'Response prioritization'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: false,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 16,
    tasksCompleted7Days: 112,
    avgResponseTime: '1.2s',
    metrics: { contractsReviewed: 55, complianceChecks: 85, riskEventsIdentified: 45, pendingQueue: 6, timelineAccuracy: '90%' }
  },
  {
    id: 'legal-016',
    name: 'OSHA Documentation Agent',
    role: 'Safety & OSHA Documentation Agent',
    department: 'Legal & Compliance',
    category: 'Risk & Investigations',
    description: 'Manages OSHA compliance documentation, tracks safety requirements, and ensures workplace safety compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Manages OSHA documentation',
      'Tracks safety requirements',
      'Ensures workplace compliance',
      'Prepares safety reports',
      'Documents safety incidents',
      'Monitors safety regulations'
    ],
    primaryResponsibilities: [
      'Manage OSHA docs',
      'Track requirements',
      'Ensure compliance',
      'Prepare reports',
      'Document incidents'
    ],
    recommendedTasks: [
      'Documentation management',
      'Requirement tracking',
      'Compliance assurance',
      'Report preparation',
      'Incident documentation'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'HR', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '0.9s',
    metrics: { contractsReviewed: 10, complianceChecks: 165, riskEventsIdentified: 12, pendingQueue: 5, timelineAccuracy: '96%' }
  },
  {
    id: 'legal-017',
    name: 'Incident Investigation Agent',
    role: 'Incident Investigation & Reporting Agent',
    department: 'Legal & Compliance',
    category: 'Risk & Investigations',
    description: 'Investigates incidents, documents findings, and prepares incident reports for compliance purposes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Investigates incidents',
      'Documents findings',
      'Prepares incident reports',
      'Identifies root causes',
      'Recommends corrective actions',
      'Tracks incident trends'
    ],
    primaryResponsibilities: [
      'Investigate incidents',
      'Document findings',
      'Prepare reports',
      'Identify root causes',
      'Recommend actions'
    ],
    recommendedTasks: [
      'Incident investigation',
      'Finding documentation',
      'Report preparation',
      'Root cause analysis',
      'Action recommendation'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'HR', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 10,
    tasksCompleted7Days: 70,
    avgResponseTime: '1.5s',
    metrics: { contractsReviewed: 20, complianceChecks: 75, riskEventsIdentified: 28, pendingQueue: 4, timelineAccuracy: '93%' }
  },
  {
    id: 'legal-018',
    name: 'Legal Risk Monitor Agent',
    role: 'Legal Risk Monitoring Agent',
    department: 'Legal & Compliance',
    category: 'Risk & Investigations',
    description: 'Monitors legal risks across operations, identifies emerging legal threats, and alerts on risk changes.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Monitors legal risks',
      'Identifies emerging threats',
      'Alerts on risk changes',
      'Tracks legal developments',
      'Assesses impact',
      'Reports risk status'
    ],
    primaryResponsibilities: [
      'Monitor risks',
      'Identify threats',
      'Alert on changes',
      'Track developments',
      'Assess impact'
    ],
    recommendedTasks: [
      'Risk monitoring',
      'Threat identification',
      'Change alerts',
      'Development tracking',
      'Impact assessment'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: false,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 6,
    tasksToday: 18,
    tasksCompleted7Days: 126,
    avgResponseTime: '0.8s',
    metrics: { contractsReviewed: 40, complianceChecks: 95, riskEventsIdentified: 32, pendingQueue: 6, timelineAccuracy: '91%' }
  },
  {
    id: 'legal-019',
    name: 'Internal Affairs Agent',
    role: 'Internal Affairs Review Agent',
    department: 'Legal & Compliance',
    category: 'Risk & Investigations',
    description: 'Conducts internal investigations, reviews employee conduct issues, and ensures proper investigation procedures.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Conducts internal investigations',
      'Reviews conduct issues',
      'Ensures proper procedures',
      'Documents investigation findings',
      'Recommends disciplinary actions',
      'Maintains confidentiality'
    ],
    primaryResponsibilities: [
      'Conduct investigations',
      'Review conduct',
      'Ensure procedures',
      'Document findings',
      'Recommend actions'
    ],
    recommendedTasks: [
      'Investigation conduct',
      'Conduct review',
      'Procedure assurance',
      'Finding documentation',
      'Action recommendation'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'HR', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 20, maxConcurrentTasks: 5 },
    tasksInQueue: 3,
    tasksToday: 6,
    tasksCompleted7Days: 42,
    avgResponseTime: '2.0s',
    metrics: { contractsReviewed: 10, complianceChecks: 55, riskEventsIdentified: 15, pendingQueue: 3, timelineAccuracy: '94%' }
  },
  {
    id: 'legal-020',
    name: 'Fraud Detection Agent',
    role: 'Fraud & Misconduct Detection Agent',
    department: 'Legal & Compliance',
    category: 'Risk & Investigations',
    description: 'Detects fraud and misconduct, analyzes suspicious patterns, and escalates potential issues.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Detects fraud patterns',
      'Identifies misconduct',
      'Analyzes suspicious activity',
      'Escalates potential issues',
      'Documents fraud indicators',
      'Supports fraud investigations'
    ],
    primaryResponsibilities: [
      'Detect fraud',
      'Identify misconduct',
      'Analyze activity',
      'Escalate issues',
      'Document indicators'
    ],
    recommendedTasks: [
      'Fraud detection',
      'Misconduct identification',
      'Activity analysis',
      'Issue escalation',
      'Indicator documentation'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 12 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.6s',
    metrics: { contractsReviewed: 35, complianceChecks: 125, riskEventsIdentified: 42, pendingQueue: 8, timelineAccuracy: '92%' }
  },

  // --- Claims ---
  {
    id: 'legal-021',
    name: 'Claim Documentation Agent',
    role: 'Claim Documentation Agent',
    department: 'Legal & Compliance',
    category: 'Claims',
    description: 'Documents claims, maintains claim files, and ensures proper claim documentation procedures.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Documents claims',
      'Maintains claim files',
      'Ensures proper procedures',
      'Tracks claim status',
      'Organizes claim evidence',
      'Prepares claim summaries'
    ],
    primaryResponsibilities: [
      'Document claims',
      'Maintain files',
      'Ensure procedures',
      'Track status',
      'Organize evidence'
    ],
    recommendedTasks: [
      'Claim documentation',
      'File maintenance',
      'Procedure assurance',
      'Status tracking',
      'Evidence organization'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 35, maxConcurrentTasks: 10 },
    tasksInQueue: 8,
    tasksToday: 22,
    tasksCompleted7Days: 154,
    avgResponseTime: '0.9s',
    metrics: { contractsReviewed: 25, complianceChecks: 85, riskEventsIdentified: 12, pendingQueue: 8, timelineAccuracy: '94%' }
  },
  {
    id: 'legal-022',
    name: 'Dispute Intake Agent',
    role: 'Dispute & Complaint Intake Agent',
    department: 'Legal & Compliance',
    category: 'Claims',
    description: 'Receives and processes disputes and complaints, documents initial information, and routes for resolution.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Receives disputes',
      'Processes complaints',
      'Documents initial information',
      'Routes for resolution',
      'Tracks dispute status',
      'Prioritizes urgent matters'
    ],
    primaryResponsibilities: [
      'Receive disputes',
      'Process complaints',
      'Document information',
      'Route for resolution',
      'Track status'
    ],
    recommendedTasks: [
      'Dispute intake',
      'Complaint processing',
      'Information documentation',
      'Resolution routing',
      'Status tracking'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 12 },
    tasksInQueue: 10,
    tasksToday: 28,
    tasksCompleted7Days: 196,
    avgResponseTime: '0.7s',
    metrics: { contractsReviewed: 15, complianceChecks: 65, riskEventsIdentified: 18, pendingQueue: 10, timelineAccuracy: '93%' }
  },
  {
    id: 'legal-023',
    name: 'Insurance Claim Support Agent',
    role: 'Insurance Claim Submission Support Agent',
    department: 'Legal & Compliance',
    category: 'Claims',
    description: 'Supports insurance claim submissions, gathers required documentation, and coordinates with insurance carriers.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Supports claim submissions',
      'Gathers required documentation',
      'Coordinates with carriers',
      'Tracks claim progress',
      'Prepares submission packets',
      'Ensures claim completeness'
    ],
    primaryResponsibilities: [
      'Support submissions',
      'Gather documentation',
      'Coordinate carriers',
      'Track progress',
      'Prepare packets'
    ],
    recommendedTasks: [
      'Submission support',
      'Documentation gathering',
      'Carrier coordination',
      'Progress tracking',
      'Packet preparation'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Vendor Management', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 6,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.0s',
    metrics: { contractsReviewed: 20, complianceChecks: 75, riskEventsIdentified: 8, pendingQueue: 6, timelineAccuracy: '95%' }
  },
  {
    id: 'legal-024',
    name: 'Case File Assembly Agent',
    role: 'Case File Assembly & Legal Packet Agent',
    department: 'Legal & Compliance',
    category: 'Claims',
    description: 'Assembles case files, creates legal packets, and organizes documentation for legal proceedings.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Assembles case files',
      'Creates legal packets',
      'Organizes documentation',
      'Prepares for proceedings',
      'Indexes case materials',
      'Maintains file integrity'
    ],
    primaryResponsibilities: [
      'Assemble files',
      'Create packets',
      'Organize documentation',
      'Prepare for proceedings',
      'Index materials'
    ],
    recommendedTasks: [
      'File assembly',
      'Packet creation',
      'Documentation organization',
      'Proceeding preparation',
      'Material indexing'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 4,
    tasksToday: 10,
    tasksCompleted7Days: 70,
    avgResponseTime: '1.5s',
    metrics: { contractsReviewed: 35, complianceChecks: 55, riskEventsIdentified: 5, pendingQueue: 4, timelineAccuracy: '96%' }
  },

  // --- Administration ---
  {
    id: 'legal-025',
    name: 'Legal Records Agent',
    role: 'Legal Records Management Agent',
    department: 'Legal & Compliance',
    category: 'Administration',
    description: 'Manages legal records, ensures proper filing, and maintains legal document archives.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Manages legal records',
      'Ensures proper filing',
      'Maintains document archives',
      'Organizes legal files',
      'Retrieves records on request',
      'Tracks record locations'
    ],
    primaryResponsibilities: [
      'Manage records',
      'Ensure filing',
      'Maintain archives',
      'Organize files',
      'Retrieve records'
    ],
    recommendedTasks: [
      'Record management',
      'Filing assurance',
      'Archive maintenance',
      'File organization',
      'Record retrieval'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 12 },
    tasksInQueue: 8,
    tasksToday: 25,
    tasksCompleted7Days: 175,
    avgResponseTime: '0.6s',
    metrics: { contractsReviewed: 45, complianceChecks: 95, riskEventsIdentified: 5, pendingQueue: 8, timelineAccuracy: '97%' }
  },
  {
    id: 'legal-026',
    name: 'Document Retention Agent',
    role: 'Document Retention & Compliance Agent',
    department: 'Legal & Compliance',
    category: 'Administration',
    description: 'Manages document retention policies, ensures compliance with retention requirements, and oversees document destruction.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Manages retention policies',
      'Ensures retention compliance',
      'Oversees document destruction',
      'Tracks retention schedules',
      'Implements legal holds',
      'Reports retention status'
    ],
    primaryResponsibilities: [
      'Manage policies',
      'Ensure compliance',
      'Oversee destruction',
      'Track schedules',
      'Implement holds'
    ],
    recommendedTasks: [
      'Policy management',
      'Compliance assurance',
      'Destruction oversight',
      'Schedule tracking',
      'Hold implementation'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 14,
    tasksCompleted7Days: 98,
    avgResponseTime: '1.0s',
    metrics: { contractsReviewed: 25, complianceChecks: 145, riskEventsIdentified: 8, pendingQueue: 5, timelineAccuracy: '95%' }
  },
  {
    id: 'legal-027',
    name: 'Data Privacy Agent',
    role: 'Data Privacy & Security Compliance Agent',
    department: 'Legal & Compliance',
    category: 'Administration',
    description: 'Ensures data privacy compliance, monitors security requirements, and manages privacy policies.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Ensures privacy compliance',
      'Monitors security requirements',
      'Manages privacy policies',
      'Tracks data regulations',
      'Handles privacy requests',
      'Reports compliance status'
    ],
    primaryResponsibilities: [
      'Ensure privacy',
      'Monitor security',
      'Manage policies',
      'Track regulations',
      'Handle requests'
    ],
    recommendedTasks: [
      'Privacy assurance',
      'Security monitoring',
      'Policy management',
      'Regulation tracking',
      'Request handling'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 30, maxConcurrentTasks: 8 },
    tasksInQueue: 5,
    tasksToday: 15,
    tasksCompleted7Days: 105,
    avgResponseTime: '1.0s',
    metrics: { contractsReviewed: 15, complianceChecks: 165, riskEventsIdentified: 10, pendingQueue: 5, timelineAccuracy: '94%' }
  },
  {
    id: 'legal-028',
    name: 'Subpoena Handling Agent',
    role: 'Subpoena & Request Handling Agent',
    department: 'Legal & Compliance',
    category: 'Administration',
    description: 'Handles legal subpoenas and requests, coordinates responses, and ensures timely compliance.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Handles subpoenas',
      'Processes legal requests',
      'Coordinates responses',
      'Ensures timely compliance',
      'Tracks request deadlines',
      'Documents response activities'
    ],
    primaryResponsibilities: [
      'Handle subpoenas',
      'Process requests',
      'Coordinate responses',
      'Ensure compliance',
      'Track deadlines'
    ],
    recommendedTasks: [
      'Subpoena handling',
      'Request processing',
      'Response coordination',
      'Compliance assurance',
      'Deadline tracking'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Reporting / Audit Trails'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: true,
      canViewVendorFiles: true,
      canReviewContracts: true,
      canSendExternalComm: false,
      canAccessFinancialRecords: true
    },
    limits: { maxTasksPerHour: 25, maxConcurrentTasks: 6 },
    tasksInQueue: 3,
    tasksToday: 8,
    tasksCompleted7Days: 56,
    avgResponseTime: '1.5s',
    metrics: { contractsReviewed: 20, complianceChecks: 65, riskEventsIdentified: 5, pendingQueue: 3, timelineAccuracy: '98%' }
  },
  {
    id: 'legal-029',
    name: 'Compliance Calendar Agent',
    role: 'Compliance Calendar & Deadline Tracking Agent',
    department: 'Legal & Compliance',
    category: 'Administration',
    description: 'Manages compliance calendar, tracks deadlines, and ensures timely completion of compliance obligations.',
    status: 'active',
    createdBy: 'Ryan March',
    createdAt: '2025-12-11T10:00:00Z',
    updatedAt: '2025-12-11T10:00:00Z',
    personaPrompt: 'Define how this legal agent interprets risk, compliance policies, documentation rigor, and communication style.',
    capabilities: [
      'Manages compliance calendar',
      'Tracks deadlines',
      'Ensures timely completion',
      'Sends deadline reminders',
      'Monitors compliance schedule',
      'Reports calendar status'
    ],
    primaryResponsibilities: [
      'Manage calendar',
      'Track deadlines',
      'Ensure completion',
      'Send reminders',
      'Monitor schedule'
    ],
    recommendedTasks: [
      'Calendar management',
      'Deadline tracking',
      'Completion assurance',
      'Reminder sending',
      'Schedule monitoring'
    ],
    modulesAssigned: ['Legal Hub', 'Compliance Center', 'Automations'],
    permissions: {
      canReadLegalDocuments: true,
      canWriteLegalDocuments: false,
      canViewVendorFiles: false,
      canReviewContracts: false,
      canSendExternalComm: false,
      canAccessFinancialRecords: false
    },
    limits: { maxTasksPerHour: 40, maxConcurrentTasks: 12 },
    tasksInQueue: 6,
    tasksToday: 20,
    tasksCompleted7Days: 140,
    avgResponseTime: '0.5s',
    metrics: { contractsReviewed: 10, complianceChecks: 225, riskEventsIdentified: 3, pendingQueue: 6, timelineAccuracy: '99%' }
  }
];

// Mock tasks data

// Helper functions for generating mock data
export const generateMockTasks = (agentId) => {
  const taskTemplates = [
    { title: 'Process incoming lead', source: 'CRM Webhook' },
    { title: 'Send follow-up email', source: 'Scheduled' },
    { title: 'Update contact record', source: 'API Trigger' },
    { title: 'Generate daily report', source: 'Cron Job' },
    { title: 'Sync data to external system', source: 'Integration' }
  ];
  const statuses = ['queued', 'in-progress', 'completed', 'failed'];
  const results = ['success', 'failed', 'partial'];

  return Array.from({ length: 10 }, (_, i) => {
    const template = taskTemplates[i % taskTemplates.length];
    const isCompleted = i > 4;
    return {
      id: `task-${agentId}-${i}`,
      agentId,
      title: template.title,
      description: `Automated task generated by ${template.source}`,
      source: template.source,
      priority: i === 0 ? 'high' : i < 3 ? 'normal' : 'low',
      status: isCompleted ? 'completed' : statuses[i % 4],
      createdAt: new Date(Date.now() - (10 - i) * 3600000).toISOString(),
      completedAt: isCompleted ? new Date(Date.now() - (10 - i) * 3600000 + 1800000).toISOString() : null,
      result: isCompleted ? results[i % 3] : null,
      notes: isCompleted ? 'Task completed successfully' : null
    };
  });
};

export const generateActivityLog = (agentId) => {
  const events = [
    { type: 'Task Completed', description: 'Successfully processed lead #1234' },
    { type: 'Status Changed', description: 'Agent activated for daily operations' },
    { type: 'Config Updated', description: 'Response timeout increased to 60s' },
    { type: 'Task Started', description: 'Beginning batch email send' },
    { type: 'Error Logged', description: 'Rate limit reached, backing off' },
    { type: 'Task Completed', description: 'Generated weekly performance report' }
  ];

  return Array.from({ length: 10 }, (_, i) => ({
    id: `log-${agentId}-${i}`,
    timestamp: new Date(Date.now() - i * 7200000).toISOString(),
    eventType: events[i % events.length].type,
    description: events[i % events.length].description,
    triggeredBy: i % 3 === 0 ? 'System' : 'Super Admin'
  }));
};

